/**
 * As rotas de `/v1`, num registro só.
 *
 * ── Por que um registro, e não decorador no controller ────────────────────
 *
 * O documento OpenAPI, o cliente que `apps/web` importa e a validação em
 * runtime saem **todos daqui**. Se cada um lesse de um lugar diferente, os três
 * divergiriam em ritmos diferentes — e a divergência entre contrato publicado e
 * comportamento real é a que custa dias, porque quem consome não tem como
 * suspeitar dela.
 *
 * O controller do NestJS declara a rota e chama o caso de uso; quem descreve o
 * contrato é este arquivo.
 */

// Valor, e não só tipo: a rota de anexo devolve bytes e não tem schema própria
// para apontar — o `z.string()` dela é construído aqui mesmo.
import { ChatDeSuporteSchema } from './suporte.js';
import { ConsultaDoCepSchema, EnderecoDoCepSchema } from './cep.js';
import { ListaDeEsperaSchema, LugarNaListaDeEsperaSchema } from './lista-de-espera.js';
import { ConcederSuperAdminSchema, SuperAdminSchema, SuperAdminsSchema } from './super-admins.js';
import { CriarPlanoSchema, EditarPlanoSchema, PlanoSchema, PlanosSchema } from './planos.js';
import {
  AlterarCadastroDaOrganizacaoSchema,
  AlterarContratoDaOrganizacaoSchema,
  CadastroDaOrganizacaoAlteradoSchema,
  ContratoDaOrganizacaoAlteradoSchema,
  HistoricoDaOrganizacaoSchema,
  OrganizacaoDetalhadaSchema,
  OrganizacoesNaGestaoSchema,
  RetomarCriacaoDaOrganizacaoSchema,
} from './organizacoes.js';
import { z } from 'zod';
import {
  AtendimentoIniciadoSchema,
  ConversaCompostaSchema,
  IniciarAtendimentoSchema,
} from './conversas.js';
import {
  CatalogoDeEtiquetasSchema,
  CriarEtiquetaSchema,
  DefinirEtiquetasSchema,
  EtiquetaSchema,
  ConversaLidaSchema,
  DefinirPrioridadeSchema,
  PrioridadeDaConversaSchema,
  EtiquetasDaConversaSchema,
  EtiquetasDoClienteSchema,
} from './etiquetas.js';
import {
  CriarEtapaSchema,
  EtapasDoFunilSchema,
  RenomearEtapaSchema,
  ReordenarEtapasSchema,
} from './etapas.js';
import {
  ConversaTransferidaSchema,
  ResponsaveisPossiveisSchema,
  TransferirConversaSchema,
} from './responsavel.js';
import {
  AtualizarClienteSchema,
  CriarClienteSchema,
  Cliente360Schema,
  ClienteAtualizadoSchema,
} from './cliente-360.js';
import {
  CartaoDeContatoSchema,
  ExportacaoDeClientesSchema,
  PaginaDeClientesSchema,
} from './clientes.js';
import { CaixasDoAtendimentoSchema, NaoLidasDaInboxSchema, PaginaDaInboxSchema } from './inbox.js';
import {
  CriarMensagemProntaSchema,
  EditarMensagemProntaSchema,
  MensagemProntaSchema,
  MensagensProntasSchema,
} from './mensagens-prontas.js';
import {
  CatalogoDePermissoesSchema,
  PermissoesDaPessoaSchema,
  PessoasDasPermissoesSchema,
  SalvarPermissoesDaPessoaSchema,
} from './permissoes.js';
import { DefinirRoteamentoSchema, CaixaRoteadaSchema, RoteamentoSchema } from './roteamento.js';
import { HistoricoDaOportunidadeSchema } from './historico-da-oportunidade.js';
import { AtividadesDaOportunidadeSchema } from './atividades-da-oportunidade.js';
import { ConversasDaOportunidadeSchema } from './conversas-da-oportunidade.js';
import {
  DocumentosDaOportunidadeSchema,
  EnviarDocumentosSchema,
} from './documentos-da-oportunidade.js';
import {
  ChavePublicaDoPushSchema,
  DispositivoRegistradoSchema,
  DispositivosRemovidosSchema,
  RegistrarDispositivoSchema,
  RemoverDispositivoSchema,
} from './push.js';
import {
  MarcarNotificacoesVistasSchema,
  NotificacoesMarcadasSchema,
  NotificacoesSchema,
  MencionaveisSchema,
  MencoesParaMimSchema,
} from './notificacoes.js';
import {
  CriarMotivoDePerdaSchema,
  EditarMotivoDePerdaSchema,
  MotivoDePerdaSchema,
  MotivosDePerdaSchema,
} from './motivos-de-perda.js';
import { EnviarFotoDePerfilSchema, FotoDePerfilSchema } from './foto-de-perfil.js';
import {
  EnviarFeedbackSchema,
  FeedbackEnviadoSchema,
  FeedbacksDoSuporteSchema,
} from './feedback.js';
import {
  CriarNotaDoClienteSchema,
  EditarNotaDoClienteSchema,
  NotaDoClienteSchema,
  NotasDoClienteSchema,
} from './notas-do-cliente.js';
import { FichaDoPacienteSchema, SalvarFichaDoPacienteSchema } from './ficha-do-paciente.js';
import {
  BuscarPacienteDoClienteSchema,
  CadastrarPacienteDoClienteSchema,
  CandidatosAPacienteSchema,
  PacienteDoClienteSchema,
  UltimasVezesDoPacienteSchema,
  VincularPacienteDoClienteSchema,
} from './paciente-no-sisac.js';
import {
  ConsultaDaOportunidadeSchema,
  OpcoesDaConsultaSchema,
  SalvarConsultaDaOportunidadeSchema,
  SincronizarOpcoesDaConsultaSchema,
  ResultadoDaSincronizacaoDeOpcoesSchema,
  AgendarConsultaSchema,
  CancelarConsultaSchema,
  CadastrarPacienteNaAgendaSchema,
  ConsultaLeituraSchema,
  PacienteCadastradoNaAgendaSchema,
  PacienteVinculadoDaConsultaSchema,
  AgendaDaClinicaSchema,
  AgendamentoDaConsultaSchema,
  ConferenciaDaConsultaSchema,
  RemarcarConsultaSchema,
  ListaDaAgendaSchema,
  PedidoDeListaDaAgendaSchema,
} from './consulta-da-oportunidade.js';
import {
  ListaDePrecosSchema,
  PrecoDaConsultaDoMedicoSchema,
  ResultadoDaSincronizacaoDePrecosSchema,
} from './precos-da-clinica.js';
import {
  ConvenioDoPacienteSchema,
  ConveniosDoPacienteSchema,
  CriarConvenioDoPacienteSchema,
  CriarOperadoraSchema,
  EditarConvenioDoPacienteSchema,
  EditarOperadoraSchema,
  OperadoraSchema,
  OperadorasSchema,
} from './convenio-do-paciente.js';
import {
  CriarEtapaDaClinicaSchema,
  CriarExameDoPacienteSchema,
  DefinirEtapasDoExameSchema,
  EditarEtapaDaClinicaSchema,
  EditarExameDoPacienteSchema,
  EtapaDaClinicaSchema,
  EtapasDaClinicaSchema,
  ExameDoPacienteSchema,
  ExamesDoPacienteSchema,
  OrdenarEtapasDaClinicaSchema,
} from './exames-do-paciente.js';
import {
  CampoPersonalizadoSchema,
  CamposPersonalizadosSchema,
  CriarCampoPersonalizadoSchema,
  EditarCampoPersonalizadoSchema,
  OrdenarCamposPersonalizadosSchema,
} from './campos-personalizados.js';
import {
  AjustarValorDoTratamentoSchema,
  CatalogoDeTratamentosSchema,
  CriarTratamentoDoPacienteSchema,
  EditarTratamentoDoPacienteSchema,
  TratamentoDoPacienteSchema,
  TratamentosDoPacienteSchema,
} from './tratamentos-do-paciente.js';
import { AcessoAoProntuarioSchema, ProntuarioDoPacienteSchema } from './prontuario-do-paciente.js';
import {
  AtendimentosDaOportunidadeSchema,
  AtendimentosDoFunilSchema,
} from './atendimentos-do-sisac.js';
import {
  DocumentosDoPacienteSchema,
  EnviarDocumentosDoPacienteSchema,
} from './documentos-do-paciente.js';
import {
  AtribuicaoDaOportunidadeSchema,
  AtribuirOportunidadeSchema,
  AtualizacaoDoFunilSchema,
  ConfiguracaoDoAtualizarSchema,
  DefinirConfiguracaoDoAtualizarSchema,
  BoardDoFunilSchema,
  CriarFunilSchema,
  FunilSchema,
  FunisSchema,
  RenomearFunilSchema,
  CriarNotaDaOportunidadeSchema,
  CriarOportunidadeSchema,
  EditarNotaDaOportunidadeSchema,
  EditarOportunidadeSchema,
  NotaDaOportunidadeSchema,
  NotasDaOportunidadeSchema,
  OportunidadeSchema,
  OportunidadeLidaSchema,
} from './funil.js';
import {
  AlterarTarefaSchema,
  CriarTarefaSchema,
  PaginaDeTarefasSchema,
  ResponsaveisDeTarefaSchema,
  TarefaSchema,
} from './tarefas.js';
import { PaginaDaTimelineSchema } from './timeline.js';
import { DashboardSchema } from './dashboard.js';
import {
  AtendimentoDoPeriodoSchema,
  ComparativosSchema,
  GargalosDoFunilSchema,
  OportunidadesParadasSchema,
  RankingDeVendasSchema,
  VendasDoRankingSchema,
} from './relatorios.js';
import {
  ConcessaoDeStepUpSchema,
  DefinirExigenciaDeMfaSchema,
  EstadoDoMfaSchema,
  PedirStepUpSchema,
  ResetarSegundoFatorSchema,
  ResultadoDoResetSchema,
} from './seguranca.js';
import {
  ConfigurarIntegracaoSchema,
  AutorizacaoDaMetaSchema,
  CanalCriadoSchema,
  CanalDaMetaSchema,
  CanalPorSignupSchema,
  ConectarWhatsAppPorSignupSchema,
  ConectarPaginaSchema,
  PaginasDaMetaSchema,
  RetornoDaMetaSchema,
  CatalogoDePlataformasSchema,
  ConectarWhatsAppSchema,
  ResultadoDaLigacaoSchema,
  AlterarMensagensAutomaticasSchema,
  AtendentesDoCanalSchema,
  CanaisConectadosSchema,
  CanalConectadoSchema,
  CriarCanalDeApiSchema,
  CriarWebChatSchema,
  DefinirAtendentesSchema,
  CanaisDeWhatsAppSchema,
  CriarModeloDeMensagemSchema,
  ModeloDeMensagemSchema,
  ModelosDeMensagemSchema,
  SincroniaDeModelosSchema,
  CanalDeWhatsAppSchema,
  ListaDeIntegracoesSchema,
  ResultadoDaConfiguracaoSchema,
} from './integracoes.js';
import {
  DetalheDaDlqSchema,
  ItemResolvidoSchema,
  PaginaDaDlqSchema,
  ReprocessarDaDlqSchema,
  ResolverItemDaDlqSchema,
  ResultadoDoReprocessamentoSchema,
} from './dlq.js';
import {
  CriarPedidoDoTitularSchema,
  ExecutarPedidoDoTitularSchema,
  ExportacaoDoTitularSchema,
  PaginaDePedidosDoTitularSchema,
  PedidoDoTitularSchema,
  RecusarPedidoDoTitularSchema,
  ResultadoDoPedidoSchema,
} from './titular.js';
import { NotificacaoDeMudancaSchema } from './eventos.js';
import { EnviarMensagemSchema, MensagemEnviadaSchema } from './mensagens.js';
import { ConversaEncerradaSchema, EncerrarConversaSchema } from './encerramento.js';
import {
  AlterarConfiguracaoSchema,
  AlterarFlagSchema,
  ConfiguracaoDoTenantSchema,
  ConfiguracaoSalvaSchema,
  FlagsDoTenantSchema,
  SimulacaoDaRegraSchema,
  SimularRegraSchema,
} from './configuracao.js';
import {
  AprovarMergeSchema,
  DecisaoDoCandidatoSchema,
  DetalheDoCandidatoSchema,
  MetricaDeIdentidadeSchema,
  PaginaDeCandidatosSchema,
  RejeitarMergeSchema,
} from './identidade.js';
import { MeSchema, SegurancaDaContaSchema } from './me.js';
import { OrganizacoesDaPessoaSchema } from './entrada.js';
import {
  AlterarPessoaSchema,
  AlterarTimeSchema,
  AlterarUnidadeSchema,
  ConvidarPessoaSchema,
  PessoaConvidadaSchema,
  CriarTimeSchema,
  CriarUnidadeSchema,
  DesativacaoSchema,
  DesativarPessoaSchema,
  LimitesDaOrganizacaoSchema,
  PessoaSchema,
  PessoasSchema,
  RedefinicaoDeSenhaSchema,
  RedefinirSenhaSchema,
  RedistribuicaoSchema,
  CarteiraDaPessoaSchema,
  ContratarArmazenamentoSchema,
  RedistribuirSchema,
  ReativarPessoaSchema,
  ReprovisionamentoSchema,
  TimeSchema,
  TimesSchema,
  UnidadeSchema,
  UnidadesSchema,
} from './administracao.js';
import {
  CompletarRevendaSchema,
  EncerrarRevendaSchema,
  ReconciliacaoForcadaSchema,
  ProvisionarRevendaSchema,
  RevendaCompletadaSchema,
  RevendaDesprovisionadaSchema,
  RevendaProvisionadaSchema,
  CriacaoRetomadaSchema,
} from './revendas.js';

export interface ParametroDeRota {
  readonly nome: string;
  readonly em: 'path' | 'query' | 'header';
  readonly descricao: string;
  readonly obrigatorio?: boolean;
}

import {
  ConexaoSchema,
  ConexoesSchema,
  CriarConexaoSchema,
  EditarConexaoSchema,
  ExecutarOperacaoSchema,
  HistoricoDaOperacaoSchema,
  MapaDoBancoSchema,
  OperacaoSchema,
  OperacoesSchema,
  ResultadoDaExecucaoSchema,
  SalvarOperacaoSchema,
  TesteDeConexaoSchema,
} from './integracoes-de-dados.js';
import {
  AutomacaoSchema,
  AutomacoesSchema,
  BlocosDoEditorSchema,
  CancelamentoDeEsperaSchema,
  ConfiguracaoDaAutomacaoSchema,
  CriadasDoModeloSchema,
  CriarAPartirDeModeloSchema,
  EditarAutomacaoDoModeloSchema,
  CriarAutomacaoSchema,
  EditarAutomacaoSchema,
  IdentificarAutomacaoSchema,
  SimulacaoDaAutomacaoSchema,
  SimularAutomacaoSchema,
  ExecucaoDaAutomacaoSchema,
  ExecucoesDaAutomacaoSchema,
  HistoricoDaAutomacaoSchema,
  InstanciasEmEsperaSchema,
  ModeloDeAutomacaoSchema,
  ModelosDeAutomacaoSchema,
  PainelDasAutomacoesSchema,
  RestaurarAutomacaoSchema,
  ValidacaoDaAutomacaoSchema,
} from './automacoes.js';
import {
  AtenderPedidoDeListaSchema,
  PedidoDeListaSchema,
  PedidosDeListaSchema,
  PedirListaSchema,
  RecusarPedidoDeListaSchema,
  CriarListaSchema,
  EditarListaSchema,
  ItensDaListaSchema,
  LeituraDaListaSchema,
  LerListaSchema,
  ListaSchema,
  ListasSchema,
} from './listas.js';

export interface Rota {
  readonly metodo: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly caminho: string;
  readonly resumo: string;
  readonly descricao?: string;
  /** Schema do corpo da requisição, quando houver. */
  readonly corpo?: z.ZodType;
  /**
   * O corpo pode faltar: a rota aceita o pedido sem ele, o OpenAPI diz
   * `required: false` e o cliente não o exige. É o corpo que nasce depois da
   * rota, sem quebrar quem já a chamava sem corpo (régua do `/v1`, §4).
   */
  readonly corpoOpcional?: true;
  /** Schema da resposta de sucesso. */
  readonly resposta: z.ZodType;
  /** Código HTTP do sucesso. Ausente = 200. */
  readonly status?: number;
  /**
   * `false` só para rota que não exige token.
   *
   * Hoje há UMA: `GET /v1/anexos/chatwoot/{token}`. Ela é servida ao `<audio>`
   * e ao `<img>` do navegador, que não mandam `Authorization` — e quem autoriza
   * é a assinatura da própria URL, com prazo de cinco minutos. Rota aberta aqui
   * significa "sem sessão", nunca "sem autorização".
   */
  readonly autenticada?: boolean;
  /**
   * Tipo de mídia da resposta de sucesso. Ausente = `application/json`.
   * `text/event-stream` (C21): a `resposta` descreve o JSON do campo `data:`
   * de cada evento — o fluxo em si não é um corpo único.
   * `application/octet-stream` (C02): o corpo são os BYTES do arquivo, e o tipo
   * real vai no cabeçalho da resposta — o contrato não sabe se o anexo é um
   * áudio ou um PDF, só que é um arquivo.
   */
  readonly conteudoDaResposta?:
    'application/json' | 'text/event-stream' | 'application/octet-stream';
  readonly parametros?: readonly ParametroDeRota[];
}

export const ROTAS = {
  organizacoesDaPessoa: {
    metodo: 'GET',
    caminho: '/v1/entrada/organizacoes',
    resumo: 'Em quais organizações esta pessoa entra',
    descricao:
      'A segunda tela do login. O primeiro passo prova quem a pessoa é; este responde onde ela ' +
      'pode entrar, para ela escolher. ' +
      'É a ÚNICA rota de /v1 que aceita token sem o claim `tenant_id` — ele ainda não existe, ' +
      'porque a escolha é justamente o que está sendo feita. O que a mantém estreita é o que ela ' +
      'devolve: nome de organização e papel da própria pessoa, sem nenhum dado de cliente, ' +
      'conversa ou negócio. ' +
      'Vínculo suspenso VEM na lista, com o status: esconder faria a organização sumir sem ' +
      'explicação para quem foi suspenso nela e continua ativo em outra. Quem autoriza a ' +
      'entrada continua sendo o vínculo ativo, conferido a cada requisição.',
    resposta: OrganizacoesDaPessoaSchema,
  },
  me: {
    metodo: 'GET',
    caminho: '/v1/me',
    resumo: 'Sessão da pessoa autenticada',
    descricao:
      'Devolve usuário, tenant do vínculo, papel, escopo e feature flags. ' +
      'O papel vem de core.memberships, não do claim do token: o claim congela ' +
      'no momento da emissão e pessoa rebaixada continuaria com o papel antigo ' +
      'até o token expirar.',
    resposta: MeSchema,
  },
  segurancaDaConta: {
    metodo: 'GET',
    caminho: '/v1/me/security',
    resumo: 'A conta tem segundo fator?',
    descricao:
      'Lido do provedor de identidade, e não do token: o `amr` diz como a pessoa entrou ' +
      'NESTA sessão, não o que a conta tem. Quem cadastra o segundo fator com a sessão já ' +
      'aberta continua com `amr: ["pwd"]` até sair e entrar de novo — e a tela, lendo dali, ' +
      'seguiria avisando que falta cadastrar depois de já existir. Rota própria porque a ' +
      'consulta é externa e o `/v1/me` é caminho quente.',
    resposta: SegurancaDaContaSchema,
  },
  eventos: {
    metodo: 'GET',
    caminho: '/v1/eventos',
    resumo: 'Fluxo SSE de notificações de mudança',
    descricao:
      'Server-Sent Events (ADR-0004): um fluxo por sessão, multiplexando os recursos ' +
      'assinados. Cada evento notifica QUE um recurso mudou — nunca o dado em si; o ' +
      'cliente rebusca pela API, onde a autorização já existe. Toda assinatura é ' +
      'autorizada individualmente na abertura; recurso inexistente ou de outro tenant ' +
      'recusa o fluxo com 404. Reconexão com Last-Event-ID reentrega o que ficou para ' +
      'trás na janela de retenção do canal.',
    resposta: NotificacaoDeMudancaSchema,
    conteudoDaResposta: 'text/event-stream',
    parametros: [
      {
        nome: 'assinaturas',
        em: 'query',
        descricao:
          'Recursos a observar, separados por vírgula — conversation:{id canônico}, ' +
          'inbox:{id externo}, opportunity:{id canônico}, notificacao:eu (as notificações de ' +
          'quem abre o fluxo; o id é sempre `eu`, porque a pessoa é a da sessão) e sessao:eu ' +
          '(o `/v1/me` de quem abre o fluxo mudou: funcionalidade, papel, acesso ao prontuário ' +
          'ou preferências). Pelo menos um.',
        obrigatorio: true,
      },
      {
        nome: 'Last-Event-ID',
        em: 'header',
        descricao:
          'Id do último evento recebido — o EventSource o manda sozinho no reconnect; ' +
          'o fluxo reentrega o que ficou para trás desde ele.',
      },
    ],
  },
  listarClientes: {
    metodo: 'GET',
    caminho: '/v1/customers',
    resumo: 'Lista de clientes da organização, no escopo de quem pergunta',
    descricao:
      'O caminho até a ficha, que não existia: a `clienteTrezentosESessenta` entrega o 360 ' +
      'por id, e nada entregava a lista. Busca única — nome, e-mail, telefone e documento no ' +
      'MESMO campo, e o servidor decide o que o termo é: dígitos que formam CPF ou CNPJ ' +
      'válido viram busca por documento, comparada pelo HMAC (C10), sem nunca decifrar a ' +
      'coluna. Telefone e e-mail voltam MASCARADOS: a lista é onde o vazamento em volume ' +
      'acontece (A06), e a ficha é quem revela, com a leitura registrada. `total_no_escopo` ' +
      'conta DEPOIS do recorte por carteira — contar antes daria "1.240" a quem enxerga ' +
      'oitenta. Cliente sem conversa e sem oportunidade aparece para todos: ele não está na ' +
      'carteira de ninguém, e escondê-lo o perderia para sempre. A lista é uma FILA DE ' +
      'TRABALHO, e não uma agenda: `recorte` são as filas que alguém pergunta de manhã, ' +
      '`contagens` diz onde há trabalho antes de filtrar, e a linha traz responsável, ' +
      'etiquetas e o maior negócio aberto — que é o que faz alguém decidir para quem ligar.',
    resposta: PaginaDeClientesSchema,
    parametros: [
      {
        nome: 'busca',
        em: 'query',
        descricao: 'Nome, e-mail, telefone ou documento — um campo só.',
      },
      {
        nome: 'responsavel',
        em: 'query',
        descricao: 'Id do atendente na engine: só clientes com conversa dele.',
      },
      {
        nome: 'campos',
        em: 'query',
        descricao:
          'Filtro por campo personalizado (#568): condições separadas por `;`, cada uma ' +
          '`<id do campo>:<valor>`. Lista de opções: os ids das opções, separados por vírgula ' +
          '(qualquer uma). Sim/não: `sim` ou `nao`. Data: `AAAA-MM-DD..AAAA-MM-DD`, com um ' +
          'lado opcional. Campo de outra organização é 404.',
      },
      {
        nome: 'recorte',
        em: 'query',
        descricao:
          'A fila: `esfriando` (negócio aberto e trinta dias sem contato), `meus`, ' +
          '`sem_responsavel`, `com_negocio` ou `todos`. Padrão `todos` — quem chama a API ' +
          'direto pede a base; quem abre a tela recebe `esfriando`, que é decisão de tela.',
      },
      {
        nome: 'ordenar',
        em: 'query',
        descricao: '`ultimo_contato` (padrão), `nome` ou `valor`.',
      },
      {
        nome: 'direcao',
        em: 'query',
        descricao:
          '`asc` ou `desc`. O padrão depende da coluna: data e valor descem (o maior ' +
          'primeiro), nome sobe.',
      },
      { nome: 'cursor', em: 'query', descricao: 'O `cursor_proximo` da página anterior (§6.1).' },
      { nome: 'limite', em: 'query', descricao: 'Itens por página: 1 a 100, padrão 30.' },
    ],
  },
  /*
   * A EXPORTAÇÃO vem antes do `/{id}` — e não é estilo.
   *
   * O Nest casa na ordem de declaração, e `/v1/customers/export` bate no
   * padrão `/v1/customers/:id`. Declarada depois, ela viraria uma busca pelo
   * cliente de id "export" — um 404 que ninguém explicaria.
   */
  exportarClientes: {
    metodo: 'GET',
    caminho: '/v1/customers/export',
    resumo: 'Exportar a lista de clientes, com o contato inteiro',
    descricao:
      'O mesmo recorte e a mesma busca da lista, sem paginação e com telefone e e-mail ' +
      'INTEIROS — uma planilha de contatos mascarados não serve para ligar para ninguém, e ' +
      'seria uma exportação que não exporta. O que sustenta a diferença não é o formato, é ' +
      'o REGISTRO: cada cliente do arquivo vira uma entrada em `core.data_access_log` ' +
      '(§13.1) na MESMA transação da leitura, e só o papel com `cliente:exportar` pode ' +
      'pedir — hoje, o `admin_tenant` (§13.2). Exige step-up (`cliente.exportar`, sem alvo, ' +
      'com justificativa): sem concessão em vigor, a resposta é 403 com a extensão ' +
      '`step_up`; a concessão vale 15 minutos. O DOCUMENTO não vai, nem cifrado: lê-lo ' +
      'exige o step-up da B17 com justificativa, e uma planilha seria a porta dos fundos ' +
      'disso. Há teto de linhas, e o que ficou de fora é DITO em `truncada_em` — arquivo ' +
      'truncado em silêncio é pior que recusa, porque quem o recebe acha que tem tudo.',
    resposta: ExportacaoDeClientesSchema,
    parametros: [
      { nome: 'busca', em: 'query', descricao: 'O mesmo campo único da lista.' },
      { nome: 'recorte', em: 'query', descricao: 'A mesma fila da lista. Padrão `todos`.' },
      {
        nome: 'responsavel',
        em: 'query',
        descricao: 'Id do atendente na engine: só clientes com conversa dele.',
      },
      {
        nome: 'campos',
        em: 'query',
        descricao:
          'Filtro por campo personalizado (#568): condições separadas por `;`, cada uma ' +
          '`<id do campo>:<valor>`. Lista de opções: os ids das opções, separados por vírgula ' +
          '(qualquer uma). Sim/não: `sim` ou `nao`. Data: `AAAA-MM-DD..AAAA-MM-DD`, com um ' +
          'lado opcional. Campo de outra organização é 404.',
      },
    ],
  },
  criarCliente: {
    metodo: 'POST',
    caminho: '/v1/customers',
    resumo: 'Cadastrar um cliente à mão',
    descricao:
      'O caminho para quem chega pelo balcão ou pelo telefone, antes de existir conversa. ' +
      'A pessoa nasce no CRM — que é dono dela (§4) — e o canônico e o vínculo são escritos ' +
      'depois, na mesma ordem da edição: com a ordem invertida, uma falha do motor deixaria ' +
      'o nosso lado afirmando o que o CRM não sabe. RECUSA DUPLICATA antes de escrever: ' +
      'documento, telefone ou e-mail que já existam em cliente ativo devolvem 409 com o id ' +
      'do que já está lá. A escada de identidade (C09-C11) existe para unir o que chegou ' +
      'por dois canais, e um botão que cria a segunda cópia à mão desfaz isso — nome ' +
      'repetido, esse, não recusa: duas Marias existem. Exige `Idempotency-Key` (§6.1), ' +
      'porque é comando com efeito externo.',
    corpo: CriarClienteSchema,
    /*
     * A MESMA forma da edição, e de propósito: a tela faz a mesma coisa com as
     * duas respostas — fecha o formulário e recarrega a ficha. Duas formas
     * quase iguais fariam a tela ter dois caminhos para o mesmo desfecho.
     */
    resposta: ClienteAtualizadoSchema,
  },
  cartaoDeContato: {
    metodo: 'GET',
    caminho: '/v1/customers/{id}/cartao-de-contato',
    resumo: 'Cartão de um contato, para compartilhar na conversa',
    descricao:
      'Nome, cargo e telefone COMPLETO de um contato, para o botão "Compartilhar contato" do ' +
      'editor (#595). A lista devolve o telefone mascarado; este devolve o de um só, o escolhido. ' +
      'Vale a mesma carteira da lista: contato de outra organização, ou fora do que a pessoa vê, ' +
      'responde 404. Toda leitura é registrada em `core.data_access_log` (§13.1): quem leu o ' +
      'telefone de quem. O registro não diz para quem foi enviado, porque ler não é enviar.',
    resposta: CartaoDeContatoSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1), nunca o da engine.' },
    ],
  },
  clienteTrezentosESessenta: {
    metodo: 'GET',
    caminho: '/v1/customers/{id}/360',
    resumo: 'Cliente 360',
    descricao:
      'A tela mais pesada do produto numa requisição (§6): pessoa, empresa, oportunidades, ' +
      'conversas, tarefas e timeline. Pessoa, oportunidades e tarefas vêm FRESCAS do cadastro ' +
      'comercial, numa leitura só; empresa, conversas e timeline vêm de projeção. ' +
      'Cada bloco carrega o próprio carimbo de frescor, porque cada um degrada sozinho. ' +
      'Abertura progressiva: os blocos vêm limitados, com cursor para o resto (§11.2). ' +
      'Todo acesso é registrado em `core.data_access_log` (§13.1).',
    resposta: Cliente360Schema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1), nunca o da engine.' },
      {
        nome: 'conversas_cursor',
        em: 'query',
        descricao: 'Cursor do bloco de conversas, devolvido em `conversas.cursor_proximo`.',
      },
      {
        nome: 'timeline_cursor',
        em: 'query',
        descricao: 'Cursor do bloco de timeline, devolvido em `timeline.cursor_proximo`.',
      },
    ],
  },
  atualizarCliente: {
    metodo: 'PATCH',
    caminho: '/v1/customers/{id}',
    resumo: 'Editar o cadastro do cliente',
    descricao:
      'Nome, e-mail, telefone e cargo — os campos que a matriz da §4 dá à PESSOA no cadastro ' +
      'comercial. Escreve no cadastro primeiro e alinha o canônico depois: na ordem inversa, ' +
      'uma falha lá deixaria este lado dizendo o que o cadastro não sabe, e o próximo evento ' +
      'do contato reenviaria o valor velho por cima do novo. Campo ausente é "não mexer"; ' +
      '`null` apaga. Cidade não entra: no cadastro comercial, cidade é da empresa. O ' +
      'documento (CPF ou CNPJ) fica fora do cadastro: ele é guardado como HMAC para casar e ' +
      'cifrado para ler com step-up (C10).',
    corpo: AtualizarClienteSchema,
    resposta: ClienteAtualizadoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  iniciarAtendimento: {
    metodo: 'POST',
    caminho: '/v1/customers/{id}/iniciar-atendimento',
    resumo: 'Abre conversa com quem nunca escreveu, por modelo aprovado',
    descricao:
      'Cliente cadastrado à mão não tem conversa, e até aqui não havia por onde falar com ' +
      'ele: toda conversa nascia de mensagem RECEBIDA, e `conversa: criar` era permissão só ' +
      'do papel `sistema`. ' +
      'Só sai por MODELO aprovado, porque o WhatsApp não permite texto livre para quem não ' +
      'escreveu nas últimas 24 horas — aceitar texto aqui produziria a recusa silenciosa do ' +
      'provedor. A caixa vai no pedido e não é deduzida: revenda com dois números mandaria a ' +
      'primeira mensagem pelo que calhar. ' +
      'Exige `Idempotency-Key` (§6.1): sem ela, o segundo clique manda o modelo duas vezes ' +
      'para o cliente. Recusa quando o cliente não tem telefone, quando a caixa está fora do ' +
      'escopo de quem pede, e quando o número de valores não bate com as variáveis do modelo. ' +
      'O `conversa_id` canônico vem NULO: a cunhagem acontece na ingestão, quando o evento ' +
      'volta pelo webhook.',
    corpo: IniciarAtendimentoSchema,
    resposta: AtendimentoIniciadoSchema,
    status: 201,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  definirEtiquetasDoCliente: {
    metodo: 'PUT',
    caminho: '/v1/customers/{id}/etiquetas',
    resumo: 'Substitui as etiquetas próprias de um cliente',
    descricao:
      'As etiquetas que o 360 já mostrava são DERIVADAS das conversas do cliente, então quem ' +
      'nunca conversou não tinha nenhuma e não havia como pôr — e é justamente o cliente que ' +
      'chegou pelo cadastro ou pelo portal aquele sobre quem a equipe menos sabe. Estas são ' +
      'dele, não de conversa nenhuma, e por isso não passam pelo motor de conversa: guardar ' +
      'como rótulo de contato deixaria de fora quem não tem contato lá. ' +
      'PUT porque substitui, como o da conversa: mandar a lista inteira é obrigação de quem ' +
      'chama. Não confere contra o catálogo, pela mesma razão que a conversa não confere — e ' +
      'aqui ela rende mais, porque o catálogo vive no motor de conversa e some quando ele cai.',
    corpo: DefinirEtiquetasSchema,
    resposta: EtiquetasDoClienteSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  notasDoCliente: {
    metodo: 'GET',
    caminho: '/v1/customers/{id}/notas',
    resumo: 'As notas internas escritas sobre o cliente, da mais recente para a mais antiga',
    descricao:
      'O que vale sobre o cliente para além de qualquer negócio: "prefere ser chamado depois das ' +
      '18h", "é sócio da oficina da rua de baixo". Vem com o NOME de quem escreveu, porque quem ' +
      'lê quase nunca é quem escreveu — é quem pega a conversa de outra pessoa. Mora em tabela ' +
      'nossa (migração 059), como a nota da oportunidade, para não depender do CRM de pé. ' +
      'Lê quem lê cliente; o cliente é da organização, e a carteira não recorta.',
    resposta: NotasDoClienteSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  fichaDoPaciente: {
    metodo: 'GET',
    caminho: '/v1/customers/{id}/ficha-do-paciente',
    resumo: 'A ficha do paciente: o que a clínica guarda sobre o cliente além do cadastro',
    descricao:
      'Nome social, nascimento, endereço, profissão, estado civil, contato de emergência e a ' +
      'preferência de contato (#811). Só nas organizações com a funcionalidade "clinica" ligada; ' +
      'sem ela, 404, igual ao cliente que não existe. Sem ficha preenchida, os campos e a versão ' +
      'vêm nulos. Lê quem lê cliente. Não traz dado de saúde.',
    resposta: FichaDoPacienteSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  salvarFichaDoPaciente: {
    metodo: 'PUT',
    caminho: '/v1/customers/{id}/ficha-do-paciente',
    resumo: 'Salvar a ficha do paciente inteira',
    descricao:
      'Cria ou substitui a ficha (#811): todo campo vai, e `null` apaga. A `versao` é a que a ' +
      'tela leu (nula na primeira vez); gravar sobre outra devolve 409 `CONFLITO_DE_VERSAO`, ' +
      'porque outra pessoa salvou antes. Grava quem edita cliente. Só com a funcionalidade ' +
      '"clinica" ligada; sem ela, 404.',
    corpo: SalvarFichaDoPacienteSchema,
    resposta: FichaDoPacienteSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  pacienteDoCliente: {
    metodo: 'GET',
    caminho: '/v1/customers/{id}/paciente',
    resumo: 'O paciente do cliente no sistema da clínica (SISAC)',
    descricao:
      'O vínculo do contato com o paciente no SISAC (#1024) e, se houver, os dados atuais dele e ' +
      'as últimas consultas, lidos na hora pelas partes `ler_paciente` e `historico_paciente` da ' +
      'agenda da organização (ADR-0028). O CliqCRM guarda só o código. SISAC fora: o vínculo vem ' +
      'com `sisac: "fora"`, sem falhar. A leitura entra no registro de acesso do cliente. Lê quem ' +
      'lê cliente. Só com a funcionalidade "clinica" ligada e a agenda com `buscar_paciente` e ' +
      '`ler_paciente`; sem a funcionalidade, 404; sem as partes, 409 `CONFLITO_DE_ESTADO`.',
    resposta: PacienteDoClienteSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  buscarPacienteDoCliente: {
    metodo: 'POST',
    caminho: '/v1/customers/{id}/paciente/busca',
    resumo: 'Procura o paciente no SISAC pelo CPF, pelo nome ou código e pelo telefone do contato',
    descricao:
      'Roda `buscar_paciente` com o CPF digitado, `buscar_paciente_por_termo` com o nome e ' +
      'sobrenome ou o código digitado e `buscar_paciente_por_telefone` com o telefone do ' +
      'contato; cada parte só é exigida quando usada (sem ela, 409). O termo exige a marca do ' +
      'prontuário (senão 403) e nome e sobrenome (senão 400), devolve no máximo 10 pacientes e ' +
      'deixa na trilha os códigos devolvidos, nunca o termo. O telefone é o principal do ' +
      'contato, lido no servidor (#1024). Devolve os candidatos com o CPF só nos ' +
      'dois últimos dígitos, por onde cada um foi achado e se já está vinculado a outro contato. ' +
      'Achado pelo telefone é sugestão: a mesma linha costuma ser da família inteira. Entra no ' +
      'registro de acesso do cliente. Busca quem edita cliente. Só com a funcionalidade ' +
      '"clinica" ligada, senão 404. SISAC fora: 503.',
    corpo: BuscarPacienteDoClienteSchema,
    resposta: CandidatosAPacienteSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  ultimasVezesDoPaciente: {
    metodo: 'GET',
    caminho: '/v1/customers/{id}/paciente/ultimas-vezes',
    resumo: 'Os atendimentos do paciente vinculado no SISAC ("Últimas vezes")',
    descricao:
      'Roda `ultimas_vezes_paciente` com o código do vínculo (nunca com um código da tela): os ' +
      'atendimentos do paciente no SISAC, com movimento, situação, tipo, médico, procedimentos, ' +
      'convênio, plano, clínica e local de origem (sem observação nem matrícula), do mais novo ao mais ' +
      'antigo. Médico e procedimentos revelam a condição do paciente: lê quem lê cliente e tem a ' +
      'marca do prontuário (ADR-0022), senão 403. Entra no registro de acesso do cliente. Sem ' +
      'vínculo ou sem a parte ligada, 409 `CONFLITO_DE_ESTADO`. Só com a funcionalidade ' +
      '"clinica" ligada, senão 404. SISAC fora: 503.',
    resposta: UltimasVezesDoPacienteSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  vincularPacienteDoCliente: {
    metodo: 'PUT',
    caminho: '/v1/customers/{id}/paciente',
    resumo: 'Vincula o contato a um paciente do SISAC',
    descricao:
      'Grava o vínculo (#1024) depois de conferir, pela parte `ler_paciente`, que o código existe ' +
      'no SISAC. Um contato tem um paciente: vincular de novo troca o anterior. Um paciente fica ' +
      'num contato só: já vinculado a outro, 409 `CONFLITO_DE_ESTADO`. Código que o SISAC não ' +
      'tem: 404. SISAC fora: 503, e nada é gravado. Entra na trilha. Grava quem edita cliente. ' +
      'Só com a funcionalidade "clinica" ligada, senão 404.',
    corpo: VincularPacienteDoClienteSchema,
    resposta: PacienteDoClienteSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  desvincularPacienteDoCliente: {
    metodo: 'DELETE',
    caminho: '/v1/customers/{id}/paciente',
    resumo: 'Desfaz o vínculo do contato com o paciente do SISAC',
    descricao:
      'Apaga só o vínculo no CliqCRM (#1024); nada muda no SISAC. Sem vínculo, `removido: ' +
      'false`. Entra na trilha. Grava quem edita cliente. Só com a funcionalidade "clinica" ' +
      'ligada, senão 404.',
    resposta: z.object({ removido: z.boolean() }),
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  cadastrarPacienteDoCliente: {
    metodo: 'POST',
    caminho: '/v1/customers/{id}/paciente/cadastro',
    resumo: 'Cadastra o paciente no SISAC e vincula o contato a ele',
    descricao:
      'Procura o CPF antes: o SISAC já tem → 409 `CONFLITO_DE_ESTADO`, e nada é gravado. Senão ' +
      'roda a parte `cadastrar_paciente` da agenda (ADR-0028), acha o código pelo CPF e grava o ' +
      'vínculo (#1024). Exige `Idempotency-Key`: o segundo clique não cadastra duas vezes. Entra ' +
      'no registro de acesso, com os nomes dos campos, e na trilha. Grava quem edita cliente. Só ' +
      'com a funcionalidade "clinica" ligada, senão 404. SISAC fora: 503.',
    corpo: CadastrarPacienteDoClienteSchema,
    resposta: PacienteDoClienteSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' },
      {
        nome: 'Idempotency-Key',
        em: 'header',
        obrigatorio: true,
        descricao: 'Uma por tentativa de cadastro; o reenvio devolve a mesma resposta.',
      },
    ],
  },
  documentosDoPaciente: {
    metodo: 'GET',
    caminho: '/v1/customers/{id}/documentos-do-paciente',
    resumo: 'Os documentos do paciente, do mais novo ao mais antigo',
    descricao:
      'Identidade, carteirinha, termos e contratos (#811), separados dos documentos da ' +
      'oportunidade. Cada um vem com um link de abrir assinado, que vale cinco minutos: a ' +
      'listagem emite um novo a cada leitura, e entra no registro de acesso. Lê quem lê ' +
      'cliente. Só nas organizações com a funcionalidade "clinica" ligada; sem ela, 404.',
    resposta: DocumentosDoPacienteSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  enviarDocumentosDoPaciente: {
    metodo: 'POST',
    caminho: '/v1/customers/{id}/documentos-do-paciente',
    resumo: 'Enviar documentos do paciente',
    descricao:
      'Formulário com arquivos, no campo `arquivos` (até 10, até 25 MB cada), a ' +
      '`categoria` de todos eles e, se houver, a `validade`. Vão para o armazenamento, um de ' +
      'cada vez. Grava quem edita cliente. Só com a funcionalidade "clinica" ligada; sem ela, 404.',
    corpo: EnviarDocumentosDoPacienteSchema,
    resposta: DocumentosDoPacienteSchema,
    status: 201,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  removerDocumentoDoPaciente: {
    metodo: 'DELETE',
    caminho: '/v1/customers/{id}/documentos-do-paciente/{documento}',
    resumo: 'Apagar um documento do paciente',
    descricao:
      'Apaga o documento e manda o arquivo para a lixeira do armazenamento. Confere antes que ' +
      'o documento é deste paciente: de outro, ou de outra organização, é 404. Grava quem edita ' +
      'cliente.',
    resposta: z.object({ removido: z.boolean() }),
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' },
      { nome: 'documento', em: 'path', descricao: 'Id do documento.' },
    ],
  },
  documentoDoPaciente: {
    metodo: 'GET',
    caminho: '/v1/anexos/paciente/{token}',
    resumo: 'Abre um documento do paciente',
    descricao:
      'Sem sessão, como o documento da oportunidade: quem abre é o navegador, por link. ' +
      'Autoriza a ASSINATURA da URL, válida por cinco minutos e emitida pela listagem, que ' +
      'passa pela matriz. Responde 302 para o endereço assinado do armazenamento, que vence ' +
      'junto com este link. Armazenamento fora: 503. Vencido, adulterado, de outro tipo de ' +
      'anexo ou de documento que não é do paciente assinado: 404, sem distinguir os casos.',
    resposta: z.string(),
    conteudoDaResposta: 'application/octet-stream',
    autenticada: false,
    parametros: [
      {
        nome: 'token',
        em: 'path',
        descricao: 'A assinatura emitida pela listagem dos documentos do paciente.',
      },
    ],
  },
  conveniosDoPaciente: {
    metodo: 'GET',
    caminho: '/v1/customers/{id}/convenios',
    resumo: 'Os convênios do paciente, o principal primeiro',
    descricao:
      'Particular ou convênio, com operadora, plano, os quatro últimos caracteres da ' +
      'carteirinha e a validade (#811). Só nas organizações com a funcionalidade "clinica" ' +
      'ligada; sem ela, 404, igual ao cliente que não existe. Lê quem lê cliente. O número ' +
      'inteiro da carteirinha nunca volta.',
    resposta: ConveniosDoPacienteSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  criarConvenioDoPaciente: {
    metodo: 'POST',
    caminho: '/v1/customers/{id}/convenios',
    resumo: 'Acrescentar um convênio ao paciente',
    descricao:
      'O primeiro convênio do paciente nasce principal; marcar outro como principal tira o ' +
      'anterior. Particular não tem operadora, plano, carteirinha nem validade; convênio precisa ' +
      'de uma operadora ativa. A carteirinha é cifrada ao chegar. Grava quem edita cliente. ' +
      'Exige `Idempotency-Key` (§6.1): sem ela, o segundo clique cria o convênio duas vezes. ' +
      'Só com a funcionalidade "clinica" ligada; sem ela, 404.',
    corpo: CriarConvenioDoPacienteSchema,
    resposta: ConvenioDoPacienteSchema,
    status: 201,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  editarConvenioDoPaciente: {
    metodo: 'PATCH',
    caminho: '/v1/customers/{id}/convenios/{convenioId}',
    resumo: 'Corrigir um convênio do paciente',
    descricao:
      'Substitui os campos do convênio. `carteirinha` ausente mantém a que está, `null` tira e ' +
      'texto troca. O principal não se desmarca sozinho: para trocar, marque outro. A `versao` é ' +
      'a que a tela leu; gravar sobre outra devolve 409 `CONFLITO_DE_VERSAO`. Grava quem edita ' +
      'cliente. Convênio de outro paciente ou de outra organização é 404.',
    corpo: EditarConvenioDoPacienteSchema,
    resposta: ConvenioDoPacienteSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' },
      { nome: 'convenioId', em: 'path', descricao: 'Id do convênio.' },
    ],
  },
  removerConvenioDoPaciente: {
    metodo: 'DELETE',
    caminho: '/v1/customers/{id}/convenios/{convenioId}',
    resumo: 'Remover um convênio do paciente',
    descricao:
      'Apaga o convênio, com a carteirinha. Se era o principal, o mais antigo que sobrar ' +
      'herda. Grava quem edita cliente. Convênio de outro paciente ou de outra organização é 404.',
    resposta: z.object({ removido: z.boolean() }),
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' },
      { nome: 'convenioId', em: 'path', descricao: 'Id do convênio.' },
    ],
  },
  operadoras: {
    metodo: 'GET',
    caminho: '/v1/operadoras',
    resumo: 'As operadoras de convênio da organização, ativas e inativas',
    descricao:
      'As cadastradas em Configurações (`origem: manual`) e as mantidas pela agenda da ' +
      'clínica (`origem: agenda`), que não se editam por aqui (#811). Só nas organizações com a ' +
      'funcionalidade "clinica" ligada; sem ela, 404. Lê quem lê cliente.',
    resposta: OperadorasSchema,
  },
  criarOperadora: {
    metodo: 'POST',
    caminho: '/v1/operadoras',
    resumo: 'Cadastrar uma operadora de convênio',
    descricao:
      'Nome e, se houver, o registro de seis dígitos na ANS (#811). Grava quem edita a ' +
      'configuração da organização. Só com a funcionalidade "clinica" ligada; sem ela, 404.',
    corpo: CriarOperadoraSchema,
    resposta: OperadoraSchema,
    status: 201,
  },
  editarOperadora: {
    metodo: 'PATCH',
    caminho: '/v1/operadoras/{id}',
    resumo: 'Corrigir ou inativar uma operadora cadastrada à mão',
    descricao:
      'Inativar tira a operadora das opções de convênio novo, sem mexer nos que já a usam. A ' +
      'operadora mantida pela agenda da clínica é recusada com 409 `CONFLITO_DE_ESTADO`: a ' +
      'próxima sincronização desfaria a edição. Grava quem edita a configuração. Operadora de ' +
      'outra organização é 404.',
    corpo: EditarOperadoraSchema,
    resposta: OperadoraSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id da operadora.' }],
  },
  examesDoPaciente: {
    metodo: 'GET',
    caminho: '/v1/customers/{id}/exames',
    resumo: 'Os pedidos de exame do paciente, o mais novo primeiro',
    descricao:
      'Cada pedido com as etapas dele, na ordem, e em cada etapa quando e quem deu o ok ' +
      '(#811). Lê quem lê cliente; a leitura fica registrada, porque o exame é dado de saúde. ' +
      'Só nas organizações com a funcionalidade "clinica" ligada; sem ela, 404.',
    resposta: ExamesDoPacienteSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  criarExameDoPaciente: {
    metodo: 'POST',
    caminho: '/v1/customers/{id}/exames',
    resumo: 'Cadastrar um pedido de exame com as etapas dele',
    descricao:
      'As etapas vêm na ordem em que vão acontecer: da lista da clínica (com o id dela) ou ' +
      'escritas só para este pedido. Até 20. Grava quem edita cliente. Exige `Idempotency-Key` ' +
      '(§6.1): sem ela, o segundo clique cria o pedido duas vezes. Só nas organizações com a funcionalidade "clinica" ligada; sem ela, 404.',
    corpo: CriarExameDoPacienteSchema,
    resposta: ExameDoPacienteSchema,
    status: 201,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  editarExameDoPaciente: {
    metodo: 'PATCH',
    caminho: '/v1/customers/{id}/exames/{pedidoId}',
    resumo: 'Corrigir os dados de um pedido de exame',
    descricao:
      'Substitui os campos do pedido; as etapas mudam por `PUT .../etapas`. A `versao` é a que ' +
      'a tela leu; gravar sobre outra devolve 409 `CONFLITO_DE_VERSAO`. Pedido cancelado: 409 ' +
      '`CONFLITO_DE_ESTADO`. Pedido de outro paciente ou de outra organização é 404.',
    corpo: EditarExameDoPacienteSchema,
    resposta: ExameDoPacienteSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' },
      { nome: 'pedidoId', em: 'path', descricao: 'Id do pedido de exame.' },
    ],
  },
  definirEtapasDoExame: {
    metodo: 'PUT',
    caminho: '/v1/customers/{id}/exames/{pedidoId}/etapas',
    resumo: 'Montar e ordenar as etapas ainda abertas do pedido',
    descricao:
      'Troca as etapas sem ok pela lista nova, na ordem dela: acrescentar, tirar e reordenar ' +
      'é aqui. As que já têm ok ficam no começo, como aconteceram. A `versao` é a que a tela ' +
      'leu; outra devolve 409 `CONFLITO_DE_VERSAO`. Grava quem edita cliente.',
    corpo: DefinirEtapasDoExameSchema,
    resposta: ExameDoPacienteSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' },
      { nome: 'pedidoId', em: 'path', descricao: 'Id do pedido de exame.' },
    ],
  },
  concluirEtapaDoExame: {
    metodo: 'POST',
    caminho: '/v1/customers/{id}/exames/{pedidoId}/etapas/{etapaId}/ok',
    resumo: 'Dar o ok numa etapa do pedido',
    descricao:
      'Guarda quando e quem deu o ok. Segue a ordem: só a próxima etapa aberta recebe; outra ' +
      'devolve 409 `CONFLITO_DE_ESTADO` dizendo qual é a próxima. Sem `Idempotency-Key`: ' +
      'repetir esbarra na ordem, e não dá o ok duas vezes. Grava quem edita cliente.',
    resposta: ExameDoPacienteSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' },
      { nome: 'pedidoId', em: 'path', descricao: 'Id do pedido de exame.' },
      { nome: 'etapaId', em: 'path', descricao: 'Id da etapa do pedido.' },
    ],
  },
  desfazerEtapaDoExame: {
    metodo: 'DELETE',
    caminho: '/v1/customers/{id}/exames/{pedidoId}/etapas/{etapaId}/ok',
    resumo: 'Desfazer o ok da última etapa concluída',
    descricao:
      'Só a última etapa com ok pode ser desfeita; outra devolve 409 `CONFLITO_DE_ESTADO`. ' +
      'Grava quem edita cliente, e a trilha registra quem desfez.',
    resposta: ExameDoPacienteSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' },
      { nome: 'pedidoId', em: 'path', descricao: 'Id do pedido de exame.' },
      { nome: 'etapaId', em: 'path', descricao: 'Id da etapa do pedido.' },
    ],
  },
  cancelarExameDoPaciente: {
    metodo: 'POST',
    caminho: '/v1/customers/{id}/exames/{pedidoId}/cancelar',
    resumo: 'Cancelar um pedido de exame',
    descricao:
      'O pedido fica na lista, marcado como cancelado, e não muda mais. Cancelar de novo ' +
      'devolve 409 `CONFLITO_DE_ESTADO`. Grava quem edita cliente.',
    resposta: ExameDoPacienteSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' },
      { nome: 'pedidoId', em: 'path', descricao: 'Id do pedido de exame.' },
    ],
  },
  etapasDaClinica: {
    metodo: 'GET',
    caminho: '/v1/etapas-de-exame',
    resumo: 'As etapas de exame da organização, ativas e inativas',
    descricao:
      'A lista de onde cada pedido escolhe as suas, na ordem sugerida (#811). Lê quem lê ' +
      'cliente. Só nas organizações com a funcionalidade "clinica" ligada; sem ela, 404.',
    resposta: EtapasDaClinicaSchema,
  },
  criarEtapaDaClinica: {
    metodo: 'POST',
    caminho: '/v1/etapas-de-exame',
    resumo: 'Cadastrar uma etapa de exame',
    descricao:
      'Entra no fim da ordem. Grava quem edita a configuração da organização. Só nas organizações com a funcionalidade "clinica" ligada; sem ela, 404.',
    corpo: CriarEtapaDaClinicaSchema,
    resposta: EtapaDaClinicaSchema,
    status: 201,
  },
  editarEtapaDaClinica: {
    metodo: 'PATCH',
    caminho: '/v1/etapas-de-exame/{id}',
    resumo: 'Renomear ou inativar uma etapa de exame',
    descricao:
      'Inativar tira a etapa das opções de pedido novo, sem mexer nos que já a usam. Grava ' +
      'quem edita a configuração. Etapa de outra organização é 404.',
    corpo: EditarEtapaDaClinicaSchema,
    resposta: EtapaDaClinicaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id da etapa.' }],
  },
  ordenarEtapasDaClinica: {
    metodo: 'PUT',
    caminho: '/v1/etapas-de-exame/ordem',
    resumo: 'Reordenar as etapas de exame',
    descricao:
      'Todos os ids, uma vez cada, na ordem nova; faltar ou sobrar um é 400. É a ordem ' +
      'sugerida num pedido novo, e não muda os pedidos que já existem. Grava quem edita a ' +
      'configuração.',
    corpo: OrdenarEtapasDaClinicaSchema,
    resposta: EtapasDaClinicaSchema,
  },
  camposPersonalizados: {
    metodo: 'GET',
    caminho: '/v1/campos-personalizados',
    resumo: 'Os campos personalizados de um cadastro, arquivados também',
    descricao:
      'O catálogo da organização para um cadastro, na ordem em que aparece no formulário ' +
      '(#568, ADR-0023). Lê quem preenche os cadastros. Pedido de exame e tratamento só nas ' +
      'organizações com a funcionalidade "clinica" ligada; sem ela, 404.',
    resposta: CamposPersonalizadosSchema,
    parametros: [
      {
        nome: 'entidade',
        em: 'query',
        descricao: 'O cadastro: cliente, oportunidade, pedido_de_exame ou tratamento.',
      },
    ],
  },
  criarCampoPersonalizado: {
    metodo: 'POST',
    caminho: '/v1/campos-personalizados',
    resumo: 'Criar um campo personalizado',
    descricao:
      'Entra no fim da ordem do cadastro. Gestor e admin. Nome repetido entre os campos vivos ' +
      'do mesmo cadastro (sem caixa e sem acento) é 409. A etapa do "obrigatório a partir de" ' +
      'precisa existir no funil da organização.',
    corpo: CriarCampoPersonalizadoSchema,
    resposta: CampoPersonalizadoSchema,
    status: 201,
  },
  editarCampoPersonalizado: {
    metodo: 'PATCH',
    caminho: '/v1/campos-personalizados/{id}',
    resumo: 'Renomear, mexer nas opções, no obrigatório, ou arquivar um campo',
    descricao:
      'Arquivar tira o campo das telas de preencher, sem apagar o que já foi preenchido; ' +
      'desarquivar traz de volta. O tipo não muda. Versão desatualizada é 409. Gestor e admin. ' +
      'Campo de outra organização é 404.',
    corpo: EditarCampoPersonalizadoSchema,
    resposta: CampoPersonalizadoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id do campo.' }],
  },
  ordenarCamposPersonalizados: {
    metodo: 'PUT',
    caminho: '/v1/campos-personalizados/ordem',
    resumo: 'Reordenar os campos de um cadastro',
    descricao:
      'Todos os ids do cadastro, arquivados também, uma vez cada; faltar ou sobrar um é 400. ' +
      'É a ordem do formulário. Gestor e admin.',
    corpo: OrdenarCamposPersonalizadosSchema,
    resposta: CamposPersonalizadosSchema,
  },
  prontuarioDoPaciente: {
    metodo: 'GET',
    caminho: '/v1/customers/{id}/prontuario',
    resumo: 'O resumo do prontuário do paciente, o mais recente primeiro',
    descricao:
      'Só o resumo de cada atendimento (quando, quem atendeu e o tipo), com o código do ' +
      'registro no sistema de origem, onde o prontuário completo fica (ADR-0022). Lê quem lê ' +
      'cliente E tem a marca "acesso ao prontuário"; sem ela, 403, conferido antes do cliente. ' +
      'A leitura fica registrada. Só nas organizações com a funcionalidade "clinica" ligada; ' +
      'sem ela, 404. Lê AO VIVO do SISAC (#1144), pelo paciente vinculado ao cliente, as partes ' +
      '`historico_paciente` (agendamentos e consultas) e `exames_paciente` (exames feitos) da ' +
      'agenda da clínica: nada é gravado no CRM. `sisac` diz por que a lista vem vazia ' +
      '(`sem_vinculo`, `sem_partes`, `fora`, `parcial`).',
    resposta: ProntuarioDoPacienteSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  definirAcessoAoProntuario: {
    metodo: 'PUT',
    caminho: '/v1/admin/pessoas/{id}/acesso-ao-prontuario',
    resumo: 'Liga ou desliga o acesso de uma pessoa ao prontuário',
    descricao:
      'A marca por pessoa do ADR-0022: os papéis não separam a recepção do profissional de ' +
      'saúde. Grava quem gerencia pessoas, e a trilha registra quem ligou ou desligou. Vínculo ' +
      'de outra organização é 404. Só com a funcionalidade "clinica" ligada; sem ela, 404.',
    corpo: AcessoAoProntuarioSchema,
    resposta: AcessoAoProntuarioSchema,
    parametros: [{ nome: 'id', em: 'path', obrigatorio: true, descricao: 'Id do vínculo.' }],
  },
  tratamentosDoPaciente: {
    metodo: 'GET',
    caminho: '/v1/customers/{id}/tratamentos',
    resumo: 'Os tratamentos do paciente, o mais novo primeiro',
    descricao:
      'Cada tratamento com o nome e o valor de tabela copiados do SISAC, o valor negociado, as ' +
      'datas de fechamento e de início e o histórico de ajustes do valor (#811). Lê quem lê ' +
      'cliente; a leitura fica registrada, porque o tratamento é dado de saúde. Só nas organizações com a funcionalidade "clinica" ligada; sem ela, 404.',
    resposta: TratamentosDoPacienteSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  criarTratamentoDoPaciente: {
    metodo: 'POST',
    caminho: '/v1/customers/{id}/tratamentos',
    resumo: 'Cadastrar um tratamento no paciente',
    descricao:
      'O tratamento vem do catálogo do SISAC, ativo; o nome e o valor de tabela são copiados. ' +
      'Sem `valor_negociado_micros`, vale o de tabela; diferente dele, o primeiro ajuste fica ' +
      'registrado. Com `oportunidade_id` (deste paciente, na carteira, e quem pede edita ' +
      'oportunidade), o valor negociado passa a ser o valor dela no funil. Grava quem edita ' +
      'cliente. Exige `Idempotency-Key` (§6.1). Só nas organizações com a funcionalidade "clinica" ligada; sem ela, 404.',
    corpo: CriarTratamentoDoPacienteSchema,
    resposta: TratamentoDoPacienteSchema,
    status: 201,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  editarTratamentoDoPaciente: {
    metodo: 'PATCH',
    caminho: '/v1/customers/{id}/tratamentos/{tratamentoId}',
    resumo: 'Corrigir as datas, o pagamento, as sessões ou a situação de um tratamento',
    descricao:
      'Substitui esses campos; o valor muda por `POST .../valor`. A `versao` é a que a tela ' +
      'leu; gravar sobre outra devolve 409 `CONFLITO_DE_VERSAO`. Grava quem edita cliente. ' +
      'Tratamento de outro paciente ou de outra organização é 404.',
    corpo: EditarTratamentoDoPacienteSchema,
    resposta: TratamentoDoPacienteSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' },
      { nome: 'tratamentoId', em: 'path', descricao: 'Id do tratamento do paciente.' },
    ],
  },
  ajustarValorDoTratamento: {
    metodo: 'POST',
    caminho: '/v1/customers/{id}/tratamentos/{tratamentoId}/valor',
    resumo: 'Ajustar o valor negociado de um tratamento',
    descricao:
      'Registra o ajuste, com quem, quando e o motivo, e o valor novo passa a valer; ligado a ' +
      'uma oportunidade, vira também o valor dela. O mesmo valor não vira ajuste. Sem ' +
      '`Idempotency-Key`: repetir esbarra na versão, que já subiu. Grava quem edita cliente, ' +
      'sem limite de desconto por enquanto.',
    corpo: AjustarValorDoTratamentoSchema,
    resposta: TratamentoDoPacienteSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' },
      { nome: 'tratamentoId', em: 'path', descricao: 'Id do tratamento do paciente.' },
    ],
  },
  catalogoDeTratamentos: {
    metodo: 'GET',
    caminho: '/v1/tratamentos',
    resumo: 'Os tratamentos do SISAC, com o valor de tabela',
    descricao:
      'A lista de onde o tratamento do paciente é escolhido, ativos e inativos (#811). Mantida ' +
      'pelo SISAC, sem cadastro manual. Lê quem lê cliente. Só nas organizações com a funcionalidade "clinica" ligada; sem ela, 404.',
    resposta: CatalogoDeTratamentosSchema,
  },
  criarNotaDoCliente: {
    metodo: 'POST',
    caminho: '/v1/customers/{id}/notas',
    resumo: 'Escrever uma nota interna sobre o cliente',
    descricao:
      'Escreve quem lê cliente — a regra da nota da oportunidade (editar o negócio) deixaria de ' +
      'fora o agente, que só lê cadastro e é quem mais atende. ' +
      'Exige `Idempotency-Key` (§6.1): sem ela o segundo clique grava a mesma nota duas vezes, ' +
      'e como não há como apagar, a duplicata fica.',
    corpo: CriarNotaDoClienteSchema,
    resposta: NotaDoClienteSchema,
    status: 201,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' }],
  },
  editarNotaDoCliente: {
    metodo: 'PATCH',
    caminho: '/v1/customers/{id}/notas/{notaId}',
    resumo: 'Corrigir o texto de uma nota interna do cliente',
    descricao:
      'Altera quem escreveu a nota, ou quem edita cadastro de cliente — o gestor que arruma a ' +
      'nota de quem saiu de férias. A nota corrigida passa a mostrar `editada_em`, e a trilha ' +
      'de auditoria guarda quem corrigiu, sem o texto. ' +
      'Sem `Idempotency-Key`: repetir o mesmo PATCH deixa a nota no estado que ele descreve.',
    corpo: EditarNotaDoClienteSchema,
    resposta: NotaDoClienteSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' },
      { nome: 'notaId', em: 'path', descricao: 'Id da nota.' },
    ],
  },
  removerNotaDoCliente: {
    metodo: 'DELETE',
    caminho: '/v1/customers/{id}/notas/{notaId}',
    resumo: 'Excluir uma nota interna do cliente',
    descricao:
      'Apaga a nota. Mesma regra de quem corrige: quem escreveu, ou quem edita cadastro de ' +
      'cliente. A trilha de auditoria guarda quem apagou e de quem era a nota — nunca o texto, ' +
      'que é dado pessoal e sairia do alcance da eliminação do titular.',
    resposta: z.object({ removida: z.boolean() }),
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id canônico do cliente (§5.1).' },
      { nome: 'notaId', em: 'path', descricao: 'Id da nota.' },
    ],
  },
  criarTarefa: {
    metodo: 'POST',
    caminho: '/v1/tasks',
    resumo: 'Criar tarefa',
    descricao:
      'Comando unificado de tarefa. A tarefa nasce no cadastro comercial, com a ORIGEM — ' +
      'manual, automação, SLA, conector — registrada na API Core. Exige ' +
      '`Idempotency-Key` (§6.1): é comando com efeito externo.',
    corpo: CriarTarefaSchema,
    resposta: TarefaSchema,
  },
  listarTarefas: {
    metodo: 'GET',
    caminho: '/v1/tasks',
    resumo: 'Minhas tarefas e atrasos operacionais',
    descricao:
      'Servida do read model (D01), com o escopo da B16 como predicado. `atrasada` é ' +
      'DERIVADA na leitura, com o horário de atendimento do tenant: tarefa vencida às 22h ' +
      'de sexta não está atrasada até segunda de manhã (§11).',
    resposta: PaginaDeTarefasSchema,
    parametros: [
      { nome: 'cursor', em: 'query', descricao: 'Cursor da página anterior.' },
      {
        nome: 'responsavel',
        em: 'query',
        descricao: 'Id externo do responsável, ou `eu` para as do próprio usuário.',
      },
      {
        nome: 'status',
        em: 'query',
        descricao: 'TODO · IN_PROGRESS · DONE. Repetível por vírgula.',
      },
      {
        nome: 'somente_atrasadas',
        em: 'query',
        descricao: '`true` devolve só o que já conta como atraso operacional.',
      },
      { nome: 'limite', em: 'query', descricao: 'Itens por página (1 a 100; padrão 30).' },
    ],
  },
  listarResponsaveisDeTarefa: {
    metodo: 'GET',
    caminho: '/v1/tasks/responsaveis',
    resumo: 'A quem se pode atribuir uma tarefa',
    descricao:
      'A equipe dentro da carteira de quem pede (B45) — a MESMA régua que a criação usa ' +
      'para recusar responsável fora do escopo. Existe porque `GET /v1/admin/pessoas` só ' +
      'responde a quem administra a conta, e quem mais preenche o formulário de tarefa é ' +
      'o vendedor. Não traz contato: para escolher uma pessoa bastam nome e identificador.',
    resposta: ResponsaveisDeTarefaSchema,
  },
  lerTarefa: {
    metodo: 'GET',
    caminho: '/v1/tasks/{id}',
    resumo: 'A tarefa inteira, com o corpo',
    descricao:
      'Servida da PORT (ADR-0008), e não da projeção: `read.tasks` guarda título, status, ' +
      'prazo e responsável — o CORPO não está lá, e é ele que responde "o que exatamente ' +
      'eu combinei de fazer". Origem, prioridade e o atraso são somados pela API Core, que ' +
      'é onde eles vivem. Uma ida ao motor por tarefa aberta, e não por lista carregada.',
    resposta: TarefaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da tarefa (§5.1).' }],
  },
  alterarTarefa: {
    metodo: 'PATCH',
    caminho: '/v1/tasks/{id}',
    resumo: 'Alterar uma tarefa',
    descricao:
      'Campo AUSENTE não é tocado; campo presente com `null` limpa — mandar o objeto inteiro ' +
      'apagaria o prazo de quem só quis trocar o título. Título, corpo, prazo e responsável vão ' +
      'ao cadastro comercial; a PRIORIDADE fica na API Core, que é onde ela existe. Alvo, origem ' +
      'e status não entram: trocar o vínculo é outra operação, origem é história, e concluir ' +
      'tem rota própria.',
    corpo: AlterarTarefaSchema,
    resposta: TarefaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da tarefa (§5.1).' }],
  },
  removerOportunidade: {
    metodo: 'DELETE',
    caminho: '/v1/opportunities/{id}',
    resumo: 'Excluir uma oportunidade',
    descricao:
      'Exclusão LÓGICA no CRM (ADR-0007, regra 1): a oportunidade sai do funil, e o CRM ainda ' +
      'consegue restaurá-la; pela interface do CliqCRM não há desfazer. O card sai do quadro ' +
      'quando a projeção recebe o aviso do CRM. Vale a permissão de EDITAR a ' +
      'oportunidade, com o escopo da carteira: fora dela, ou de outra organização, é 404. ' +
      'Auditado, porque não tem volta. A interface precisa confirmar antes.',
    resposta: z.object({}).strict(),
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade (§5.1).' }],
  },
  removerTarefa: {
    metodo: 'DELETE',
    caminho: '/v1/tasks/{id}',
    resumo: 'Apagar uma tarefa',
    descricao:
      'APAGA, e não arquiva: a exclusão da tarefa é FÍSICA. A tarefa some da lista e da ' +
      'linha do tempo do cliente, onde deixa um buraco na ' +
      'sequência. Auditado por isso — sem o registro, "sumiu uma tarefa" é pergunta sem ' +
      'resposta possível. A interface precisa confirmar antes.',
    resposta: z.object({}).strict(),
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da tarefa (§5.1).' }],
  },
  concluirTarefa: {
    metodo: 'POST',
    caminho: '/v1/tasks/{id}/complete',
    resumo: 'Concluir tarefa',
    descricao:
      'Idempotente por natureza: concluir o que já está concluído devolve a tarefa como ' +
      'está, sem erro — duplo clique não é conflito.',
    resposta: TarefaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da tarefa (§5.1).' }],
  },
  listarTimeline: {
    metodo: 'GET',
    caminho: '/v1/timeline',
    resumo: 'Timeline de um cliente ou de uma oportunidade',
    descricao:
      'O que aconteceu, em ordem cronológica do FATO (§6.1). Servida de `read.timeline`, ' +
      'materializada pelos workers de projeção — unir as origens a cada consulta trava no ' +
      'primeiro cliente com um ano de histórico. Guarda referência e um trecho curto; o ' +
      'conteúdo completo vem da fonte, para não duplicar dado pessoal. Exige `cliente` OU ' +
      '`oportunidade`, nunca os dois.',
    resposta: PaginaDaTimelineSchema,
    parametros: [
      { nome: 'cliente', em: 'query', descricao: 'Id canônico do cliente (§5.1).' },
      { nome: 'oportunidade', em: 'query', descricao: 'Id canônico da oportunidade.' },
      {
        nome: 'tipo',
        em: 'query',
        descricao:
          'mensagem · mudanca_de_etapa · tarefa · nota · evento_de_portal. Repetível por vírgula.',
      },
      { nome: 'cursor', em: 'query', descricao: 'Cursor da página anterior.' },
      { nome: 'limite', em: 'query', descricao: 'Itens por página (1 a 200; padrão 50).' },
    ],
  },
  encerrarConversa: {
    metodo: 'POST',
    caminho: '/v1/conversations/{id}/resolve',
    resumo: 'Encerra ou reabre uma conversa',
    descricao:
      'O fluxo da §10.4: o usuário resolve no CliqCRM, o Chatwoot registra e emite evento, ' +
      'e a API Core reage ao evento. Este comando cobre os dois primeiros degraus — ele ' +
      'NÃO encerra o relógio operacional. Fazer isso antes da confirmação é a armadilha ' +
      'da task: o Chatwoot recusa por permissão, a conversa fica aberta lá e fechada aqui, ' +
      'e a métrica passa a mentir. Os efeitos (carimbo do encerramento, atividade na ' +
      'timeline, tarefa de próxima ação) chegam por evento e vêm listados em ' +
      '`efeitos_pendentes`. A `situacao` devolvida é a que a ENGINE aplicou, não a pedida. ' +
      'Exige Idempotency-Key: encerrar duas vezes não pode registrar dois encerramentos.',
    corpo: EncerrarConversaSchema,
    resposta: ConversaEncerradaSchema,
    status: 202,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id canônico da conversa (§5.1).' },
      {
        nome: 'Idempotency-Key',
        em: 'header',
        descricao: 'Chave do comando (B30). Repetir a mesma devolve a primeira resposta.',
        obrigatorio: true,
      },
    ],
  },
  listarCandidatosDeMerge: {
    metodo: 'GET',
    caminho: '/v1/identity/merge-candidates',
    resumo: 'Fila de revisão de identidade',
    descricao:
      'O degrau 6 da §5.2: as suspeitas que a escada NÃO mesclou por ficarem abaixo do ' +
      'limiar da organização. Cada item traz a contribuição de cada sinal (C11) e os dois ' +
      'clientes em disputa — revisor que vê só um score decide no chute. O `limiar` vem ' +
      'junto porque o score sozinho não significa nada: 0,72 é alto ou baixo conforme a ' +
      'régua. Ordenada por score decrescente, que é o mais caro deixar parado.',
    resposta: PaginaDeCandidatosSchema,
    parametros: [
      {
        nome: 'status',
        em: 'query',
        descricao:
          'merge_candidate (padrão) · aprovado · rejeitado · expirado. Repetível por ' +
          'vírgula — é assim que a tela monta o histórico de decisões.',
      },
      { nome: 'cursor', em: 'query', descricao: 'Cursor da página anterior.' },
      { nome: 'limite', em: 'query', descricao: 'Itens por página (1 a 100; padrão 30).' },
    ],
  },
  detalharCandidatoDeMerge: {
    metodo: 'GET',
    caminho: '/v1/identity/merge-candidates/{id}',
    resumo: 'Comparação lado a lado de um candidato',
    descricao:
      'O contexto que decide, e não só o score: conversas, oportunidades, vínculos de ' +
      'origem e última interação de CADA cliente. A decisão depende do contexto — é por ' +
      'ele que se sabe qual dos dois tem o histórico mais rico e deve sobreviver.',
    resposta: DetalheDoCandidatoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id do candidato.' }],
  },
  aprovarCandidatoDeMerge: {
    metodo: 'POST',
    caminho: '/v1/identity/merge-candidates/{id}/approve',
    resumo: 'Aprova a junção, com sobrevivente explícito',
    descricao:
      '`sobrevivente_id` é OBRIGATÓRIO e precisa ser um dos dois clientes do candidato. ' +
      'Não há padrão — nem "o mais antigo", nem "o de maior score": a armadilha da task é ' +
      'justamente o revisor não perceber qual ficou, e o cliente com o histórico mais rico ' +
      'virar o mesclado. Dispara o merge da C12, que é reversível por split. Exige alcance ' +
      'de organização inteira: mesclar cruza carteiras por natureza.',
    corpo: AprovarMergeSchema,
    resposta: DecisaoDoCandidatoSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id do candidato.' },
      {
        nome: 'Idempotency-Key',
        em: 'header',
        descricao: 'Chave do comando (B30). Repetir a mesma devolve a primeira resposta.',
        obrigatorio: true,
      },
    ],
  },
  rejeitarCandidatoDeMerge: {
    metodo: 'POST',
    caminho: '/v1/identity/merge-candidates/{id}/reject',
    resumo: 'Rejeita a junção, com motivo',
    descricao:
      'O motivo é obrigatório, ao contrário do da aprovação: é ele que permite calibrar o ' +
      'limiar. "São irmãos com o mesmo sobrenome" e "é a mesma pessoa, mas não agora" ' +
      'pedem ajustes opostos, e a contagem de rejeições sem texto não diz para que lado ' +
      'mexer. O banco repete a exigência por CHECK (migração 027).',
    corpo: RejeitarMergeSchema,
    resposta: DecisaoDoCandidatoSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id do candidato.' },
      {
        nome: 'Idempotency-Key',
        em: 'header',
        descricao: 'Chave do comando (B30).',
        obrigatorio: true,
      },
    ],
  },
  medirIdentidade: {
    metodo: 'GET',
    caminho: '/v1/identity/metrics',
    resumo: 'Taxa de vínculo automático',
    descricao:
      'A meta da §14.2 (≥ 95%). Taxa = vínculos que a escada resolveu sozinha ÷ (esses ' +
      'mais os candidatos que ela mandou para revisão) — cada candidato é uma resolução ' +
      'que NÃO foi automática, e dividir por "total de vínculos" ignoraria a fila inteira. ' +
      'Vem com o tamanho atual da fila e as rejeições: fila crescendo é limiar alto demais, ' +
      'rejeição subindo é limiar baixo demais.',
    resposta: MetricaDeIdentidadeSchema,
  },
  lerConfiguracao: {
    metodo: 'GET',
    caminho: '/v1/admin/settings',
    resumo: 'Configuração da organização',
    descricao:
      'Fuso, horário de atendimento, regra de abertura de negócio, regra de encerramento, ' +
      'limiar de identidade e política de documento. NÃO inclui a exigência de MFA, que tem ' +
      'rota própria (B15) com justificativa — dois caminhos para desligar o segundo fator da ' +
      'organização seria um a mais.',
    resposta: ConfiguracaoDoTenantSchema,
  },
  alterarConfiguracao: {
    metodo: 'PATCH',
    caminho: '/v1/admin/settings',
    resumo: 'Altera a configuração, com justificativa',
    descricao:
      'Só o que vier no corpo é alterado; o resto fica como está. Justificativa é ' +
      'obrigatória — configuração alterada é a primeira suspeita num incidente, e tudo vai ' +
      'para audit_logs com antes e depois. A resposta traz `avisos` com o que a mudança NÃO ' +
      'faz: baixar o limiar de identidade, por exemplo, não junta duplicatas antigas.',
    corpo: AlterarConfiguracaoSchema,
    resposta: ConfiguracaoSalvaSchema,
  },
  simularRegraDeOportunidade: {
    metodo: 'POST',
    caminho: '/v1/admin/settings/simulate',
    resumo: 'Simula a regra de abertura de negócio antes de salvar',
    descricao:
      'A regra mal configurada abre um negócio por mensagem ou nenhum. Esta rota roda a ' +
      'regra proposta contra os últimos N dias e devolve quantos negócios ela teria aberto. ' +
      'O número é ESTIMADO e a resposta diz por quê: saber quem já tinha negócio aberto ' +
      'naquele momento exigiria replay da timeline, então o cálculo usa a situação de hoje ' +
      'e erra para menos. Não grava nada.',
    corpo: SimularRegraSchema,
    resposta: SimulacaoDaRegraSchema,
  },
  listarFlags: {
    metodo: 'GET',
    caminho: '/v1/admin/flags',
    resumo: 'Funcionalidades opcionais da organização',
    descricao:
      'O catálogo inteiro, com o que está ligado nesta organização. Lista tudo, e não só o que ' +
      'está ativo: é aqui que alguém descobre o que existe para ligar. Sem vínculo = ' +
      'desligada, que é como toda organização nasce (princípio 08).',
    resposta: FlagsDoTenantSchema,
  },
  alterarFlag: {
    metodo: 'PATCH',
    caminho: '/v1/admin/flags/{chave}',
    resumo: 'Liga ou desliga uma funcionalidade',
    descricao:
      'Ligar É homologar: a linha só existe com carimbo de homologação (princípio 08), e a ' +
      'justificativa fica registrada como o motivo. Desligar apaga a linha — é o kill switch ' +
      'da B19, com efeito no próximo ciclo de cache e sem deploy.',
    corpo: AlterarFlagSchema,
    resposta: FlagsDoTenantSchema,
    parametros: [{ nome: 'chave', em: 'path', descricao: 'Chave da funcionalidade.' }],
  },
  lerDashboard: {
    metodo: 'GET',
    caminho: '/v1/dashboard',
    resumo: 'Indicadores da organização no período',
    descricao:
      'Volume de atendimento, funil, canal e saúde das integrações (§11). Servido de agregado ' +
      'materializado por job (ADR-0010) — calcular ao vivo faria dez gestores com o dashboard ' +
      'aberto competirem com o fluxo do vendedor. Exige `indicador:ler`, que na matriz é do ' +
      '`gestor` e do `admin_tenant`. Cada bloco devolve `abrangencia` dizendo de quem são os ' +
      'números: o funil se recorta pela carteira, o volume de atendimento não — a conversa ' +
      'guarda id de agente do Chatwoot e nada o liga a um vínculo do CliqCRM. Não há taxa de ' +
      'conversão nem indicador de SLA: a engine não marca ganho e perda (C06) e a regra de SLA ' +
      'é a D08. A saúde das integrações é leitura VIVA, não agregada.',
    resposta: DashboardSchema,
    parametros: [
      {
        nome: 'de',
        em: 'query',
        descricao: 'Primeiro dia (YYYY-MM-DD) no fuso da organização. Padrão: 29 dias atrás.',
      },
      {
        nome: 'ate',
        em: 'query',
        descricao: 'Último dia (YYYY-MM-DD). Padrão: hoje. O período vai até 366 dias.',
      },
      {
        nome: 'dias',
        em: 'query',
        descricao:
          'Os últimos N dias, contando hoje NO FUSO DA ORGANIZAÇÃO (1 a 366). Alternativa a ' +
          '`de`/`ate` para quem não sabe o fuso: a tela calculava as datas pelo relógio UTC do ' +
          'navegador, e depois das 21h de Brasília "7 dias" viravam 6. Não combina com `de`.',
      },
    ],
  },
  lerRankingDeVendas: {
    metodo: 'GET',
    caminho: '/v1/relatorios/ranking-de-vendas',
    resumo: 'Ranking de vendas do período, por valor vendido',
    descricao:
      'Quem vendeu mais no período (#557). Venda é a entrada numa etapa final marcada como ' +
      'ganho, a mesma base do bloco Resultado do painel. Ordena pelo valor vendido; a ' +
      'quantidade vai ao lado. Traz a posição no período anterior de mesmo tamanho, quem não ' +
      'vendeu e quantas vendas estão sem valor. Exige `indicador:ler` (gestor e administrador) ' +
      'e se recorta pela carteira de quem pergunta.',
    resposta: RankingDeVendasSchema,
    parametros: [
      { nome: 'de', em: 'query', descricao: 'Primeiro dia (YYYY-MM-DD) no fuso da organização.' },
      { nome: 'ate', em: 'query', descricao: 'Último dia (YYYY-MM-DD). Padrão: hoje.' },
      {
        nome: 'dias',
        em: 'query',
        descricao: 'Os últimos N dias no fuso da organização (1 a 366). Não combina com `de`.',
      },
      { nome: 'funil', em: 'query', descricao: 'Só as vendas deste funil (chave).' },
      { nome: 'time', em: 'query', descricao: 'Só as pessoas desta equipe (id do time).' },
    ],
  },
  lerComparativos: {
    metodo: 'GET',
    caminho: '/v1/relatorios/comparativos',
    resumo: 'Os principais números do período contra um período de comparação',
    descricao:
      'Atendimento (da organização inteira, como no painel) e comercial (recortado pela ' +
      'carteira): atendimentos, resolvidas, sem resposta, espera, oportunidades criadas, vendas, ' +
      'valor vendido e perdidas, com a variação. A venda é a mesma do ranking e do painel. ' +
      'Exige indicador:ler.',
    resposta: ComparativosSchema,
    parametros: [
      { nome: 'de', em: 'query', descricao: 'Primeiro dia (YYYY-MM-DD) no fuso da organização.' },
      { nome: 'ate', em: 'query', descricao: 'Último dia (YYYY-MM-DD). Padrão: hoje.' },
      { nome: 'dias', em: 'query', descricao: 'Os últimos N dias (1 a 366). Não combina com de.' },
      { nome: 'base', em: 'query', descricao: 'anterior (padrão), mes_anterior ou ano_anterior.' },
    ],
  },
  lerGargalosDoFunil: {
    metodo: 'GET',
    caminho: '/v1/relatorios/gargalos',
    resumo: 'Onde as oportunidades param no funil',
    descricao:
      'Por etapa: quantas estão nela agora, quantas paradas há N dias ou mais, quantas entraram ' +
      'no período, quantas avançaram e o tempo médio até sair. Por responsável: quem tem ' +
      'oportunidade parada. Recortado pela carteira. Exige indicador:ler.',
    resposta: GargalosDoFunilSchema,
    parametros: [
      { nome: 'de', em: 'query', descricao: 'Primeiro dia (YYYY-MM-DD) no fuso da organização.' },
      { nome: 'ate', em: 'query', descricao: 'Último dia (YYYY-MM-DD). Padrão: hoje.' },
      { nome: 'dias', em: 'query', descricao: 'Os últimos N dias (1 a 366). Não combina com de.' },
      { nome: 'funil', em: 'query', descricao: 'A chave do funil. Padrão: o primeiro.' },
      {
        nome: 'parada_dias',
        em: 'query',
        descricao:
          'A partir de quantos dias na mesma etapa conta como parada (1 a 365). Padrão: 7.',
      },
    ],
  },
  lerOportunidadesParadas: {
    metodo: 'GET',
    caminho: '/v1/relatorios/gargalos/paradas',
    resumo: 'As oportunidades paradas que formam um número dos gargalos',
    descricao:
      'O detalhamento dos gargalos: as oportunidades paradas de uma etapa, de um responsável ou ' +
      'das duas coisas, com o mesmo corte. Registra quem viu o negócio de cada cliente.',
    resposta: OportunidadesParadasSchema,
    parametros: [
      { nome: 'funil', em: 'query', descricao: 'A chave do funil.' },
      { nome: 'parada_dias', em: 'query', descricao: 'O mesmo corte do relatório.' },
      { nome: 'etapa', em: 'query', descricao: 'Só desta etapa.' },
      { nome: 'responsavel', em: 'query', descricao: 'Só deste responsável.' },
    ],
  },
  lerAtendimentoDoPeriodo: {
    metodo: 'GET',
    caminho: '/v1/relatorios/atendimento',
    resumo: 'Volume de atendimento por canal, por hora e por pessoa',
    descricao:
      'Resumo do período, atendimentos por canal, por hora do dia e dia da semana (no fuso da ' +
      'organização), e conversas e mensagens por pessoa. O volume é da organização inteira; o ' +
      'bloco por pessoa se recorta pela carteira. Exige indicador:ler.',
    resposta: AtendimentoDoPeriodoSchema,
    parametros: [
      { nome: 'de', em: 'query', descricao: 'Primeiro dia (YYYY-MM-DD) no fuso da organização.' },
      { nome: 'ate', em: 'query', descricao: 'Último dia (YYYY-MM-DD). Padrão: hoje.' },
      { nome: 'dias', em: 'query', descricao: 'Os últimos N dias (1 a 366). Não combina com de.' },
    ],
  },
  lerVendasDoRanking: {
    metodo: 'GET',
    caminho: '/v1/relatorios/ranking-de-vendas/vendas',
    resumo: 'As vendas que formam o número de uma pessoa no ranking',
    descricao:
      'O detalhamento do ranking: as mesmas vendas, com os mesmos filtros, de um responsável. ' +
      'Responsável fora da carteira de quem pergunta devolve a lista vazia.',
    resposta: VendasDoRankingSchema,
    parametros: [
      {
        nome: 'responsavel',
        em: 'query',
        descricao: 'O `responsavel_referencia_externa` da linha do ranking.',
      },
      { nome: 'de', em: 'query', descricao: 'Primeiro dia (YYYY-MM-DD).' },
      { nome: 'ate', em: 'query', descricao: 'Último dia (YYYY-MM-DD).' },
      { nome: 'dias', em: 'query', descricao: 'Os últimos N dias (1 a 366).' },
      { nome: 'funil', em: 'query', descricao: 'Só as vendas deste funil (chave).' },
    ],
  },
  definirFotoDePerfil: {
    metodo: 'PUT',
    caminho: '/v1/me/foto',
    resumo: 'Trocar a própria foto de perfil',
    descricao:
      'Formulário com a imagem no campo `foto`: JPEG, PNG ou WebP, até 512 KB — a ' +
      'tela reduz para 256×256 antes de mandar. O tipo é conferido pelos primeiros bytes, e não ' +
      'pelo que o navegador declara. Troca só a foto de quem está na sessão: não existe rota ' +
      'para trocar a de outra pessoa. Devolve o link novo.',
    corpo: EnviarFotoDePerfilSchema,
    resposta: FotoDePerfilSchema,
  },
  removerFotoDePerfil: {
    metodo: 'DELETE',
    caminho: '/v1/me/foto',
    resumo: 'Tirar a própria foto de perfil',
    descricao: 'A tela volta a mostrar as iniciais. Tirar sem ter foto não é erro.',
    resposta: FotoDePerfilSchema,
  },
  fotoDePerfil: {
    metodo: 'GET',
    caminho: '/v1/fotos/{token}',
    resumo: 'Serve uma foto de perfil',
    descricao:
      'Sem sessão, como o anexo: quem abre é o `<img>` do navegador. Autoriza a ASSINATURA, ' +
      'válida por cinco minutos e emitida por quem lista a pessoa (`/v1/me`, a lista de ' +
      'pessoas), e exige que o dono da foto tenha vínculo com a organização do link. ' +
      'Responde 302 para o endereço assinado do armazenamento, que vence junto com este ' +
      'link; a foto de antes do armazenamento vem no corpo. ' +
      'Vencido, adulterado, de outra organização ou sem foto: 404, sem distinguir os casos.',
    resposta: z.string(),
    conteudoDaResposta: 'application/octet-stream',
    autenticada: false,
    parametros: [{ nome: 'token', em: 'path', descricao: 'O link assinado.' }],
  },
  enviarFeedback: {
    metodo: 'POST',
    caminho: '/v1/feedbacks',
    resumo: 'Enviar um feedback ao suporte da Cliqfy',
    descricao:
      'Formulário com o texto e até cinco imagens no campo `imagens` (JPEG, PNG ou WebP, até ' +
      '2 MB cada; o tipo é conferido pelos primeiros bytes). Qualquer pessoa logada envia. A ' +
      'organização, a pessoa e a hora saem da sessão, nunca do pedido; a tela vem do caminho ' +
      'que a página informa. O feedback é gravado na organização de SUPORTE, a única que lê: ' +
      'quem enviou não o vê depois. Sem a organização de suporte configurada, 503.',
    corpo: EnviarFeedbackSchema,
    resposta: FeedbackEnviadoSchema,
  },
  listarFeedbacks: {
    metodo: 'GET',
    caminho: '/v1/feedbacks',
    resumo: 'Os feedbacks recebidos, para o suporte da Cliqfy',
    descricao:
      'Só para quem está na organização de suporte; as outras recebem 403. Do mais novo para ' +
      'o mais antigo, até 200, com a organização, a pessoa, a tela, o texto e os links ' +
      'assinados das imagens (cinco minutos).',
    resposta: FeedbacksDoSuporteSchema,
  },
  imagemDoFeedback: {
    metodo: 'GET',
    caminho: '/v1/feedbacks/imagens/{token}',
    resumo: 'Serve uma imagem de feedback',
    descricao:
      'Sem sessão, como a foto: quem abre é o `<img>` do navegador. Autoriza a ASSINATURA, ' +
      'emitida pela lista do suporte e válida por cinco minutos. Responde 302 para o ' +
      'endereço assinado do armazenamento, que vence junto com este link; a imagem de ' +
      'antes do armazenamento vem no corpo. Vencido, adulterado ou inexistente: 404, sem ' +
      'distinguir os casos.',
    resposta: z.string(),
    conteudoDaResposta: 'application/octet-stream',
    autenticada: false,
    parametros: [{ nome: 'token', em: 'path', descricao: 'O link assinado.' }],
  },
  lerPoliticaDeMfa: {
    metodo: 'GET',
    caminho: '/v1/security/mfa',
    resumo: 'Política de MFA do tenant',
    descricao:
      'A exigência REGISTRADA e a que o provedor aplica agora. Os dois campos existem ' +
      'porque divergem de verdade: reprovisionar o realm zera a exigência, e sem os dois a ' +
      'tela diria "MFA exigido" enquanto ninguém é desafiado.',
    resposta: EstadoDoMfaSchema,
  },
  definirPoliticaDeMfa: {
    metodo: 'PATCH',
    caminho: '/v1/security/mfa',
    resumo: 'Ligar ou desligar a exigência de MFA',
    descricao:
      'Só o `admin_tenant` (decisão A2: MFA não é obrigatório na v1, a organização decide). ' +
      'Ligar obriga TODO usuário do tenant a cadastrar TOTP no próximo login, então exige ' +
      'justificativa e fica em `audit_logs`. Escreve no provedor primeiro e no banco depois: ' +
      'a inconsistência inversa pareceria segura sem ser.',
    corpo: DefinirExigenciaDeMfaSchema,
    resposta: EstadoDoMfaSchema,
  },
  listarDlq: {
    metodo: 'GET',
    caminho: '/v1/admin/dlq',
    resumo: 'Fila de descarte, do item mais velho para o mais novo',
    descricao:
      'Filtra por razão, fonte, tipo, dono e período. A ordem é por IDADE, ao contrário de ' +
      'toda outra listagem: recência mostra a operação e esconde o abandono. O `resumo` ' +
      'traz idade do mais velho e contagem de antigos ao lado da profundidade — medir só ' +
      'quantos são é a armadilha que faz uma fila esquecida parecer saudável.',
    resposta: PaginaDaDlqSchema,
    parametros: [
      { nome: 'razao', em: 'query', descricao: 'Razão da falha, do catálogo da migração 006.' },
      { nome: 'fonte', em: 'query', descricao: '`source` do evento — `chatwoot`, `olx_lead`.' },
      { nome: 'tipo', em: 'query', descricao: '`event_type` do evento.' },
      { nome: 'dono', em: 'query', descricao: 'Time responsável por resolver.' },
      { nome: 'desde', em: 'query', descricao: 'Início da janela sobre a entrada na fila (ISO).' },
      { nome: 'ate', em: 'query', descricao: 'Fim da janela, exclusivo (ISO).' },
      {
        nome: 'incluir_resolvidos',
        em: 'query',
        descricao: '`true` traz também o que já foi tratado. Padrão: só os abertos.',
      },
      { nome: 'limite', em: 'query', descricao: 'Itens por página. Padrão 50, máximo 200.' },
    ],
  },
  detalharItemDaDlq: {
    metodo: 'GET',
    caminho: '/v1/admin/dlq/{id}',
    resumo: 'Payload e histórico de tentativas de um item',
    descricao:
      'O payload é REFERENCIADO do `event_log`, não copiado — e some com a purga de 90 ' +
      'dias (ADR-0009), quando `payload_disponivel` fica falso. As tentativas contam por ' +
      'que o evento demorou e por onde ele falhou.',
    resposta: DetalheDaDlqSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador do item de fila.' }],
  },
  reprocessarDaDlq: {
    metodo: 'POST',
    caminho: '/v1/admin/dlq/reprocess',
    resumo: 'Reprocessar em lote a partir do filtro',
    descricao:
      'SIMULA por padrão. Só `simular: false` executa — lote sem simulação é como se ' +
      'descobre, tarde, que o filtro pegou dez mil eventos. O reprocessamento cria evento ' +
      'NOVO sem alterar o payload (B31); o item da fila é vinculado a ele e NÃO é dado por ' +
      'resolvido, porque o replay pode morrer pelo mesmo motivo.',
    corpo: ReprocessarDaDlqSchema,
    resposta: ResultadoDoReprocessamentoSchema,
  },
  resolverItemDaDlq: {
    metodo: 'POST',
    caminho: '/v1/admin/dlq/{id}/resolve',
    resumo: 'Dar por resolvido SEM reprocessar',
    descricao:
      'Caso real: evento de conversa que foi apagada não precisa reprocessar. Exige motivo ' +
      'escrito, que vai para a coluna e para `audit_logs` — é o que separa tratar a fila de ' +
      'esvaziá-la.',
    corpo: ResolverItemDaDlqSchema,
    resposta: ItemResolvidoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador do item de fila.' }],
  },
  enderecoDoCep: {
    metodo: 'POST',
    caminho: '/v1/cep/consulta',
    resumo: 'O endereço de um CEP, para completar o endereço do agendamento',
    descricao:
      'Pergunta à BrasilAPI e, se ela não responder (rede, tempo de 3 s ou 5xx), à ViaCEP ' +
      '(decisão de 03/11/2026). Quem pergunta é o servidor: o navegador não fala com o ' +
      'terceiro, e ele recebe só o CEP. A resposta fica 30 dias em cache por organização, e o ' +
      'CEP que não existe, 1 dia. Lê quem está autenticado numa organização, com qualquer ' +
      'papel. CEP com outra coisa além dos 8 dígitos (traço, ponto, espaço): 400. CEP que ' +
      'nenhum provedor conhece: 404 `NAO_ENCONTRADO` "CEP não encontrado.". Os dois provedores ' +
      'fora: 503 `ENGINE_INDISPONIVEL`, nunca um endereço vazio. O CEP vai no corpo, e não no ' +
      'caminho, que os logs de erro e de acesso registram: o CEP não vai para o log.',
    corpo: ConsultaDoCepSchema,
    resposta: EnderecoDoCepSchema,
  },
  chatDeSuporte: {
    metodo: 'GET',
    caminho: '/v1/suporte/chat',
    resumo: 'O chat de suporte da Cliqfy, dentro do CliqCRM',
    descricao:
      'Diz se o balão de suporte aparece para quem chamou e, se sim, entrega o token público do ' +
      'widget e a identidade já assinada (HMAC do identificador com o segredo do canal). O ' +
      'segredo não sai do servidor. Não aparece para quem é da própria organização de suporte.',
    resposta: ChatDeSuporteSchema,
  },
  // ── Integrações de dados (#922): só os devs da Cliqfy ─────────────────────
  conexoesDeDados: {
    metodo: 'GET',
    caminho: '/v1/integracoes-de-dados/conexoes',
    resumo: 'As conexões aos bancos do cliente',
    descricao:
      'Nenhuma resposta traz a senha. Só os devs da Cliqfy (`integracao_de_dados`). Sem a funcionalidade `automacoes` na organização, 404.',
    resposta: ConexoesSchema,
  },
  criarConexaoDeDados: {
    metodo: 'POST',
    caminho: '/v1/integracoes-de-dados/conexoes',
    resumo: 'Criar uma conexão ao banco do cliente',
    descricao:
      'A senha é guardada selada no serviço de automações e não volta. A conexão nasce ' +
      '`nao_testada`. Exige Idempotency-Key. Só os devs da Cliqfy (`integracao_de_dados`). Sem a funcionalidade `automacoes` na organização, 404.',
    corpo: CriarConexaoSchema,
    resposta: ConexaoSchema,
    status: 201,
  },
  conexaoDeDados: {
    metodo: 'GET',
    caminho: '/v1/integracoes-de-dados/conexoes/{id}',
    resumo: 'Uma conexão',
    resposta: ConexaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da conexão.' }],
  },
  editarConexaoDeDados: {
    metodo: 'PATCH',
    caminho: '/v1/integracoes-de-dados/conexoes/{id}',
    resumo: 'Editar uma conexão',
    descricao:
      'Troca tudo menos a família. Sem `senha`, a atual fica. A conexão volta a `nao_testada`.',
    corpo: EditarConexaoSchema,
    resposta: ConexaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da conexão.' }],
  },
  removerConexaoDeDados: {
    metodo: 'DELETE',
    caminho: '/v1/integracoes-de-dados/conexoes/{id}',
    resumo: 'Remover uma conexão',
    descricao: 'Recusada com 409 enquanto houver operação publicada usando a conexão.',
    resposta: z.object({ removida: z.boolean() }).strict(),
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da conexão.' }],
  },
  testarConexaoDeDados: {
    metodo: 'POST',
    caminho: '/v1/integracoes-de-dados/conexoes/{id}/teste',
    resumo: 'Testar a conexão, etapa por etapa',
    descricao: 'A falha de uma etapa é resposta (200 com `ok: false`), não erro.',
    resposta: TesteDeConexaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da conexão.' }],
  },
  mapaDoBanco: {
    metodo: 'GET',
    caminho: '/v1/integracoes-de-dados/conexoes/{id}/mapa',
    resumo: 'O mapa do banco: tabelas e colunas',
    descricao: 'A última leitura guardada; sem nenhuma ainda, `versao: 0` e nenhuma tabela.',
    resposta: MapaDoBancoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da conexão.' }],
  },
  atualizarMapaDoBanco: {
    metodo: 'POST',
    caminho: '/v1/integracoes-de-dados/conexoes/{id}/mapa/atualizar',
    resumo: 'Reler o mapa do banco do cliente',
    descricao:
      'Lê ao vivo e guarda uma versão nova se mudou (`mudou`). Banco fora: 503 `BANCO_DO_CLIENTE_INDISPONIVEL`.',
    resposta: MapaDoBancoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da conexão.' }],
  },
  operacoesDeDados: {
    metodo: 'GET',
    caminho: '/v1/integracoes-de-dados/operacoes',
    resumo: 'As operações SQL',
    resposta: OperacoesSchema,
    parametros: [
      { nome: 'finalidade', em: 'query', descricao: '`consulta`, `listagem` ou `acao`.' },
    ],
  },
  criarOperacaoDeDados: {
    metodo: 'POST',
    caminho: '/v1/integracoes-de-dados/operacoes',
    resumo: 'Criar uma operação SQL',
    descricao:
      'O tipo (leitura ou escrita) é detectado do SQL: leitura nasce publicada; escrita nasce ' +
      'rascunho e espera a aprovação de outro dev. SQL recusado: 400 com o campo `sql`. ' +
      'Exige Idempotency-Key. Só os devs da Cliqfy (`integracao_de_dados`). Sem a funcionalidade `automacoes` na organização, 404.',
    corpo: SalvarOperacaoSchema,
    resposta: OperacaoSchema,
    status: 201,
  },
  operacaoDeDados: {
    metodo: 'GET',
    caminho: '/v1/integracoes-de-dados/operacoes/{id}',
    resumo: 'Uma operação SQL',
    resposta: OperacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da operação.' }],
  },
  editarOperacaoDeDados: {
    metodo: 'PATCH',
    caminho: '/v1/integracoes-de-dados/operacoes/{id}',
    resumo: 'Editar uma operação (cria versão nova)',
    descricao:
      'Substitui a configuração inteira. A escrita volta a rascunho e precisa de nova aprovação.',
    corpo: SalvarOperacaoSchema,
    resposta: OperacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da operação.' }],
  },
  testarOperacaoDeDados: {
    metodo: 'POST',
    caminho: '/v1/integracoes-de-dados/operacoes/{id}/teste',
    resumo: 'Testar a operação',
    descricao: 'Não exige publicação. A escrita roda numa transação desfeita. Parâmetros por nome.',
    corpo: ExecutarOperacaoSchema,
    resposta: ResultadoDaExecucaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da operação.' }],
  },
  executarOperacaoDeDados: {
    metodo: 'POST',
    caminho: '/v1/integracoes-de-dados/operacoes/{id}/execucao',
    resumo: 'Executar a operação ao vivo',
    descricao:
      'A escrita só roda publicada (aprovada). Toda execução fica na trilha (tipo e total). ' +
      'As linhas lidas voltam na resposta e não são guardadas: o reenvio com a mesma ' +
      'Idempotency-Key devolve o resumo, com `linhas` vazia.',
    corpo: ExecutarOperacaoSchema,
    resposta: ResultadoDaExecucaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da operação.' }],
  },
  aprovarOperacaoDeDados: {
    metodo: 'POST',
    caminho: '/v1/integracoes-de-dados/operacoes/{id}/aprovar',
    resumo: 'Aprovar uma operação de escrita',
    descricao:
      'Põe no ar a escrita que ficou pendente. Sem a regra das duas pessoas (ADR-0035): quem edita operações aprova, até a própria; a escrita nova já entra no ar ao salvar.',
    resposta: OperacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da operação.' }],
  },
  recusarOperacaoDeDados: {
    metodo: 'POST',
    caminho: '/v1/integracoes-de-dados/operacoes/{id}/recusar',
    resumo: 'Recusar uma operação de escrita',
    resposta: OperacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da operação.' }],
  },
  historicoDaOperacaoDeDados: {
    metodo: 'GET',
    caminho: '/v1/integracoes-de-dados/operacoes/{id}/historico',
    resumo: 'O histórico de versões e decisões da operação',
    resposta: HistoricoDaOperacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da operação.' }],
  },
  // ── Automações (fase 2, #932): pelo serviço de automações ──────────────────
  blocosDoEditor: {
    metodo: 'GET',
    caminho: '/v1/automacoes/blocos',
    resumo: 'O catálogo de blocos do editor',
    descricao:
      'Os eventos que disparam (com o contexto de cada um), os tipos de bloco com as saídas e os campos, e as ações do CRM com os parâmetros. Montado pelo CRM. Os blocos de SQL só aparecem para quem tem `integracao_de_dados`. Quem lê automações (`automacao` × `ler`). Sem a funcionalidade `automacoes` na organização, 404. Serviço fora: 503 `ENGINE_INDISPONIVEL`.',
    resposta: BlocosDoEditorSchema,
  },
  modelosDeAutomacao: {
    metodo: 'GET',
    caminho: '/v1/automacoes/modelos',
    resumo: 'Os modelos prontos de automação',
    descricao:
      'Os modelos que o serviço oferece, com nome e descrição. `automacao` × `ler`. Sem a funcionalidade `automacoes` na organização, 404. Serviço fora: 503 `ENGINE_INDISPONIVEL`.',
    resposta: ModelosDeAutomacaoSchema,
  },
  modeloDeAutomacao: {
    metodo: 'GET',
    caminho: '/v1/automacoes/modelos/{chave}',
    resumo: 'Um modelo, com o esquema da configuração',
    descricao:
      'O `esquema` (JSON Schema) da configuração que o modelo pede, no vocabulário do CRM, para a tela montar o formulário. Chave desconhecida: 404. `automacao` × `ler`.',
    resposta: ModeloDeAutomacaoSchema,
    parametros: [{ nome: 'chave', em: 'path', descricao: 'A chave do modelo.' }],
  },
  execucoesDasAutomacoes: {
    metodo: 'GET',
    caminho: '/v1/automacoes/execucoes',
    resumo: 'As execuções das automações',
    descricao:
      'Das mais novas para as mais antigas, com os passos (bloco, resultado, motivo), sem dado do cliente. Pagina por `proximo_cursor`. `automacao` × `ler`. Sem a funcionalidade `automacoes` na organização, 404. Serviço fora: 503 `ENGINE_INDISPONIVEL`.',
    resposta: ExecucoesDaAutomacaoSchema,
    parametros: [
      { nome: 'automacao', em: 'query', descricao: 'Só desta automação.' },
      { nome: 'resultado', em: 'query', descricao: '`concluida`, `ignorada` ou `falhou`.' },
      { nome: 'de', em: 'query', descricao: 'Início da janela (ISO).' },
      { nome: 'ate', em: 'query', descricao: 'Fim da janela (ISO).' },
      { nome: 'cursor', em: 'query', descricao: 'O `proximo_cursor` da página anterior.' },
      { nome: 'limite', em: 'query', descricao: 'Até 200; padrão 50.' },
    ],
  },
  execucaoDaAutomacao: {
    metodo: 'GET',
    caminho: '/v1/automacoes/execucoes/{id}',
    resumo: 'Uma execução',
    descricao:
      'A execução com todos os passos. 404 se não existe nesta organização. `automacao` × `ler`.',
    resposta: ExecucaoDaAutomacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da execução.' }],
  },
  automacoesEmEspera: {
    metodo: 'GET',
    caminho: '/v1/automacoes/em-espera',
    resumo: 'As instâncias paradas numa espera',
    descricao:
      'De qual automação, em qual bloco e desde quando cada instância espera (tempo ou evento). `automacao` × `ler`. Sem a funcionalidade `automacoes` na organização, 404. Serviço fora: 503 `ENGINE_INDISPONIVEL`.',
    resposta: InstanciasEmEsperaSchema,
    parametros: [
      { nome: 'cursor', em: 'query', descricao: 'O `proximo_cursor` da página anterior.' },
    ],
  },
  cancelarEsperaDaAutomacao: {
    metodo: 'POST',
    caminho: '/v1/automacoes/em-espera/{id}/cancelar',
    resumo: 'Cancelar uma instância em espera',
    descricao:
      'Repetir não dá erro: responde `nao_estava_esperando`. 404 se não existe. `automacao` × `editar`.',
    resposta: CancelamentoDeEsperaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da instância.' }],
  },
  painelDasAutomacoes: {
    metodo: 'GET',
    caminho: '/v1/automacoes/painel',
    resumo: 'O painel das automações',
    descricao:
      'Execuções por resultado, taxa de falha, duração, por gatilho e as falhas recentes, na janela pedida. Em 7d e 30d o serviço lê só as últimas 500 execuções. `automacao` × `ler`. Sem a funcionalidade `automacoes` na organização, 404. Serviço fora: 503 `ENGINE_INDISPONIVEL`.',
    resposta: PainelDasAutomacoesSchema,
    parametros: [
      { nome: 'janela', em: 'query', descricao: '`1h`, `6h`, `24h`, `7d` ou `30d`; padrão `24h`.' },
    ],
  },
  validarAutomacao: {
    metodo: 'POST',
    caminho: '/v1/automacoes/validar',
    resumo: 'Validar uma automação sem salvar',
    descricao:
      'Confere gatilho, condição e fluxo e devolve 200 `{ ok: true }` ou 400 com os problemas por bloco (`errors[].campo` = `grafo.nos.<id>.<campo>`). Não grava nada. `automacao` × `criar`. Cada lado monta o seu tipo: a automação com SQL (`consulta_sql`, `para_cada`) só a Cliqfy; a interna, só a empresa (dono e gestor). O tipo errado para o papel é 403.',
    corpo: ConfiguracaoDaAutomacaoSchema,
    resposta: ValidacaoDaAutomacaoSchema,
  },
  automacoes: {
    metodo: 'GET',
    caminho: '/v1/automacoes',
    resumo: 'As automações da organização',
    descricao:
      'Com o nome, a situação, a versão, o gatilho e se usa SQL. As arquivadas só aparecem com `situacao=arquivada`. A alteração pendente aparece no detalhe. `automacao` × `ler`. Sem a funcionalidade `automacoes` na organização, 404. Serviço fora: 503 `ENGINE_INDISPONIVEL`.',
    resposta: AutomacoesSchema,
    parametros: [
      {
        nome: 'situacao',
        em: 'query',
        descricao:
          '`rascunho`, `em_homologacao`, `ativa`, `pausada` ou `arquivada` (sem ele, as arquivadas não vêm).',
      },
      { nome: 'gatilho', em: 'query', descricao: '`evento` ou `agenda`.' },
    ],
  },
  criarAutomacao: {
    metodo: 'POST',
    caminho: '/v1/automacoes',
    resumo: 'Criar uma automação no editor',
    descricao:
      'Nasce em `rascunho`, versão 1. O fluxo é conferido antes de ir ao serviço: problema vira 400 por bloco. Exige Idempotency-Key. `automacao` × `criar`. Cada lado monta o seu tipo: a automação com SQL (`consulta_sql`, `para_cada`) só a Cliqfy; a interna, só a empresa (dono e gestor). O tipo errado para o papel é 403. Sem a funcionalidade `automacoes` na organização, 404. Serviço fora: 503 `ENGINE_INDISPONIVEL`.',
    corpo: CriarAutomacaoSchema,
    resposta: AutomacaoSchema,
    status: 201,
  },
  criarAutomacaoDoModelo: {
    metodo: 'POST',
    caminho: '/v1/automacoes/a-partir-de-modelo',
    resumo: 'Criar a partir de um modelo',
    descricao:
      'O serviço monta as automações do modelo, todas em `rascunho`. A configuração segue o esquema do modelo (`GET /v1/automacoes/modelos/{chave}`), no vocabulário do CRM; as ações são as do catálogo. A `principal` é a que a tela mostra, nomeia, liga, pausa e arquiva, e o estado dela vale para as `internas`, que não aparecem na lista. Modelo com SQL (`usa_sql`) só a Cliqfy; os outros, só a empresa (dono e gestor). Exige Idempotency-Key. `automacao` × `criar`. Sem a funcionalidade `automacoes` na organização, 404. Serviço fora: 503 `ENGINE_INDISPONIVEL`.',
    corpo: CriarAPartirDeModeloSchema,
    resposta: CriadasDoModeloSchema,
    status: 201,
  },
  identificarAutomacao: {
    metodo: 'PATCH',
    caminho: '/v1/automacoes/{id}/nome-e-descricao',
    resumo: 'Mudar o nome e a descrição',
    descricao:
      'Não cria versão nem mexe no que está no ar. Só quem monta aquele tipo de automação. `automacao` × `editar`.',
    corpo: IdentificarAutomacaoSchema,
    resposta: AutomacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da automação.' }],
  },
  descartarPendenteDaAutomacao: {
    metodo: 'POST',
    caminho: '/v1/automacoes/{id}/descartar-pendente',
    resumo: 'Descartar a alteração pendente',
    descricao:
      'Joga fora a versão em homologação de uma automação ativa; a que está no ar continua. Sem pendente, nada muda. `automacao` × `editar`.',
    resposta: AutomacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da automação.' }],
  },
  arquivarAutomacao: {
    metodo: 'POST',
    caminho: '/v1/automacoes/{id}/arquivar',
    resumo: 'Arquivar uma automação',
    descricao:
      '`rascunho` ou `pausada` → `arquivada` (a ativa precisa ser pausada antes: 409). Não dispara e some da lista; o histórico fica. `automacao` × `remover`: o dono, em qualquer automação; a Cliqfy, só nas com SQL.',
    resposta: AutomacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da automação.' }],
  },
  desarquivarAutomacao: {
    metodo: 'POST',
    caminho: '/v1/automacoes/{id}/desarquivar',
    resumo: 'Desarquivar uma automação',
    descricao:
      '`arquivada` → `rascunho`. `automacao` × `remover`: o dono, em qualquer automação; a Cliqfy, só nas com SQL.',
    resposta: AutomacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da automação.' }],
  },
  removerAutomacao: {
    metodo: 'DELETE',
    caminho: '/v1/automacoes/{id}',
    resumo: 'Excluir uma automação de vez',
    descricao:
      'Apaga a automação de vez no serviço: as versões, as execuções e as rodadas (ADR-0029). ' +
      'A ativa é recusada com 409 `CONFLITO_DE_ESTADO`: pause ou arquive antes. Não tem volta; ' +
      'o CRM guarda na trilha quem excluiu e o nome dela. `automacao` × `remover`: o dono, em ' +
      'qualquer automação; a Cliqfy, só nas com SQL. Serviço fora: 503, e nada foi apagado.',
    resposta: z.object({ removida: z.boolean() }),
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da automação.' }],
  },
  simularAutomacao: {
    metodo: 'POST',
    caminho: '/v1/automacoes/{id}/simular',
    resumo: 'Simular sem efeito real',
    descricao:
      'Roda a automação com um evento de exemplo e devolve o caminho, as ações e os prazos que sairiam e quantas linhas cada SQL leu; nada é enviado, agendado nem gravado. `versao`: `ativa` ou `pendente` (sem pendente: 404). Só quem monta aquele tipo de automação. `automacao` × `editar`.',
    corpo: SimularAutomacaoSchema,
    resposta: SimulacaoDaAutomacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da automação.' }],
  },
  automacao: {
    metodo: 'GET',
    caminho: '/v1/automacoes/{id}',
    resumo: 'Uma automação, com o fluxo',
    descricao:
      'Gatilho, condição, fluxo e a `pendente` (a versão em homologação de uma ativa). 404 se não existe nesta organização. `automacao` × `ler`.',
    resposta: AutomacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da automação.' }],
  },
  editarAutomacao: {
    metodo: 'PATCH',
    caminho: '/v1/automacoes/{id}',
    resumo: 'Editar uma automação',
    descricao:
      'Troca a configuração inteira. Numa ativa, a edição vira a versão pendente em homologação e a ativa continua no ar; a resposta é a automação inteira, com a `pendente`. `versao` é a que a tela leu (a da automação ou a da pendente); outra: 409. A empresa não edita automação que usa SQL. `automacao` × `editar`. Cada lado monta o seu tipo: a automação com SQL (`consulta_sql`, `para_cada`) só a Cliqfy; a interna, só a empresa (dono e gestor). O tipo errado para o papel é 403.',
    corpo: EditarAutomacaoSchema,
    resposta: AutomacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da automação.' }],
  },
  editarAutomacaoDoModelo: {
    metodo: 'PATCH',
    caminho: '/v1/automacoes/{id}/configuracao-do-modelo',
    resumo: 'Editar a configuração de uma automação de modelo pronto',
    descricao:
      'Para a automação que veio de um modelo e não abre no mapa (`configuracao_do_modelo` preenchida): troca a configuração inteira — horários, consulta, ação e os dados dela —, conferida pelo esquema do modelo e com as mesmas conferências do criar. Numa ativa, vira a versão pendente em homologação e a ativa continua no ar; fora dela, volta a rascunho. `versao` é a que a tela leu (a da automação ou a da pendente); outra: 409. Modelo com SQL só a Cliqfy; os outros, só a empresa. Automação sem configuração de modelo: 409. `automacao` × `editar`. Serviço fora: 503 `ENGINE_INDISPONIVEL`.',
    corpo: EditarAutomacaoDoModeloSchema,
    resposta: AutomacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da automação.' }],
  },
  enviarAutomacaoParaHomologacao: {
    metodo: 'POST',
    caminho: '/v1/automacoes/{id}/enviar-para-homologacao',
    resumo: 'Enviar para homologação',
    descricao: '`rascunho` → `em_homologacao`. Transição inválida: 409. `automacao` × `editar`.',
    resposta: AutomacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da automação.' }],
  },
  ativarAutomacao: {
    metodo: 'POST',
    caminho: '/v1/automacoes/{id}/ativar',
    resumo: 'Ativar uma automação',
    descricao:
      '`em_homologacao` → `ativa`; numa ativa com pendente, promove a pendente. Transição inválida: 409. `automacao` × `ativar`: o dono, em qualquer automação; a Cliqfy, só nas com SQL.',
    resposta: AutomacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da automação.' }],
  },
  pausarAutomacao: {
    metodo: 'POST',
    caminho: '/v1/automacoes/{id}/pausar',
    resumo: 'Pausar uma automação',
    descricao:
      '`ativa` → `pausada`. Transição inválida: 409. `automacao` × `ativar`: o dono, em qualquer automação; a Cliqfy, só nas com SQL.',
    resposta: AutomacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da automação.' }],
  },
  voltarAutomacaoParaRascunho: {
    metodo: 'POST',
    caminho: '/v1/automacoes/{id}/voltar-para-rascunho',
    resumo: 'Devolver para rascunho',
    descricao:
      '`em_homologacao` → `rascunho` (recusar a homologação de uma automação que ainda não está no ar). A pendente de uma ativa não se descarta por aqui: depende do serviço. Transição inválida: 409. `automacao` × `editar`.',
    resposta: AutomacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da automação.' }],
  },
  historicoDaAutomacao: {
    metodo: 'GET',
    caminho: '/v1/automacoes/{id}/historico',
    resumo: 'O histórico de versões',
    descricao:
      'Da versão mais nova para a mais antiga: criada, editada, situação mudou, restaurada, com quem, quando e a foto daquela versão. `automacao` × `ler`.',
    resposta: HistoricoDaAutomacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da automação.' }],
  },
  restaurarAutomacao: {
    metodo: 'POST',
    caminho: '/v1/automacoes/{id}/restaurar',
    resumo: 'Restaurar uma versão',
    descricao:
      'Reaplica a foto como versão nova (rascunho, ou pendente se ativa); não reativa. O fluxo antigo é conferido de novo. `automacao` × `editar`. Cada lado monta o seu tipo: a automação com SQL (`consulta_sql`, `para_cada`) só a Cliqfy; a interna, só a empresa (dono e gestor). O tipo errado para o papel é 403.',
    corpo: RestaurarAutomacaoSchema,
    resposta: AutomacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da automação.' }],
  },
  // ── Listas (#922): lidas ao vivo do banco do cliente ─────────────────────
  listas: {
    metodo: 'GET',
    caminho: '/v1/listas',
    resumo: 'As listas da organização',
    descricao:
      'Quem atende vê as publicadas; a equipe técnica da Cliqfy vê também os rascunhos. ' +
      'As arquivadas não aparecem. A tela que consome uma lista pede pela `finalidade`. ' +
      'Sem a funcionalidade `automacoes` na organização, 404.',
    resposta: ListasSchema,
    parametros: [
      {
        nome: 'finalidade',
        em: 'query',
        descricao: 'Recorta pela finalidade (`exames`, `produtos`…). Sem ela, todas.',
      },
    ],
  },
  criarLista: {
    metodo: 'POST',
    caminho: '/v1/listas',
    resumo: 'Montar uma lista',
    descricao:
      'A lista nasce em rascunho. A operação tem de ser de listagem, de leitura e publicada; ' +
      'as colunas, a chave e o rótulo, colunas que ela devolve; e todo parâmetro dela, o do ' +
      'filtro por cliente. Exige Idempotency-Key. Só a equipe técnica da Cliqfy (`lista` × ' +
      '`editar`). Sem a funcionalidade `automacoes` na organização, 404.',
    corpo: CriarListaSchema,
    resposta: ListaSchema,
    status: 201,
  },
  pedidosDeLista: {
    metodo: 'GET',
    caminho: '/v1/listas/pedidos',
    resumo: 'Os pedidos de lista da organização',
    descricao:
      'Os mais novos primeiro (até 200). Do gestor e do administrador, que pedem (`lista` × ' +
      '`criar`), e da equipe técnica da Cliqfy, que atende. Sem a funcionalidade `automacoes` na organização, 404.',
    resposta: PedidosDeListaSchema,
    parametros: [
      { nome: 'situacao', em: 'query', descricao: '`aguardando`, `atendido` ou `recusado`.' },
    ],
  },
  pedirLista: {
    metodo: 'POST',
    caminho: '/v1/listas/pedidos',
    resumo: 'Pedir uma lista nova à equipe técnica',
    descricao:
      'A organização descreve o que quer ver; a Cliqfy escreve o SQL e monta a lista. O texto ' +
      'descreve a lista, e não deve trazer nome, telefone, documento ou placa de cliente: a ' +
      'tela avisa. Exige Idempotency-Key. Gestor e administrador (`lista` × `criar`). Sem a funcionalidade `automacoes` na organização, 404.',
    corpo: PedirListaSchema,
    resposta: PedidoDeListaSchema,
    status: 201,
  },
  atenderPedidoDeLista: {
    metodo: 'POST',
    caminho: '/v1/listas/pedidos/{id}/atender',
    resumo: 'Atender o pedido com a lista montada',
    descricao:
      'A lista tem de ser desta organização e publicada (404 se não for). Pedido já ' +
      'decidido: 409. Só a equipe técnica da Cliqfy (`lista` × `editar`).',
    corpo: AtenderPedidoDeListaSchema,
    resposta: PedidoDeListaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador do pedido.' }],
  },
  recusarPedidoDeLista: {
    metodo: 'POST',
    caminho: '/v1/listas/pedidos/{id}/recusar',
    resumo: 'Recusar o pedido, com o motivo',
    descricao: 'Pedido já decidido: 409. Só a equipe técnica da Cliqfy (`lista` × `editar`).',
    corpo: RecusarPedidoDeListaSchema,
    resposta: PedidoDeListaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador do pedido.' }],
  },
  lista: {
    metodo: 'GET',
    caminho: '/v1/listas/{id}',
    resumo: 'Uma lista',
    descricao: 'O rascunho só para a equipe técnica da Cliqfy; para os outros, 404.',
    resposta: ListaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da lista.' }],
  },
  editarLista: {
    metodo: 'PATCH',
    caminho: '/v1/listas/{id}',
    resumo: 'Editar uma lista',
    descricao:
      'Troca a definição inteira, conferida de novo contra a operação. A publicada continua ' +
      'publicada. `versao` diferente da atual: 409.',
    corpo: EditarListaSchema,
    resposta: ListaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da lista.' }],
  },
  arquivarLista: {
    metodo: 'DELETE',
    caminho: '/v1/listas/{id}',
    resumo: 'Arquivar uma lista',
    descricao:
      'Some da tela e das leituras; a operação no serviço não é tocada. Só a equipe técnica ' +
      'da Cliqfy (`lista` × `remover`).',
    resposta: ListaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da lista.' }],
  },
  publicarLista: {
    metodo: 'POST',
    caminho: '/v1/listas/{id}/publicar',
    resumo: 'Publicar uma lista',
    descricao:
      'Confere a definição contra a operação de agora e libera a lista para quem atende. ' +
      'Publicar a publicada não muda nada.',
    resposta: ListaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da lista.' }],
  },
  lerLista: {
    metodo: 'POST',
    caminho: '/v1/listas/{id}/leitura',
    resumo: 'Ler a lista ao vivo, como tabela',
    descricao:
      'A prévia de quem monta: lê também o rascunho. Consulta o banco do cliente agora; nada ' +
      'é gravado no CRM. Banco do cliente fora: 503 `BANCO_DO_CLIENTE_INDISPONIVEL`. Só a ' +
      'equipe técnica da Cliqfy (`lista` × `editar`); com `cliente_id`, exige também ler ' +
      'cliente, que a equipe técnica não tem.',
    corpo: LerListaSchema,
    resposta: LeituraDaListaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Identificador da lista.' }],
  },
  itensDaLista: {
    metodo: 'GET',
    caminho: '/v1/listas/{id}/itens',
    resumo: 'Os itens da lista, para o dropdown',
    descricao:
      'Lê ao vivo do banco do cliente a cada chamada (com 30 s de memória para o mesmo ' +
      'dropdown aberto de novo); nada é gravado no CRM. Lista com filtro por cliente exige ' +
      '`cliente`, e o acesso fica registrado; com `cliente`, exige também ler cliente. Só ' +
      'listas publicadas. A operação da lista mudou no serviço: 409 `CONFLITO_DE_ESTADO`. ' +
      'Banco do cliente fora: 503 `BANCO_DO_CLIENTE_INDISPONIVEL`.',
    resposta: ItensDaListaSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Identificador da lista.' },
      {
        nome: 'cliente',
        em: 'query',
        descricao: 'O cliente do filtro. Obrigatório quando a lista tem `filtro_por_cliente`.',
      },
      {
        nome: 'busca',
        em: 'query',
        descricao:
          'Recorta pelo rótulo, sem acento nem caixa, no que a operação devolveu (`total` e ' +
          '`cortada` são de antes da busca).',
      },
    ],
  },
  lugarNaListaDasAutomacoes: {
    metodo: 'GET',
    caminho: '/v1/lista-de-espera/automacoes',
    resumo: 'Se quem pergunta já está na lista de espera das automações',
    descricao:
      'Responde pela pessoa da sessão, na organização da sessão: a tela usa a resposta para ' +
      'trocar o botão "Quero ser um dos primeiros" pelo aviso de que ela já está na lista.',
    resposta: LugarNaListaDeEsperaSchema,
  },
  entrarNaListaDasAutomacoes: {
    metodo: 'POST',
    caminho: '/v1/lista-de-espera/automacoes',
    resumo: 'Entrar na lista de espera das automações',
    descricao:
      'Grava a pessoa da sessão, a organização e a data (migração 066). Idempotente sem ' +
      '`Idempotency-Key`: a chave da linha é a própria pessoa na organização, e o segundo pedido ' +
      'devolve a data do primeiro em vez de duplicar.',
    resposta: LugarNaListaDeEsperaSchema,
  },
  listaDeEsperaDasAutomacoes: {
    metodo: 'GET',
    caminho: '/v1/operacao/lista-de-espera/automacoes',
    resumo: 'Quem pediu as automações, em todas as organizações',
    descricao:
      'A consulta da equipe da Cliqfy: organização, pessoa, e-mail e data de cada pedido, do ' +
      'mais antigo para o mais novo. Só `operacao_cliqfy`. A leitura passa organização por ' +
      'organização, cada uma sob o próprio contexto, e não por uma porta que atravesse a RLS.',
    resposta: ListaDeEsperaSchema,
  },
  superAdmins: {
    metodo: 'GET',
    caminho: '/v1/operacao/super-admins',
    resumo: 'Quem é Super Admin',
    descricao:
      'Os Super Admins ativos (ADR-0025), com desde quando e quem deu o papel. Só para Super ' +
      'Admin. O papel é da pessoa, e não de uma organização: a lista é a mesma em qualquer uma.',
    resposta: SuperAdminsSchema,
  },
  concederSuperAdmin: {
    metodo: 'POST',
    caminho: '/v1/operacao/super-admins',
    resumo: 'Dar o papel de Super Admin a uma pessoa',
    descricao:
      'Pelo e-mail de uma conta que já existe no CliqCRM, de qualquer domínio, de gente e ' +
      'nunca técnica. Só um Super Admin ' +
      'concede, e a concessão fica na história global do papel (ADR-0025). Idempotente: quem já ' +
      'é Super Admin volta como está. Sem conta, 404; e-mail com duas contas ativas, 409.',
    corpo: ConcederSuperAdminSchema,
    resposta: SuperAdminSchema,
  },
  revogarSuperAdmin: {
    metodo: 'DELETE',
    caminho: '/v1/operacao/super-admins/{pessoaId}',
    resumo: 'Tirar o papel de Super Admin',
    descricao:
      'Marca a revogação, com quem revogou e quando; a linha fica, como história. Ninguém ' +
      'revoga a si mesmo, nem o último Super Admin (409): a plataforma nunca fica sem nenhum. ' +
      'Quem não é Super Admin é 404.',
    resposta: z.object({ revogado: z.boolean() }),
    parametros: [{ nome: 'pessoaId', em: 'path', descricao: 'Id da pessoa (`core.users`).' }],
  },
  planos: {
    metodo: 'GET',
    caminho: '/v1/operacao/planos',
    resumo: 'O catálogo de planos',
    descricao:
      'Cada plano com o que inclui, o preço padrão e quantas organizações não encerradas o têm ' +
      'hoje. Os ativos primeiro. Só para o Super Admin com a concessão em vigor (#766). O plano ' +
      'é da Cliqfy, e não de uma organização: a lista é a mesma em qualquer uma.',
    resposta: PlanosSchema,
  },
  criarPlano: {
    metodo: 'POST',
    caminho: '/v1/operacao/planos',
    resumo: 'Criar um plano no catálogo',
    descricao:
      'O plano nasce ativo, com os usuários e o armazenamento incluídos e os preços padrão em ' +
      'reais. Nome que já existe no catálogo é 409. Só para o Super Admin com a concessão em ' +
      'vigor.',
    corpo: CriarPlanoSchema,
    resposta: PlanoSchema,
  },
  editarPlano: {
    metodo: 'PATCH',
    caminho: '/v1/operacao/planos/{id}',
    resumo: 'Editar ou desativar um plano',
    descricao:
      'Só o que vier muda, e nenhum valor volta a nulo. Não mexe em contrato nenhum: quem já ' +
      'contratou fica com o preço que contratou. Desativado, o plano some da criação de ' +
      'organização. Plano que não existe é 404; nome que já existe, 409.',
    corpo: EditarPlanoSchema,
    resposta: PlanoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id do plano (`core.planos`).' }],
  },
  organizacoesNaGestao: {
    metodo: 'GET',
    caminho: '/v1/operacao/organizacoes',
    resumo: 'Todas as organizações, com o contrato e o uso',
    descricao:
      'A lista da gestão: cada organização com a situação, o plano, os usuários em uso e ' +
      'contratados e o armazenamento contratado, por nome. A busca e o filtro são daqui, e as ' +
      'contagens dos filtros também. Só para o Super Admin com a concessão em vigor (#767).',
    resposta: OrganizacoesNaGestaoSchema,
    parametros: [
      {
        nome: 'busca',
        em: 'query',
        descricao:
          'Parte do nome ou do identificador, ou parte do CNPJ, com ou sem a máscara, com as ' +
          'letras do CNPJ alfanumérico.',
      },
      {
        nome: 'situacao',
        em: 'query',
        descricao: 'ativa, em_criacao, suspensa ou encerrada. Sem ela, todas.',
      },
    ],
  },
  organizacaoNaGestao: {
    metodo: 'GET',
    caminho: '/v1/operacao/organizacoes/{id}',
    resumo: 'Uma organização: cadastro, contrato, uso e administradores',
    descricao:
      'O detalhe da gestão. Organização que não existe, ou id fora do formato, é 404. Só para o ' +
      'Super Admin com a concessão em vigor.',
    resposta: OrganizacaoDetalhadaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id da organização (`core.tenants`).' }],
  },
  historicoDaOrganizacao: {
    metodo: 'GET',
    caminho: '/v1/operacao/organizacoes/{id}/historico',
    resumo: 'O histórico de uma organização',
    descricao:
      'Criação, contrato criado e alterado (com antes e depois), cadastro alterado, ' +
      'encerramento, reabertura e exclusão, da mais nova para a mais antiga. Sai da trilha da ' +
      'própria organização. Organização que não existe é 404.',
    resposta: HistoricoDaOrganizacaoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id da organização (`core.tenants`).' }],
  },
  alterarContratoDaOrganizacao: {
    metodo: 'PATCH',
    caminho: '/v1/operacao/organizacoes/{id}/contrato',
    resumo: 'Alterar o contrato de uma organização',
    descricao:
      'Só o que vier muda, e nenhum valor volta a nulo. Não mexe no plano nem nas outras ' +
      'organizações, e trocar o plano não recopia os valores. A mudança entra no histórico da ' +
      'organização, com antes e depois. Organização que não existe é 404; sem contrato, 409; ' +
      'plano que não existe ou desativado, 400.',
    corpo: AlterarContratoDaOrganizacaoSchema,
    resposta: ContratoDaOrganizacaoAlteradoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id da organização (`core.tenants`).' }],
  },
  alterarCadastroDaOrganizacao: {
    metodo: 'PATCH',
    caminho: '/v1/operacao/organizacoes/{id}/cadastro',
    resumo: 'Alterar o cadastro de uma organização',
    descricao:
      'A razão social, o CNPJ, o segmento e o contato financeiro (#769). Só o que vier muda; o ' +
      'contato financeiro sai com nulo, e o resto não volta a nulo. A mudança entra no ' +
      'histórico da organização: a razão social, o CNPJ e o segmento com antes e depois; o ' +
      'contato financeiro, que é dado pessoal, só como incluído, trocado ou removido. A resposta ' +
      'traz o contato só quando o pedido o trouxe. CNPJ de outra organização é 409; contato ' +
      'financeiro sem contrato, 409; organização excluída de vez, 409, salvo o pedido que só ' +
      'apaga o contato financeiro (ele fica guardado até a purga, e sai antes a pedido); ' +
      'organização que não existe, 404.',
    corpo: AlterarCadastroDaOrganizacaoSchema,
    resposta: CadastroDaOrganizacaoAlteradoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id da organização (`core.tenants`).' }],
  },
  retomarCriacaoDaOrganizacao: {
    metodo: 'POST',
    caminho: '/v1/operacao/organizacoes/{id}/retomar',
    resumo: 'Retomar a criação de uma organização',
    descricao:
      'Continua a criação que parou no meio (#769), com o pedido que ela guardou: a mesma ' +
      'saga e a mesma resposta de POST /v1/operacao/revendas. O pedido fica guardado até a ' +
      'criação terminar, no máximo 30 dias; sem ele, 409, e a criação se refaz pelo ' +
      'formulário. Organização que não está em criação, 409; que não existe, 404. O corpo é ' +
      'vazio: o e-mail do administrador que já tem conta em outra organização, ou que é das ' +
      'contas técnicas, é 409 (`errors` aponta `admin.email`), e a criação se refaz pelo ' +
      'formulário, onde quem cria confirma. Só a saga roda: o cadastro, o contrato e o contato ' +
      'financeiro gravados na criação, ou mudados depois pela gestão, ficam como estão. A ' +
      'organização que sai da criação enquanto a saga roda não é ativada (409). Com o ' +
      'primeiro acesso, a resposta leva também o nome do administrador do pedido, para a tela ' +
      'entregá-lo, e a leitura da pessoa fica registrada na organização. Só o Super Admin.',
    corpo: RetomarCriacaoDaOrganizacaoSchema,
    resposta: CriacaoRetomadaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id da organização (`core.tenants`).' }],
  },
  canaisConectados: {
    metodo: 'GET',
    caminho: '/v1/admin/integrations/chatwoot/channels',
    resumo: 'Todos os canais conectados, de qualquer tipo',
    descricao:
      'A lista da tela de Conexões: WhatsApp, Instagram, Facebook, Web Chat, API e o que mais ' +
      'o motor de conversa tiver, com o tipo já traduzido. Lida do motor a cada chamada, sem ' +
      'projeção, pelo mesmo motivo da lista de WhatsApp: quem acabou de conectar precisa ver o ' +
      'canal na hora. Sem motor, volta vazia. Nenhuma credencial sai daqui.',
    resposta: CanaisConectadosSchema,
  },
  criarWebChat: {
    metodo: 'POST',
    caminho: '/v1/admin/integrations/chatwoot/channels/web-chat',
    resumo: 'Cria um Web Chat para o site da organização',
    descricao:
      'O motor de conversa cria o widget e devolve o código de instalação, que vai no site. As ' +
      'conversas dele chegam no Atendimento como as de qualquer canal.',
    corpo: CriarWebChatSchema,
    resposta: CanalConectadoSchema,
  },
  criarCanalDeApi: {
    metodo: 'POST',
    caminho: '/v1/admin/integrations/chatwoot/channels/api',
    resumo: 'Cria um canal que outro sistema alimenta',
    descricao:
      'Para outro sistema da organização falar com o Atendimento. Devolve o identificador do ' +
      'canal, que esse sistema usa para mandar mensagens. O endereço de aviso é opcional. O ' +
      'segredo de assinatura não sai por aqui.',
    corpo: CriarCanalDeApiSchema,
    resposta: CanalConectadoSchema,
  },
  desconectarCanal: {
    metodo: 'DELETE',
    caminho: '/v1/admin/integrations/chatwoot/channels/{id}',
    resumo: 'Desconecta um canal',
    descricao:
      'O motor de conversa apaga a caixa e as conversas dela; as mensagens novas param de ' +
      'chegar. Sem desfazer, e por isso exige step-up com justificativa ' +
      '(`integracao.desconectar_canal`). Caixa de outra organização é 404, igual à inexistente, ' +
      'e nada é apagado.',
    resposta: z.object({}).strict(),
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id da caixa no motor de conversa.' }],
  },
  atendentesDoCanal: {
    metodo: 'GET',
    caminho: '/v1/admin/integrations/chatwoot/channels/{id}/atendentes',
    resumo: 'Quem atende um canal',
    descricao:
      'As pessoas da organização que podem atender (têm acesso ao atendimento), cada uma dizendo ' +
      'se é membro daquela caixa. Membro é quem a distribuição automática considera e quem ' +
      'aparece para receber uma conversa passada. Não mexe no que a pessoa VÊ no CliqCRM. ' +
      'Caixa de outra organização é 404, igual à inexistente.',
    resposta: AtendentesDoCanalSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id da caixa no motor de conversa.' }],
  },
  definirAtendentes: {
    metodo: 'PUT',
    caminho: '/v1/admin/integrations/chatwoot/channels/{id}/atendentes',
    resumo: 'Troca a lista de quem atende um canal',
    descricao:
      'A lista inteira: quem sai deixa de ser membro, quem entra passa a ser. Ao menos uma ' +
      'pessoa, porque uma caixa sem membros recebe conversas e não as entrega a ninguém. ' +
      'Pessoa que não é desta organização, ou que não tem acesso ao atendimento, recusa o pedido.',
    corpo: DefinirAtendentesSchema,
    resposta: AtendentesDoCanalSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id da caixa no motor de conversa.' }],
  },
  canaisDeWhatsApp: {
    metodo: 'GET',
    caminho: '/v1/admin/integrations/chatwoot/channels/whatsapp',
    resumo: 'Os números de WhatsApp conectados',
    descricao:
      'Lidos do motor a cada chamada, sem projeção. Existe porque a tela de Conexões mostrava ' +
      'só o botão de conectar, mesmo com número já ligado — quem tinha acabado de conectar não ' +
      'via confirmação nenhuma, e a única forma de saber era mandar uma mensagem e esperar. ' +
      'Não devolve token, `phone_number_id` nem id de conta comercial: a pergunta que a tela ' +
      'faz é "qual número está conectado?", e é só isso que volta.',
    resposta: CanaisDeWhatsAppSchema,
  },
  alterarMensagensAutomaticas: {
    metodo: 'PATCH',
    caminho: '/v1/admin/integrations/chatwoot/channels/whatsapp/{id}/mensagens-automaticas',
    resumo: 'As duas mensagens que o canal manda sozinho',
    descricao:
      'Saudação (responde à primeira mensagem de cada contato) e fora de expediente (uma vez ' +
      'por dia). São as únicas automáticas seguras no WhatsApp: as duas REAGEM a uma mensagem ' +
      'do cliente, então estão sempre dentro da janela de 24 h da Meta — regra por gatilho e ' +
      'campanha caem fora dela, e fora dela só passa template aprovado. ' +
      'Ligar o fora de expediente empurra o HORÁRIO DA ORGANIZAÇÃO para o motor: o expediente é ' +
      'configurado uma vez, em Configurações, e não duas.',
    corpo: AlterarMensagensAutomaticasSchema,
    resposta: CanalDeWhatsAppSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id da caixa no motor de conversa.' }],
  },
  modelosDeMensagem: {
    metodo: 'GET',
    caminho: '/v1/admin/integrations/chatwoot/channels/whatsapp/{id}/modelos',
    resumo: 'Os modelos aprovados pela Meta, e os que ainda não foram',
    descricao:
      'Lidos da Meta a cada chamada, e não do motor de conversa: o motor guarda uma cópia que ' +
      'ele mesmo sincroniza de 3 em 3 horas, e a pergunta que esta tela faz — "a Meta já ' +
      'aprovou o que eu cadastrei?" — é justamente a que não pode esperar. ' +
      'Devolve o status e, na recusa, o MOTIVO: sem ele a organização vê "Recusado" e não tem o ' +
      'que fazer com a informação.',
    resposta: ModelosDeMensagemSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id da caixa no motor de conversa.' }],
  },
  criarModeloDeMensagem: {
    metodo: 'POST',
    caminho: '/v1/admin/integrations/chatwoot/channels/whatsapp/{id}/modelos',
    resumo: 'Cadastra um modelo para aprovação da Meta',
    descricao:
      'O modelo é a ÚNICA forma de escrever para quem ficou 24 h em silêncio — fora da janela ' +
      'a Meta não entrega texto livre. Até esta rota existir, a organização dependia do painel da ' +
      'Meta e nada no produto dizia isso a ela. ' +
      'Nasce `PENDING`: quem aprova é a Meta, em minutos ou em até um dia. `exemplos` é ' +
      'obrigatório quando o corpo usa variável — é o motivo de recusa mais comum que existe.',
    corpo: CriarModeloDeMensagemSchema,
    resposta: ModeloDeMensagemSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id da caixa no motor de conversa.' }],
  },
  removerModeloDeMensagem: {
    metodo: 'DELETE',
    caminho: '/v1/admin/integrations/chatwoot/channels/whatsapp/{id}/modelos/{nome}',
    resumo: 'Apaga um modelo na Meta',
    descricao:
      'Existe por causa da recusa: a Meta não deixa cadastrar dois modelos com o mesmo nome, ' +
      'então corrigir um recusado passa por apagar o anterior. Sem esta rota, o primeiro erro ' +
      'de digitação queimaria o nome para sempre. ' +
      'Apaga na Meta de verdade, e não desativa.',
    resposta: z.object({}).strict(),
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id da caixa no motor de conversa.' },
      { nome: 'nome', em: 'path', descricao: 'Nome do modelo na Meta.' },
    ],
  },
  sincronizarModelosDeMensagem: {
    metodo: 'POST',
    caminho: '/v1/admin/integrations/chatwoot/channels/whatsapp/{id}/modelos/sincronizar',
    resumo: 'Traz da Meta, agora, os modelos que o Atendimento oferece',
    descricao:
      'O mesmo botão "sincronizar" do painel do motor de conversa, com um passo a mais. O motor ' +
      'guarda uma cópia dos modelos que ele mesmo relê de 3 em 3 horas, e o Atendimento oferece ' +
      'o catálogo que o CliqCRM relê do motor de 15 em 15 minutos: um modelo recém-aprovado ' +
      'podia levar até meia hora para aparecer na conversa. ' +
      'Esta rota pede ao motor que releia a Meta, espera alguns segundos pela mudança e grava o ' +
      'catálogo novo. O motor sincroniza em segundo plano e não avisa quando termina: `mudou: ' +
      'false` significa que nada mudou nesse tempo, e não que a Meta confirmou que não há ' +
      'modelo novo. ' +
      'A tela de Modelos NÃO depende disto: ela já lê direto da Meta.',
    resposta: SincroniaDeModelosSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id da caixa no motor de conversa.' }],
  },
  listarIntegracoes: {
    metodo: 'GET',
    caminho: '/v1/admin/integrations',
    resumo: 'Conexões de engine do tenant',
    descricao:
      'Sem segredo nenhum, e sem a referência dele: `credencial_configurada` é booleano. ' +
      'A referência não é o segredo, mas é o endereço dele, e devolvê-la ensinaria a tela a ' +
      'exibir uma string que parece credencial.',
    resposta: ListaDeIntegracoesSchema,
  },
  autorizarMeta: {
    metodo: 'POST',
    caminho: '/v1/admin/integrations/meta/authorize',
    resumo: 'Começar a conexão de Facebook e Instagram',
    descricao:
      'Devolve para onde mandar a organização. A tela de consentimento é da Meta e mostra o nome ' +
      'do NOSSO aplicativo — o motor de conversa não aparece em momento nenhum. O `state` vai ' +
      'assinado: ele é o que atravessa a volta, que chega sem sessão, e sem assinatura ' +
      'qualquer um ligaria uma página na organização de outro.',
    resposta: AutorizacaoDaMetaSchema,
  },
  paginasDaMeta: {
    metodo: 'POST',
    caminho: '/v1/admin/integrations/meta/pages',
    resumo: 'Listar as páginas que a autorização alcança',
    descricao:
      'O passo entre voltar da Meta e conectar. Uma conta costuma administrar várias ' +
      'páginas, e ligar a errada faz as mensagens de outro negócio chegarem na organização — ' +
      'por isso lista, e quem escolhe é quem sabe. Devolve a REFERÊNCIA da autorização, ' +
      'nunca o token: ele abre todas as páginas da pessoa.',
    corpo: RetornoDaMetaSchema,
    resposta: PaginasDaMetaSchema,
  },
  conectarPaginaDaMeta: {
    metodo: 'POST',
    caminho: '/v1/admin/integrations/meta/pages/connect',
    resumo: 'Conectar a página escolhida, com o Instagram dela',
    descricao:
      'Cria a caixa no motor com o token DA PÁGINA — e não o do usuário, que expira com a ' +
      'sessão de quem autorizou. A conta Instagram Business ligada à página é vinculada ' +
      'junto, sem passo extra. Exige step-up: conectar uma página é passar a receber, e a ' +
      'poder responder, em nome daquele negócio.',
    corpo: ConectarPaginaSchema,
    resposta: CanalDaMetaSchema,
  },
  conectarWhatsAppPorSignup: {
    metodo: 'POST',
    caminho: '/v1/admin/integrations/chatwoot/channels/whatsapp/signup',
    resumo: 'Conectar WhatsApp pelo Embedded Signup da Meta',
    descricao:
      'O caminho PRINCIPAL para WhatsApp: a organização clica, escolhe a conta num popup da Meta ' +
      'e não digita nada — nem token, nem identificador. O popup é do navegador, porque só ' +
      'ele pode abrir a janela; daqui para a frente é servidor. O `code` que ele devolve vale ' +
      'poucos minutos e é de uso único, e não entra em log nem em auditoria. O motor troca o ' +
      '`code` por um token permanente com a credencial do NOSSO aplicativo, inscreve o número ' +
      'no webhook e checa a saúde da conta. Exige step-up. A alternativa manual, para conta ' +
      'que não consegue usar o popup, é POST .../channels/whatsapp.',
    corpo: ConectarWhatsAppPorSignupSchema,
    resposta: CanalPorSignupSchema,
  },
  conectarWhatsApp: {
    metodo: 'POST',
    caminho: '/v1/admin/integrations/chatwoot/channels/whatsapp',
    resumo: 'Conectar um número de WhatsApp informando as credenciais (alternativa)',
    descricao:
      'Cria a caixa no motor de conversa sem a organização sair do produto. A validação da ' +
      'credencial acontece na própria criação: o token e o número são conferidos contra a ' +
      'Meta antes de a caixa existir, e credencial errada não cria nada — é o connection ' +
      'test da §12.1, sem gravar dado comercial. O token vai para o motor e NÃO fica ' +
      'guardado aqui: um segredo com dois donos é um segredo que ninguém sabe qual vale. ' +
      'Exige step-up. Facebook e Instagram não passam por esta rota — o motor não os cria ' +
      'por API.',
    corpo: ConectarWhatsAppSchema,
    resposta: CanalCriadoSchema,
  },
  ligarWebhookDoChatwoot: {
    metodo: 'POST',
    caminho: '/v1/admin/integrations/chatwoot/webhook',
    resumo: 'Registrar o webhook do Chatwoot apontando para o nosso ingress',
    descricao:
      'Fecha a ligação que fazia a mensagem chegar: sem ela, o Chatwoot recebe e não avisa ' +
      'ninguém. Exige que a conexão já exista — o registro do webhook é chamada autenticada. ' +
      'O segredo de assinatura que o Chatwoot devolve vai direto para o cofre, e a conexão ' +
      'sobe de `url_secreta` para `hmac`: a partir daí, evento sem assinatura válida é ' +
      'recusado na borda (§8.3). A URL volta UMA vez, e contém o `hook_key`. Exige step-up: ' +
      'quem troca a URL do webhook desvia toda mensagem da organização.',
    resposta: ResultadoDaLigacaoSchema,
  },
  catalogoDePlataformas: {
    metodo: 'GET',
    caminho: '/v1/admin/integrations/catalog',
    resumo: 'Plataformas que o produto conhece, e o que cada uma exige',
    descricao:
      'O que a tela de configuração precisa para montar o formulário de um portal: quais ' +
      'campos ele exige, de que tipo, e onde a organização encontra cada um. `situacao` separa ' +
      '`fichado` (sabemos o que o portal exige, sem código) de `implementado` e ' +
      '`homologado` — sem ela, o catálogo pareceria anunciar integrações prontas que ainda ' +
      'não existem. Nenhum valor preenchido sai daqui: isto descreve a pergunta, nunca a ' +
      'resposta.',
    resposta: CatalogoDePlataformasSchema,
  },
  configurarIntegracao: {
    metodo: 'POST',
    caminho: '/v1/admin/integrations/{provider}/connections',
    resumo: 'Cadastrar ou atualizar a conexão de uma engine',
    descricao:
      'Exige step-up (§13.1) e testa a credencial contra a engine ANTES de gravar (§12.1) — ' +
      'conexão salva sem teste falha silenciosamente depois, e o sintoma aparece longe da ' +
      'causa. A credencial vai para o cofre e o banco guarda só a referência. O `hook_key` ' +
      'volta UMA vez, na resposta que o gerou: o banco guarda o SHA-256, e nem o servidor ' +
      'consegue exibi-lo de novo.',
    corpo: ConfigurarIntegracaoSchema,
    resposta: ResultadoDaConfiguracaoSchema,
    parametros: [{ nome: 'provider', em: 'path', descricao: 'A engine: `chatwoot`.' }],
  },
  pedirStepUp: {
    metodo: 'POST',
    caminho: '/v1/step-up',
    resumo: 'Obter acesso elevado temporário',
    descricao:
      'Ação sensível da §13.1 exige reautenticar AGORA — o servidor confere o `auth_time` ' +
      'do token, não só o `amr`: MFA de três horas atrás não é step-up. A concessão vale ' +
      'para um escopo exato e por prazo curto, e a justificativa fica em `audit_logs` para ' +
      'sempre. Quando uma ação é recusada por falta de concessão, o 403 traz a extensão ' +
      '`step_up` dizendo qual ação, por que ela exige isso e o que falta.',
    corpo: PedirStepUpSchema,
    resposta: ConcessaoDeStepUpSchema,
  },
  resetarSegundoFator: {
    metodo: 'POST',
    caminho: '/v1/users/{id}/mfa/reset',
    resumo: 'Resetar o segundo fator de um usuário',
    descricao:
      'Vetor de sequestro de conta (§13.1): exige justificativa e gera `audit_logs`. ' +
      'Devolve `havia_segundo_fator: false` quando não havia nada a remover — a tela não ' +
      'deve dizer "resetado", porque nesse caso o problema de acesso é outro.',
    corpo: ResetarSegundoFatorSchema,
    resposta: ResultadoDoResetSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id do usuário em `core.users`, dentro do tenant.' },
    ],
  },
  atualizarFunil: {
    metodo: 'POST',
    caminho: '/v1/pipelines/{id}/atualizar',
    resumo: 'Atualizar o funil: adiantar as rotinas que o Super Admin marcou para ele',
    descricao:
      'O botão "Atualizar" do quadro (#1136). Adianta, no serviço de automações, só as rotinas que o Super Admin marcou para este funil e que ainda podem rodar de novo sem efeito em dobro (ativas, de agenda, criando card com referência ou movendo pela referência), sem mexer no horário delas. Funil sem rotina marcada não tem o botão: 404. Uma vez a cada 2 minutos por funil, para a organização toda: dentro da janela, 429 `LIMITE_EXCEDIDO` com `Retry-After`. Quem lê oportunidade pode (decisão de 28/10). Sem a funcionalidade `automacoes`, responde `disparadas: 0`. Funil de outra organização ou inexistente: 404. Serviço fora antes de adiantar qualquer rotina: 503 `ENGINE_INDISPONIVEL`, e a trava é solta; a rotina que falha sozinha é pulada e não conta.',
    resposta: AtualizacaoDoFunilSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'A chave do funil (`padrao` para o padrão).' },
    ],
  },
  configuracaoDoAtualizar: {
    metodo: 'GET',
    caminho: '/v1/pipelines/{id}/atualizar/configuracao',
    resumo: 'As rotinas que o "Atualizar" do funil pode adiantar, e as marcadas',
    descricao:
      'Só o Super Admin, com a concessão em vigor (decisão de 28/10, #1136). Lista as rotinas do serviço de automações que alimentam este funil e podem rodar de novo sem efeito em dobro, com `marcada` para as que o botão adianta hoje. Funil de outra organização ou inexistente: 404. Serviço fora: 503 `ENGINE_INDISPONIVEL`.',
    resposta: ConfiguracaoDoAtualizarSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'A chave do funil (`padrao` para o padrão).' },
    ],
  },
  definirConfiguracaoDoAtualizar: {
    metodo: 'PUT',
    caminho: '/v1/pipelines/{id}/atualizar/configuracao',
    resumo: 'Escolher as rotinas que o "Atualizar" do funil adianta',
    descricao:
      'Só o Super Admin, com a concessão em vigor (#1136). A lista substitui a anterior; vazia, tira o botão do funil. Cada id tem de ser de uma rotina que a configuração lista para ESTE funil: outra qualquer (de outro funil, de mensagem, pausada, de outra organização) é 400. Fica na trilha (`funil.atualizar_configurado`).',
    corpo: DefinirConfiguracaoDoAtualizarSchema,
    resposta: ConfiguracaoDoAtualizarSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'A chave do funil (`padrao` para o padrão).' },
    ],
  },
  atendimentosDoFunil: {
    metodo: 'GET',
    caminho: '/v1/pipelines/{id}/atendimentos',
    resumo: 'O que cada card do quadro é no SISAC',
    descricao:
      'Para os cards que o `board` devolveria com os mesmos filtros, o último passo que as ' +
      'rotinas ligaram a cada um (a consulta ou o exame), lido AO VIVO do SISAC pela parte ' +
      '`detalhes_dos_atendimentos` da agenda da clínica (#1151, ADR-0031): o serviço, o médico, ' +
      'quando foi agendado, o dia marcado e o valor. Nada é gravado no CRM. Lê quem lê ' +
      'oportunidade, e a leitura fica registrada por cliente. Fora da clínica, ou sem a parte ' +
      'ligada, `sisac: sem_parte` e a lista vazia; SISAC fora, `sisac: fora`.',
    resposta: AtendimentosDoFunilSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'A chave do funil (`padrao` para o padrão).' },
      { nome: 'responsavel', em: 'query', descricao: 'O mesmo filtro do `board`.' },
      { nome: 'em_risco', em: 'query', descricao: 'O mesmo filtro do `board`.' },
      { nome: 'busca', em: 'query', descricao: 'O mesmo filtro do `board`.' },
      { nome: 'campos', em: 'query', descricao: 'O mesmo filtro do `board`.' },
    ],
  },
  atendimentosDaOportunidade: {
    metodo: 'GET',
    caminho: '/v1/opportunities/{id}/atendimentos',
    resumo: 'Os passos da oportunidade no SISAC, com o total',
    descricao:
      'Todos os passos que as rotinas ligaram à oportunidade (a consulta, os exames), na ordem ' +
      'em que chegaram, lidos AO VIVO do SISAC (#1151, ADR-0031), e a soma do que foi cobrado. ' +
      'Lê quem lê a oportunidade, na carteira; de outra organização ou fora da carteira, 404. ' +
      'A leitura fica registrada. Fora da clínica, ou sem a parte ligada, `sisac: sem_parte`.',
    resposta: AtendimentosDaOportunidadeSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  boardDoFunil: {
    metodo: 'GET',
    caminho: '/v1/pipelines/{id}/board',
    resumo: 'Board do funil, por etapa',
    descricao:
      'Kanban servido do read model (D01): colunas na ordem que o workspace declara, com ' +
      'total e valor somado DO CONJUNTO no cabeçalho — não da fatia carregada. Cada card ' +
      'traz risco com o motivo, última interação, acesso rápido à conversa e o `version` ' +
      'que o movimento exige. Hoje existe um funil por organização: o `{id}` é `padrao`.',
    resposta: BoardDoFunilSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Identificador do funil. Hoje só `padrao`.' },
      {
        nome: 'responsavel',
        em: 'query',
        descricao: 'Id externo do responsável, ou `eu` para os próprios.',
      },
      { nome: 'em_risco', em: 'query', descricao: '`true` mostra só os cards em risco.' },
      { nome: 'busca', em: 'query', descricao: 'Texto no nome da oportunidade.' },
      {
        nome: 'campos',
        em: 'query',
        descricao:
          'Filtro por campo personalizado (#568): condições separadas por `;`, cada uma ' +
          '`<id do campo>:<valor>`. Lista de opções: os ids das opções, separados por vírgula ' +
          '(qualquer uma). Sim/não: `sim` ou `nao`. Data: `AAAA-MM-DD..AAAA-MM-DD`, com um ' +
          'lado opcional. Campo de outra organização é 404.',
      },
    ],
  },
  mensagensProntas: {
    metodo: 'GET',
    caminho: '/v1/mensagens-prontas',
    resumo: 'Os textos pré-escritos da organização',
    descricao:
      'O que a equipe repete o dia inteiro — saudação, endereço, horário —, escrito uma vez em ' +
      'Configurações ou na caixa da conversa, e usado por todos. Lê quem ATENDE; escreve quem ' +
      'tem a permissão própria das mensagens prontas (`mensagem_pronta:editar`, ADR-0034), o ' +
      'administrador por padrão. ' +
      'Não confundir com o modelo aprovado da Meta: este é texto livre, entregue só dentro da ' +
      'janela de 24 h. No atendimento os dois aparecem na mesma caixa, em abas.',
    resposta: MensagensProntasSchema,
  },
  criarMensagemPronta: {
    metodo: 'POST',
    caminho: '/v1/mensagens-prontas',
    resumo: 'Escrever uma mensagem pré-definida',
    descricao:
      'O título é único por organização, sem depender de caixa: "Saudação" e "saudação" são a ' +
      'mesma coisa para quem procura na lista, e duas linhas para o banco. Repetir devolve 409 ' +
      'em vez de encher a lista de quase-duplicatas que ninguém sabe qual usar. Pede ' +
      '`mensagem_pronta:editar`, como corrigir e tirar.',
    corpo: CriarMensagemProntaSchema,
    resposta: MensagemProntaSchema,
    status: 201,
  },
  editarMensagemPronta: {
    metodo: 'PATCH',
    caminho: '/v1/mensagens-prontas/{id}',
    resumo: 'Corrigir uma mensagem pré-definida',
    descricao:
      'Campo ausente é "não mexer". Corrigir aqui conserta para a equipe inteira de uma vez — ' +
      'que é a razão de a lista ser da organização e não de cada pessoa.',
    corpo: EditarMensagemProntaSchema,
    resposta: MensagemProntaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id da mensagem pronta.' }],
  },
  removerMensagemPronta: {
    metodo: 'DELETE',
    caminho: '/v1/mensagens-prontas/{id}',
    resumo: 'Tirar uma mensagem pré-definida da lista',
    descricao:
      'Some da caixa do atendimento na hora. O que já foi ENVIADO com ela continua no histórico ' +
      'da conversa: a mensagem pronta é um atalho para escrever, não o registro do que foi dito.',
    resposta: z.object({ removida: z.boolean() }),
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id da mensagem pronta.' }],
  },
  catalogoDePermissoes: {
    metodo: 'GET',
    caminho: '/v1/permissoes/catalogo',
    resumo: 'O catálogo do que se liga por pessoa',
    descricao:
      'As abas, os assuntos e as ações que a seção Permissões mostra, com o texto de cada ' +
      'toggle, o que só liga com a de ver ligada e o que é só da Cliqfy (travado). Vem junto o ' +
      'padrão de cada papel da organização: é a matriz de hoje, e quem não for ajustado ' +
      'continua com ele (ADR-0034). Pede `permissao:editar`.',
    resposta: CatalogoDePermissoesSchema,
  },
  pessoasDasPermissoes: {
    metodo: 'GET',
    caminho: '/v1/permissoes/pessoas',
    resumo: 'As pessoas da seção Permissões',
    descricao:
      'Cada pessoa da organização com o estado das permissões dela: o padrão do papel ou ' +
      'personalizada (ADR-0034, #1190). O Suporte Cliqfy, o Super Admin e a conta Automação ' +
      'não aparecem: só a Cliqfy altera esses acessos. Pede `permissao:editar`.',
    resposta: PessoasDasPermissoesSchema,
  },
  permissoesDaPessoa: {
    metodo: 'GET',
    caminho: '/v1/permissoes/pessoas/{id}',
    resumo: 'As permissões de uma pessoa',
    descricao:
      'O padrão do papel, o que ela pode hoje (com os ajustes e as dependências do catálogo), ' +
      'os ajustes guardados e se quem está na tela pode salvar. Pessoa de outra organização, ' +
      'ou que não aparece na seção, responde 404. Pede `permissao:editar`.',
    resposta: PermissoesDaPessoaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id do vínculo da pessoa.' }],
  },
  salvarPermissoesDaPessoa: {
    metodo: 'PUT',
    caminho: '/v1/permissoes/pessoas/{id}',
    resumo: 'Salvar as permissões de uma pessoa',
    descricao:
      'Guarda o que difere do padrão do papel; ajustes vazios são "Voltar ao padrão do papel". ' +
      'Quem só tem `permissao:editar` (e não é Administrador nem Super Admin) só liga o que ' +
      'ela mesma tem, até o alcance dela, não muda as próprias permissões e não mexe nas de ' +
      'quem é Administrador. Se o papel da pessoa mudou desde que a tela abriu, 409. A mudança ' +
      'vai para a trilha de auditoria e vale na requisição seguinte da pessoa.',
    corpo: SalvarPermissoesDaPessoaSchema,
    resposta: PermissoesDaPessoaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id do vínculo da pessoa.' }],
  },
  documentosDaOportunidade: {
    metodo: 'GET',
    caminho: '/v1/opportunities/{id}/documentos',
    resumo: 'Os documentos da oportunidade',
    descricao:
      'PDF, fotos, vídeos e outros arquivos, guardados como anexo da oportunidade no CRM. Cada ' +
      'um vem com um link de abrir assinado, que vale cinco minutos: a listagem emite um novo a ' +
      'cada leitura.',
    resposta: DocumentosDaOportunidadeSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  enviarDocumentosDaOportunidade: {
    metodo: 'POST',
    caminho: '/v1/opportunities/{id}/documentos',
    resumo: 'Anexar arquivos à oportunidade',
    descricao:
      '`multipart/form-data`, com os arquivos no campo `arquivos`: até 10 por envio, até ' +
      '100 MB cada. Vão para o CRM, um de cada vez, e cada envio fica na trilha de auditoria.',
    corpo: EnviarDocumentosSchema,
    resposta: DocumentosDaOportunidadeSchema,
    status: 201,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  removerDocumentoDaOportunidade: {
    metodo: 'DELETE',
    caminho: '/v1/opportunities/{id}/documentos/{documento}',
    resumo: 'Apagar um documento da oportunidade',
    descricao:
      'Apaga o anexo e o arquivo no CRM. Confere antes que o documento é desta oportunidade: ' +
      'de outra, ou inexistente, é 404.',
    resposta: z.object({ removido: z.boolean() }),
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' },
      { nome: 'documento', em: 'path', descricao: 'A referência do documento no CRM.' },
    ],
  },
  documentoDaOportunidade: {
    metodo: 'GET',
    caminho: '/v1/anexos/crm/{token}',
    resumo: 'Serve um documento da oportunidade',
    descricao:
      'Sem sessão, como o anexo de conversa: quem abre é o navegador, por link. Autoriza a ' +
      'ASSINATURA da URL, válida por cinco minutos e emitida pela listagem de documentos, que ' +
      'passa pela matriz. Responde 302 para o endereço assinado do armazenamento, que vence ' +
      'junto com este link; o documento de antes do armazenamento vem no corpo. ' +
      'Armazenamento fora: 503. Vencido, adulterado, de anexo de conversa ou de documento que ' +
      'não é da oportunidade assinada: 404, sem distinguir os casos.',
    resposta: z.string(),
    conteudoDaResposta: 'application/octet-stream',
    autenticada: false,
    parametros: [
      {
        nome: 'token',
        em: 'path',
        descricao: 'A assinatura opaca emitida na listagem dos documentos. Vale cinco minutos.',
      },
    ],
  },
  conversasDaOportunidade: {
    metodo: 'GET',
    caminho: '/v1/opportunities/{id}/conversas',
    resumo: 'As conversas do cliente desta oportunidade',
    descricao:
      'Todas as conversas do cliente, as desta oportunidade marcadas, cada uma com quando ' +
      'abriu, fechou e reabriu. Fechamentos e reaberturas só são registrados desde 23/09/2026; ' +
      'antes disso, a conversa mostra a abertura e o último fechamento.',
    resposta: ConversasDaOportunidadeSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  historicoDaOportunidade: {
    metodo: 'GET',
    caminho: '/v1/opportunities/{id}/historico',
    resumo: 'O caminho da oportunidade pelo funil',
    descricao:
      'Cada entrada em etapa, com a hora, a etapa de onde veio, quem a moveu, e se terminou ' +
      'ganha ou perdida, com o motivo. "Quem" só é conhecido quando o movimento passou pelo ' +
      'CliqCRM; o feito direto no CRM ou por automação sai sem nome, e diz isso.',
    resposta: HistoricoDaOportunidadeSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  atividadesDaOportunidade: {
    metodo: 'GET',
    caminho: '/v1/opportunities/{id}/atividades',
    resumo: 'O que aconteceu dentro da oportunidade',
    descricao:
      'Nota escrita ou corrigida (com o começo do texto de hoje), documento enviado ou removido ' +
      '(com o nome de hoje), consulta marcada, atualizada (o que mudou) ou cancelada, e edição ' +
      'dos dados, cada um com quem fez e quando, do mais recente ao mais antigo. Quem e quando ' +
      'vêm da auditoria; o conteúdo, de onde mora: nota ou documento apagado sai sem trecho ' +
      'ou nome.',
    resposta: AtividadesDaOportunidadeSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  motivosDePerda: {
    metodo: 'GET',
    caminho: '/v1/funil/motivos-de-perda',
    resumo: 'Os motivos de perda da organização',
    descricao:
      'A lista que a revenda escreve para dizer por que perdeu uma venda. Vem com os ' +
      'desativados, marcados, porque as perdas antigas continuam nomeando o motivo. Lê quem lê ' +
      'oportunidade: é quem escolhe o motivo ao levar um negócio para a perda.',
    resposta: MotivosDePerdaSchema,
  },
  criarMotivoDePerda: {
    metodo: 'POST',
    caminho: '/v1/funil/motivos-de-perda',
    resumo: 'Acrescentar um motivo de perda',
    descricao:
      'O texto é único por organização, sem depender de maiúscula: dois "Preço" dividiriam a ' +
      'contagem entre eles. Repetir devolve 409.',
    corpo: CriarMotivoDePerdaSchema,
    resposta: MotivoDePerdaSchema,
    status: 201,
  },
  editarMotivoDePerda: {
    metodo: 'PATCH',
    caminho: '/v1/funil/motivos-de-perda/{id}',
    resumo: 'Renomear ou desativar um motivo de perda',
    descricao:
      'Não há remoção: desativado, o motivo sai da lista de escolha e continua nomeando as ' +
      'perdas já registradas. Renomear não reescreve o que foi registrado antes.',
    corpo: EditarMotivoDePerdaSchema,
    resposta: MotivoDePerdaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id do motivo de perda.' }],
  },
  roteamentoDeFunil: {
    metodo: 'GET',
    caminho: '/v1/funil/roteamento',
    resumo: 'Para qual funil vai cada número ou canal',
    descricao:
      'Cada caixa conectada (número de WhatsApp, conta de Instagram), com a regra que diz em ' +
      'qual funil nasce a oportunidade criada automaticamente a partir dela. Caixa sem regra ' +
      'vai para o funil padrão. Lê quem lê oportunidade: o atendimento usa a regra para ' +
      'sugerir o funil ao criar à mão.',
    resposta: RoteamentoSchema,
  },
  definirRoteamentoDeFunil: {
    metodo: 'PUT',
    caminho: '/v1/funil/roteamento/{caixa}',
    resumo: 'Mandar as oportunidades de um número para um funil',
    descricao:
      'Cria ou troca a regra da caixa. O funil precisa existir e, se vier, a etapa inicial ' +
      'precisa ser dele. Vale para as próximas oportunidades; as que já existem ficam onde ' +
      'estão.',
    corpo: DefinirRoteamentoSchema,
    resposta: CaixaRoteadaSchema,
    parametros: [{ nome: 'caixa', em: 'path', descricao: 'Id da caixa no atendimento.' }],
  },
  removerRoteamentoDeFunil: {
    metodo: 'DELETE',
    caminho: '/v1/funil/roteamento/{caixa}',
    resumo: 'Devolver um número ao funil padrão',
    descricao: 'Tira a regra da caixa. As próximas oportunidades dela nascem no funil padrão.',
    resposta: z.object({ removida: z.boolean() }),
    parametros: [{ nome: 'caixa', em: 'path', descricao: 'Id da caixa no atendimento.' }],
  },
  caixasDoAtendimento: {
    metodo: 'GET',
    caminho: '/v1/inbox/caixas',
    resumo: 'As caixas de entrada que a pessoa atende',
    descricao:
      'As opções do filtro "Caixas" da lista de conversas: com dois números do mesmo canal, ' +
      'quem atende escolhe ver só uma caixa, ou algumas. Só as do escopo de quem pergunta ' +
      '(B16): oferecer caixa fora dele listaria um filtro que sempre volta vazio. Servida da ' +
      'projeção, como a lista.',
    resposta: CaixasDoAtendimentoSchema,
  },
  naoLidasDaInbox: {
    metodo: 'GET',
    caminho: '/v1/inbox/nao-lidas',
    resumo: 'Quantas mensagens estão esperando resposta',
    descricao:
      'O número do sino, que vive no menu e portanto em toda tela. Rota própria porque tirar ' +
      'isto da listagem obrigaria cada tela a carregar a fila inteira para ler um inteiro — e ' +
      'porque a fila é paginada: somar a página daria menos do que existe, e o número cairia ' +
      'conforme a pessoa rolasse a lista. ' +
      'Conta as conversas não resolvidas dentro do escopo de quem pergunta (B16). O recorte ' +
      'nasceu mais estreito — só as minhas e as sem dono — e estava errado: numa revenda ' +
      'pequena as conversas têm dono desde o primeiro minuto, e o sino ficava mudo justamente ' +
      'quando chegava mensagem. Quem limita é o escopo, não a atribuição.',
    resposta: NaoLidasDaInboxSchema,
  },
  notificacoes: {
    metodo: 'GET',
    caminho: '/v1/notificacoes',
    resumo: 'As notificações ainda não vistas de quem pergunta',
    descricao:
      'O conteúdo do card de mensagem recebida (#536): quem escreveu, o trecho e a caixa. O ' +
      'fluxo SSE só avisa que há novidade (`notificacao:eu`); o card sai daqui, onde o escopo ' +
      'de caixa é reavaliado na leitura — quem deixou de atender uma caixa não vê a ' +
      'notificação dela. Só as não vistas da última hora, das mais novas para trás, no máximo ' +
      '20: card é interrupção, e o que ficou para trás está na fila da inbox.',
    resposta: NotificacoesSchema,
  },
  chavePublicaDoPush: {
    metodo: 'GET',
    caminho: '/v1/push/chave-publica',
    resumo: 'A chave pública do servidor para o push do navegador',
    descricao:
      'A chave VAPID pública (base64url), que o navegador exige para criar a inscrição de push ' +
      '(#536, ADR-0017). `null` quando este servidor não tem push configurado — a tela esconde ' +
      'a opção em vez de oferecer o que não vai funcionar.',
    resposta: ChavePublicaDoPushSchema,
  },
  registrarDispositivo: {
    metodo: 'POST',
    caminho: '/v1/me/dispositivos',
    resumo: 'Guardar a inscrição de push deste navegador',
    descricao:
      'Liga a inscrição do navegador a quem está logado nesta organização. Repetir com o ' +
      'mesmo endpoint atualiza as chaves, sem duplicar; se outra pessoa da organização tinha ' +
      'inscrito o mesmo navegador (computador compartilhado), ele passa a ser de quem ' +
      'inscreveu por último. Só endpoint https de serviço de push conhecido — quem envia para ' +
      'lá é o servidor.',
    corpo: RegistrarDispositivoSchema,
    resposta: DispositivoRegistradoSchema,
    status: 201,
  },
  removerDispositivo: {
    metodo: 'POST',
    caminho: '/v1/me/dispositivos/remover',
    resumo: 'Deixar de receber push neste navegador',
    descricao:
      'Pelo endpoint da inscrição. Remove só a da própria pessoa; endpoint de outra ou ' +
      'inexistente devolve `removidos: 0`, sem erro. Chamado ao desligar o push e ao sair do CRM.',
    corpo: RemoverDispositivoSchema,
    resposta: DispositivosRemovidosSchema,
  },
  marcarNotificacoesVistas: {
    metodo: 'POST',
    caminho: '/v1/notificacoes/vistas',
    resumo: 'Marcar notificações como vistas',
    descricao:
      'Depois de vista, a notificação não volta em nenhuma aba. Idempotente: repetir não muda ' +
      'nada. Id de outra pessoa é ignorado em silêncio, sem erro — dizer que não é dela ' +
      'confirmaria que existe.',
    corpo: MarcarNotificacoesVistasSchema,
    resposta: NotificacoesMarcadasSchema,
  },
  funisDaOrganizacao: {
    metodo: 'GET',
    caminho: '/v1/funis',
    resumo: 'Os funis da organização',
    descricao:
      'Um funil existe enquanto houver etapa com o prefixo dele — `F_VENDAS_NOVO` é a etapa ' +
      '"Novo" do funil "Vendas". Não há cadastro separado, e é o que dispensa o estado "funil ' +
      'criado e vazio", que não teria quadro para mostrar nem para onde arrastar. ' +
      'A organização que nunca separou funis vê um só, de chave `padrao`: são as etapas sem ' +
      'prefixo, que é como toda revenda começa.',
    resposta: FunisSchema,
  },
  criarFunil: {
    metodo: 'POST',
    caminho: '/v1/funis',
    resumo: 'Criar um funil, com a primeira etapa dele',
    descricao:
      'Criar funil é criar a primeira etapa dele: não existe funil vazio — ele é o conjunto das ' +
      'etapas com o prefixo dele, e um sem etapa nenhuma não teria quadro para mostrar nem para ' +
      'onde arrastar. ' +
      'A etapa nasce ABERTA, e isso não é detalhe: `etapas_abertas` guarda valores completos, e ' +
      'funil cujas etapas não foram marcadas como abertas trava todo card com "etapa fechada" — ' +
      'uma mensagem que ninguém entende. ' +
      'A chave é MAIÚSCULA com sublinhado porque vira prefixo do valor da etapa no motor; o nome ' +
      'é o que a tela mostra, e aceita acento. ' +
      'Repetido é o NOME, comparado sem acento e sem caixa: nome de outro funil é 409. Se a ' +
      'chave pedida já é de outro funil, que existe ou existiu, o funil novo ganha um número no ' +
      'fim dela; a chave que vale é a da resposta.',
    corpo: CriarFunilSchema,
    resposta: FunilSchema,
    status: 201,
  },
  renomearFunil: {
    metodo: 'PATCH',
    caminho: '/v1/funis/{chave}',
    resumo: 'Trocar o nome de um funil',
    descricao:
      'Só o nome. A chave é imutável pela mesma razão que o valor da etapa é: ela está escrita em ' +
      'toda oportunidade do funil, dentro do valor da etapa — trocá-la exigiria reescrever todas, ' +
      'e uma falha no meio deixaria metade da revenda num funil que não existe mais. ' +
      'Nome de outro funil, comparado sem acento e sem caixa, é 409.',
    corpo: RenomearFunilSchema,
    resposta: FunilSchema,
    parametros: [{ nome: 'chave', em: 'path', descricao: 'A chave do funil.' }],
  },
  criarOportunidade: {
    metodo: 'POST',
    caminho: '/v1/opportunities',
    resumo: 'Criar oportunidade',
    descricao:
      'Sem etapa, nasce na primeira do funil DA ORGANIZAÇÃO — nunca num valor fixo, porque ' +
      'cada organização tem funil próprio. Exige `Idempotency-Key` (§6.1): duas oportunidades ' +
      'idênticas no funil fazem o gestor contar duas onde há uma.',
    corpo: CriarOportunidadeSchema,
    resposta: OportunidadeSchema,
  },
  moverEtapa: {
    metodo: 'PATCH',
    caminho: '/v1/opportunities/{id}',
    resumo: 'Mover de etapa, ou editar a oportunidade',
    descricao:
      'Com `etapa`, MOVE: valida permissão, TRANSIÇÃO e versão, nessa ordem (§10.3). A ' +
      'transição é regra do PRODUTO — sair de etapa fechada é recusado (e pular etapa, ' +
      'quando o funil exige as etapas em ordem) ' +
      'aqui, não no CRM, porque deixar para a engine faria cada organização se comportar ' +
      'diferente. ' +
      'Sem `etapa`, EDITA título, valor, previsão de fechamento e cliente vinculado: campo ' +
      'ausente é "não mexer" e `null` apaga, como no cadastro do cliente. Trocar o cliente ' +
      'exige que ele tenha vínculo no CRM, senão a oportunidade ficaria solta. ' +
      'Mover e editar no MESMO pedido é recusado: são duas escritas na engine, e a segunda ' +
      'falhando deixaria o card em outra etapa com os dados antigos. ' +
      '`version` é obrigatório nos dois casos e devolve 409 quando alguém mexeu antes. Os ' +
      'efeitos derivados da mudança de etapa acontecem fora da requisição.',
    corpo: EditarOportunidadeSchema,
    resposta: OportunidadeSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade (§5.1).' }],
  },
  listarInbox: {
    metodo: 'GET',
    caminho: '/v1/inbox/conversations',
    resumo: 'Lista da Inbox comercial',
    descricao:
      'A coluna esquerda da tela (§11.2), servida SÓ dos read models — é o que a mantém ' +
      'de pé com o Chatwoot fora (§14.3) e o que sustenta o P95 da §14.2. Cada item traz o ' +
      'contexto comercial resumido (cliente, oportunidade, etapa), que é o que diferencia ' +
      'esta Inbox da do Chatwoot. Paginação estável por cursor, com desempate por id. O ' +
      'escopo do vínculo (B16) entra como PREDICADO da consulta, inclusive no total.',
    resposta: PaginaDaInboxSchema,
    parametros: [
      {
        nome: 'cursor',
        em: 'query',
        descricao: 'Cursor da página anterior. Ausente = primeira página.',
      },
      {
        nome: 'caixa',
        em: 'query',
        descricao: 'Filtra por inbox (id externo). Repetível por vírgula.',
      },
      {
        nome: 'situacao',
        em: 'query',
        descricao: 'aberta · pendente · resolvida · em_espera. Repetível por vírgula.',
      },
      {
        nome: 'responsavel',
        em: 'query',
        descricao:
          'Id externo do atendente, `sem_responsavel` para os não atribuídos, ou `meus` — ' +
          'que o SERVIDOR resolve, porque quem chama só conhece o próprio id interno e a ' +
          'projeção guarda o id no motor de conversa (dois espaços de identificador).',
      },
      { nome: 'canal', em: 'query', descricao: 'Canal fino da matriz (whatsapp_cloud, api, ...).' },
      { nome: 'prioridade', em: 'query', descricao: 'baixa · media · alta · urgente.' },
      {
        nome: 'busca',
        em: 'query',
        descricao: 'Texto no trecho da última mensagem ou no nome do cliente.',
      },
      {
        nome: 'limite',
        em: 'query',
        descricao: 'Itens por página (1 a 100; padrão 30).',
      },
    ],
  },
  etapasDoFunil: {
    metodo: 'GET',
    caminho: '/v1/funil/etapas',
    resumo: 'As etapas do funil desta organização',
    descricao:
      'Lidas do motor de CRM a cada chamada, na ordem do quadro. A A01 §4 mediu o funil que ' +
      'vem de fábrica — NEW, SCREENING, MEETING, PROPOSAL, CUSTOMER — e concluiu que ele não é ' +
      'o funil de uma organização de veículos. Estas rotas existem para trocá-lo.',
    resposta: EtapasDoFunilSchema,
  },
  criarEtapaDoFunil: {
    metodo: 'POST',
    caminho: '/v1/funil/etapas',
    resumo: 'Acrescenta uma etapa no fim do funil',
    descricao:
      'NO FIM, e não numa posição escolhida: inserir no meio muda a posição das seguintes, e a ' +
      'posição é o que ordena o quadro — negócios mudariam de coluna sem ninguém ter movido ' +
      'nada. Reordenar é operação própria. O `valor` é o que fica gravado em cada oportunidade ' +
      'e nunca muda depois; o `rotulo` é o que a pessoa lê, e esse muda quando quiser. ' +
      'O `funil` diz em qual quadro ela entra: o servidor compõe o valor completo a partir dos ' +
      'dois, e ausência dele é o funil `padrao`.',
    corpo: CriarEtapaSchema,
    resposta: EtapasDoFunilSchema,
    status: 201,
  },
  renomearEtapaDoFunil: {
    metodo: 'PATCH',
    caminho: '/v1/funil/etapas/{valor}',
    resumo: 'Troca o rótulo ou a cor de uma etapa',
    descricao:
      'O `valor` NÃO muda, e é deliberado: ele é o que está gravado em cada oportunidade, e ' +
      'trocá-lo deixaria todas as daquela etapa apontando para algo que não existe mais — elas ' +
      'sumiriam do quadro, sem erro nenhum. Renomear é mexer no que se lê, não no que se guarda.',
    corpo: RenomearEtapaSchema,
    resposta: EtapasDoFunilSchema,
    parametros: [
      { nome: 'valor', em: 'path', descricao: 'O valor da etapa, como NEW ou TEST_DRIVE.' },
    ],
  },
  reordenarEtapasDoFunil: {
    metodo: 'PUT',
    caminho: '/v1/funil/etapas',
    resumo: 'Põe as etapas na ordem dada',
    descricao:
      'Quem não for citado vai para o fim, na ordem em que estava: assim uma lista incompleta ' +
      'reordena o que citou sem sumir com o resto — que seria apagar etapa por omissão.',
    corpo: ReordenarEtapasSchema,
    resposta: EtapasDoFunilSchema,
  },
  removerEtapaDoFunil: {
    metodo: 'DELETE',
    caminho: '/v1/funil/etapas/{valor}',
    resumo: 'Remove uma etapa vazia',
    descricao:
      'Só remove etapa SEM negócios, e a conferência é no motor, não na nossa projeção — que ' +
      'pode estar defasada, e apagar por dado velho é apagar assim mesmo. Com negócios dentro, ' +
      'recusa e diz quantos: apagar a etapa levaria o `valor` junto, e cada oportunidade que ' +
      'estava ali passaria a apontar para uma etapa inexistente. Elas não dão erro — somem do ' +
      'quadro, que é a forma mais cara do "não falha, some".',
    resposta: EtapasDoFunilSchema,
    parametros: [
      { nome: 'valor', em: 'path', descricao: 'O valor da etapa, como NEW ou TEST_DRIVE.' },
    ],
  },
  catalogoDeEtiquetas: {
    metodo: 'GET',
    caminho: '/v1/etiquetas',
    resumo: 'As etiquetas que a organização usa',
    descricao:
      'O catálogo da conta, com a COR de cada etiqueta. A conversa carrega só o título — a ' +
      'cor mora aqui porque, copiada para cada conversa, trocar a cor de uma etiqueta exigiria ' +
      'reprojetar toda conversa que a tem, e até lá a mesma etiqueta sairia de duas cores em ' +
      'linhas vizinhas da mesma fila. Lido do motor a cada chamada: não há projeção, logo não ' +
      'há defasagem a declarar.',
    resposta: CatalogoDeEtiquetasSchema,
  },
  criarEtiqueta: {
    metodo: 'POST',
    caminho: '/v1/etiquetas',
    resumo: 'Cria uma etiqueta no catálogo',
    descricao:
      'Existe porque colar um título inédito numa conversa NÃO o acrescenta ao catálogo — ' +
      'medido contra o motor em 01/09/2026: responde 200 e o catálogo continua igual. A ' +
      'etiqueta passaria a existir só naquela conversa, sem cor e fora de qualquer lista ' +
      'construída do catálogo. Criar é um ato; aplicar é outro. A tela pode encadear os dois ' +
      'num gesto, o contrato não finge que é um. Idempotente por título: repetir devolve a ' +
      'que já existe, sem erro.',
    corpo: CriarEtiquetaSchema,
    resposta: EtiquetaSchema,
    status: 201,
  },
  definirEtiquetasDaConversa: {
    metodo: 'PUT',
    caminho: '/v1/conversations/{id}/etiquetas',
    resumo: 'Substitui as etiquetas de uma conversa',
    descricao:
      'PUT, e não POST, porque a semântica é substituir — que é o que o motor faz, medido. ' +
      'Mandar a lista inteira é obrigação de quem chama: mandar só a nova apagaria as outras, ' +
      'e o sintoma seria etiqueta sumindo sozinha, sem ninguém ligar à ação que a apagou. ' +
      'A resposta traz o que o MOTOR diz que ficou, não o que foi pedido.',
    corpo: DefinirEtiquetasSchema,
    resposta: EtiquetasDaConversaSchema,
    parametros: [
      {
        nome: 'id',
        em: 'path',
        descricao: 'O canônico da conversa (§5.1) — nunca o id da engine.',
      },
    ],
  },
  definirPrioridadeDaConversa: {
    metodo: 'POST',
    caminho: '/v1/conversations/{id}/prioridade',
    resumo: 'Troca a prioridade de uma conversa',
    descricao:
      'A prioridade existia só em leitura: a tela mostrava o degrau e não deixava mudá-lo, ' +
      'porque não havia rota. Quem precisava subir uma conversa para urgente ia ao motor de ' +
      'conversa por fora — e a partir daí o CliqCRM deixava de ser o lugar onde o atendimento ' +
      'acontece. ' +
      '`null` LIMPA a prioridade, e é um valor legítimo: conversa sem degrau é o estado mais ' +
      'comum, e não uma ausência de resposta. ' +
      'A resposta traz o que o MOTOR diz que ficou, relido depois da gravação. Ele aceita e ' +
      'ignora em silêncio — responde 200 sem olhar se salvou —, e sem a releitura a tela ' +
      'pintaria "Urgente" sobre uma conversa que continua sem prioridade. O erro só apareceria ' +
      'no dia em que alguém filtrasse por urgente e não achasse.',
    corpo: DefinirPrioridadeSchema,
    resposta: PrioridadeDaConversaSchema,
    parametros: [
      {
        nome: 'id',
        em: 'path',
        descricao: 'O canônico da conversa (§5.1) — nunca o id da engine.',
      },
    ],
  },
  marcarConversaComoLida: {
    metodo: 'POST',
    caminho: '/v1/conversations/{id}/lida',
    resumo: 'Registra que esta conversa foi vista',
    descricao:
      'O contador de não lidas vem do motor de conversa, e até 02/09/2026 ninguém nunca o ' +
      'avisava de nada: abrir uma conversa com seis mensagens novas, ler todas e sair deixava ' +
      'o seis lá. Contador que não zera é pior que contador nenhum — quem confia nele uma vez ' +
      'e é traído para de olhar para ele. ' +
      'Do lado do motor não há coluna para zerar: o que se move é o carimbo de "visto até ' +
      'aqui", e o não lido é derivado dele. Por isso o comando não recebe corpo — não há o ' +
      'que dizer além de "vi". ' +
      'A resposta traz o que o MOTOR diz que sobrou, relido depois da marcação, e nem sempre ' +
      'é zero: mensagem pode chegar entre uma coisa e outra, e um zero forçado esconderia ' +
      'justamente ela. ' +
      'É `conversa × ler`, e não `editar`: quem pode abrir a conversa pode dizer que a leu. ' +
      'Exigir `editar` faria o contador nunca zerar para quem só lê — que é o defeito de novo, ' +
      'com outra roupa.',
    resposta: ConversaLidaSchema,
    parametros: [
      {
        nome: 'id',
        em: 'path',
        descricao: 'O canônico da conversa (§5.1) — nunca o id da engine.',
      },
    ],
  },
  /*
   * As duas do FUNIL vêm em par com as duas da conversa — mesma operação, e o
   * que muda é por onde se entra. Card sem conversa é o caso comum no começo
   * (lead de portal, cadastro à mão, visita na loja), e endereçar pela
   * conversa faria o botão sumir justo nos cards que mais precisam de dono.
   */
  responsaveisDaOportunidade: {
    metodo: 'GET',
    caminho: '/v1/opportunities/{id}/responsaveis',
    resumo: 'Quem pode receber esta oportunidade, e o que falta a quem não pode',
    descricao:
      'A MESMA lista da conversa, com a mesma dupla conferência — sem agente no motor de ' +
      'conversa a atribuição é aceita e ignorada; sem membro no CRM a oportunidade fica sem ' +
      'dono. Quem não pode aparece com o motivo, em vez de sumir: esconder responderia "não ' +
      'existe" a quem pergunta "cadê o Fulano?". O `atual` aqui compara ' +
      '`responsavel_source_id` — id de membro do CRM —, e não id de agente: são espaços de ' +
      'identificador diferentes, e trocá-los faz a marca nunca acender.',
    resposta: ResponsaveisPossiveisSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  atribuirOportunidade: {
    metodo: 'POST',
    caminho: '/v1/opportunities/{id}/responsavel',
    resumo: 'Passar a oportunidade — e a conversa ligada a ela — para outra pessoa',
    descricao:
      'Move a oportunidade E as conversas ligadas a ela. Se cada porta movesse só o seu, o ' +
      'resultado seria o estado que a transferência existe para impedir: o WhatsApp ' +
      'apontando para um vendedor e o funil para outro, que ninguém percebe até o cliente ' +
      'cobrar resposta de quem já não é dono. A oportunidade vai primeiro, porque é o que ' +
      'quem clicou no card pediu; o que sobra é dito item a item em `moveu`, e `conversas` ' +
      'é número porque uma oportunidade pode ter mais de uma. `usuario_id` nulo desatribui, ' +
      'e aí a conversa FICA.',
    corpo: AtribuirOportunidadeSchema,
    resposta: AtribuicaoDaOportunidadeSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },

  lerOportunidade: {
    metodo: 'GET',
    caminho: '/v1/opportunities/{id}',
    resumo: 'Uma oportunidade, com o mesmo card do quadro',
    descricao:
      'Para abrir o negócio de fora do funil — o Cliente 360 —, sem carregar o quadro inteiro. ' +
      'Devolve o card como o quadro o monta (cliente, conversa, risco, próxima etapa) e as ' +
      'etapas do funil DELA, para a tela dar nome à etapa. Mesma guarda das outras rotas do ' +
      'funil: `oportunidade:ler`, 404 para a de outra organização, e a carteira recorta.',
    resposta: OportunidadeLidaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  consultaDaOportunidade: {
    metodo: 'GET',
    caminho: '/v1/opportunities/{id}/consulta',
    resumo: 'A consulta da oportunidade: médico, especialidade, data e hora',
    descricao:
      'A consulta do paciente nesta oportunidade (#811), com o nome do médico e da ' +
      'especialidade, e o `agendamento` que a integração com a agenda da clínica gravou. Só nas ' +
      'organizações com a funcionalidade "clinica" ligada; sem ela, 404. Sem consulta, os ' +
      'campos e a versão vêm nulos. Lê quem lê a oportunidade, na carteira dele. A especialidade ' +
      'é dado de saúde, e a leitura entra no registro de acesso.',
    resposta: ConsultaDaOportunidadeSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  salvarConsultaDaOportunidade: {
    metodo: 'PUT',
    caminho: '/v1/opportunities/{id}/consulta',
    resumo: 'Escolher médico, especialidade e horário da consulta',
    descricao:
      'Grava a escolha do atendente (#811): médico e especialidade entre as opções ativas, e o ' +
      'horário com fuso. O `agendamento` não muda por aqui. A `versao` é a que a tela leu (nula ' +
      'na primeira vez); gravar sobre outra devolve 409 `CONFLITO_DE_VERSAO`. Grava quem edita ' +
      'a oportunidade, na carteira dele. Só com a funcionalidade "clinica" ligada; sem ela, 404.',
    corpo: SalvarConsultaDaOportunidadeSchema,
    resposta: ConsultaDaOportunidadeSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  opcoesDaConsulta: {
    metodo: 'GET',
    caminho: '/v1/consultas/opcoes',
    resumo: 'Os médicos e as especialidades para escolher na consulta',
    descricao:
      'As opções ativas, mantidas pela agenda da clínica (#811): não há cadastro manual. Só nas ' +
      'organizações com a funcionalidade "clinica" ligada; sem ela, 404. Lê quem lê oportunidade.',
    resposta: OpcoesDaConsultaSchema,
  },
  sincronizarOpcoesDaConsulta: {
    metodo: 'POST',
    caminho: '/v1/consultas/opcoes/sincronizar',
    resumo: 'Puxa da agenda da clínica a lista de médicos ou especialidades e a aplica',
    descricao:
      'Lê a lista de um tipo de opção (médico ou especialidade) de uma operação de leitura do ' +
      'serviço de automações (`operacao_id`, que devolve `{ codigo, nome }`) e a aplica: cria o ' +
      'novo, renomeia o que mudou e inativa o que não veio. É a única forma de as opções mudarem ' +
      '(não há cadastro manual). O `operacao_id` tem de ser o da agenda da organização para o ' +
      'tipo (`medicos` ou `especialidades`, ADR-0028): outro é 404, e sem a agenda ligada, 409 ' +
      '`CONFLITO_DE_ESTADO`. Operação de integração de dados (Cliqfy); só com a ' +
      'funcionalidade "clinica" ligada, senão 404. Devolve só contagens, nunca nomes.',
    corpo: SincronizarOpcoesDaConsultaSchema,
    resposta: ResultadoDaSincronizacaoDeOpcoesSchema,
  },
  listarPrecosDaClinica: {
    metodo: 'GET',
    caminho: '/v1/consultas/precos',
    resumo: 'Os preços particulares da clínica: consultas por médico, exames e procedimentos',
    descricao:
      'Os preços ativos, por tipo e nome, de 20 em 20, com o total (#1084, #1085). Copiados do sistema da clínica pela ' +
      'rotina da madrugada ou pelo botão, sem cadastro manual. A busca casa o nome do ' +
      'procedimento, o código ou o nome do médico. Lê quem lê cliente. Só nas organizações ' +
      'com a funcionalidade "clinica" ligada; sem ela, 404.',
    resposta: ListaDePrecosSchema,
    parametros: [
      {
        nome: 'tipo',
        em: 'query',
        descricao: 'consulta, exame ou procedimento. Sem ele, todos.',
        obrigatorio: false,
      },
      {
        nome: 'busca',
        em: 'query',
        descricao: 'Parte do nome, do código ou do nome do médico, até 100 caracteres.',
        obrigatorio: false,
      },
      {
        nome: 'pagina',
        em: 'query',
        descricao: 'A página, a partir de 1 (padrão 1, até 10000). Cada uma tem 20 preços.',
        obrigatorio: false,
      },
    ],
  },
  precoDaConsultaDoMedico: {
    metodo: 'GET',
    caminho: '/v1/consultas/medicos/{id}/preco',
    resumo: 'O preço particular da consulta com um médico',
    descricao:
      'O preço da consulta do médico, ou o padrão da clínica quando ele não tem preço próprio ' +
      '(#1100). É o valor que o agendamento mostra ao escolher o médico. Sem nenhum dos dois, ' +
      '`preco: null`. Médico que não é da organização é 404. Lê quem lê cliente; só com a ' +
      'funcionalidade "clinica" ligada, senão 404.',
    resposta: PrecoDaConsultaDoMedicoSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id do médico no CliqCRM.' }],
  },
  sincronizarPrecosDaClinica: {
    metodo: 'POST',
    caminho: '/v1/consultas/precos/sincronizar',
    resumo: 'Atualiza agora os preços da clínica a partir do sistema dela',
    descricao:
      'Roda as operações `precos_de_consulta` e `precos_de_procedimentos` do mapa da agenda ' +
      '(ADR-0028) e aplica cada lista: cria o novo, atualiza o que mudou e inativa o que não ' +
      'veio, cada grupo separado (#1084). Grupo não ligado volta `null`; nenhum ligado é 409 ' +
      '`CONFLITO_DE_ESTADO`. Lista vazia ou cortada não muda nada (409). É o botão "Atualizar ' +
      'preços agora"; a rotina da madrugada faz o mesmo. Quem edita a configuração da ' +
      'organização. Só com a funcionalidade "clinica" ligada, senão 404. Devolve só contagens.',
    resposta: ResultadoDaSincronizacaoDePrecosSchema,
  },
  agendarConsulta: {
    metodo: 'POST',
    caminho: '/v1/opportunities/{id}/consulta/agendar',
    resumo: 'Agenda a consulta no SISAC (preenche a vaga e grava os códigos)',
    descricao:
      'Roda a operação de escrita que preenche a vaga na agenda da clínica (`operacao_id`, com ' +
      '`parametros` por nome) e, se preencheu, grava o código do paciente e o da agenda na ' +
      'consulta. O `operacao_id` tem de ser o da finalidade `agendar` da agenda da organização ' +
      '(ADR-0028): outro é 404, e sem a agenda ligada, 409 `CONFLITO_DE_ESTADO`. Faltando um ' +
      'campo que a clínica exige (especialidade, local, origem, nascimento, CEP, convênio, plano, ' +
      'tipo, WhatsApp, celular, e o paciente pelo código ou pelo nome completo) → **400** com o ' +
      'campo em `errors`, e nada vai ao SISAC. Vaga já tomada → ' +
      '**409** `CONFLITO_DE_VERSAO`, e nada é gravado. Exige `Idempotency-Key`. Grava quem edita a ' +
      'oportunidade, na carteira dele. Só com a funcionalidade "clinica" ligada; sem ela, 404.',
    corpo: AgendarConsultaSchema,
    resposta: ConsultaDaOportunidadeSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  agendamentoDaConsulta: {
    metodo: 'GET',
    caminho: '/v1/opportunities/{id}/consulta/agendamento',
    resumo: 'O que está gravado na vaga agendada, para o "Editar" começar com isso',
    descricao:
      'Roda a parte `ler_agendamento` da agenda (#1187) com a vaga que o CRM gravou ao agendar, ' +
      'nunca uma da tela, e devolve só as colunas do agendar (convênio, plano, local, origem, ' +
      'endereço…), sem nome nem CPF. Sem a parte ligada, `dados` nulo e `sisac: "desligada"`; ' +
      'sistema da clínica fora, `sisac: "fora"`. Sem agendamento, 409. Entra no registro de ' +
      'acesso do cliente. Lê quem edita a oportunidade, na carteira dele. Só com a ' +
      'funcionalidade "clinica" ligada, senão 404.',
    resposta: AgendamentoDaConsultaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  remarcarConsulta: {
    metodo: 'POST',
    caminho: '/v1/opportunities/{id}/consulta/remarcar',
    resumo: 'Atualiza a consulta marcada no SISAC: os dados e, se mudou, o horário',
    descricao:
      'Roda a parte `remarcar` da agenda (#1187) com os campos do agendar, a vaga nova e a de ' +
      'antes, que sai da consulta gravada. Numa escrita só, a operação passa o paciente para a ' +
      'vaga nova e devolve a antiga; a mesma vaga só atualiza os dados. Vaga nova já tomada, ou ' +
      'a de antes que não é mais do paciente: **409** `CONFLITO_DE_VERSAO`, e nada muda. A ' +
      '`versao` é a da consulta lida: outra pessoa mudou no meio, 409. Só com paciente ' +
      'vinculado (sem cadastro, o SISAC não teria como conferir a vaga de antes). Exige ' +
      '`Idempotency-Key`. Grava quem edita a oportunidade, na carteira dele. Só com a ' +
      'funcionalidade "clinica" ligada, senão 404.',
    corpo: RemarcarConsultaSchema,
    resposta: ConsultaDaOportunidadeSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  conferirConsulta: {
    metodo: 'POST',
    caminho: '/v1/opportunities/{id}/consulta/conferir',
    resumo: 'Confere no SISAC se a consulta marcada ainda está no horário',
    descricao:
      'Roda a parte `ler_agendamento` da agenda (#1187) com o horário que o CRM gravou. Se o ' +
      'horário não tem mais o paciente da consulta (a recepção o tirou direto no SISAC), o CRM ' +
      'tira o agendamento da consulta, com médico e especialidade, e responde ' +
      '`situacao: "desmarcada"`; nada é escrito no SISAC. Sem agendamento, `sem_agendamento`; ' +
      'sem a parte ligada, `desligada`; sistema da clínica fora, `nao_conferida`, e nada muda. ' +
      'Não devolve dado do paciente. Lê quem lê a oportunidade, na carteira dele. Só com a ' +
      'funcionalidade "clinica" ligada, senão 404.',
    resposta: ConferenciaDaConsultaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  cancelarConsulta: {
    metodo: 'POST',
    caminho: '/v1/opportunities/{id}/consulta/cancelar',
    resumo: 'Cancela o agendamento da consulta no SISAC (devolve a vaga, com o motivo)',
    descricao:
      'Roda a operação de escrita da parte `liberar_vaga` da agenda da organização (ADR-0028) ' +
      'com a vaga e o paciente que o CRM gravou ao agendar (a operação tem de declarar ' +
      '`cod_paciente` e `id_motivo`; agendado sem cadastro não se cancela por aqui, **409**), o ' +
      '`motivo` (código da lista ' +
      '`motivos_de_cancelamento`), a `fila_de_espera` e a `observacao`. Se o SISAC liberou, a ' +
      'consulta fica sem agendamento (o médico e a especialidade ficam). A `versao` é a da ' +
      'consulta lida: outra pessoa mudou no meio → **409** `CONFLITO_DE_VERSAO`. Consulta sem ' +
      'agendamento, ou o SISAC sem a vaga com este paciente → **409** `CONFLITO_DE_ESTADO`, e ' +
      'nada muda no CRM. SISAC fora → 503, e nada foi cancelado. Exige `Idempotency-Key`. ' +
      'Cancela quem edita a oportunidade, na carteira dele. Só com a funcionalidade "clinica" ' +
      'ligada; sem ela, 404.',
    corpo: CancelarConsultaSchema,
    resposta: ConsultaDaOportunidadeSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  pacienteVinculadoDaConsulta: {
    metodo: 'GET',
    caminho: '/v1/opportunities/{id}/consulta/paciente',
    resumo: 'O paciente do SISAC vinculado ao cliente da oportunidade',
    descricao:
      'Para a aba Consulta não pedir o CPF (#1024): o código sai do vínculo do cliente da ' +
      'oportunidade, no servidor, e o nome e o fim do CPF são lidos pela parte `ler_paciente` ' +
      'da agenda (ADR-0028). Do CPF, só os dois últimos dígitos. Sem vínculo, `paciente` nulo; ' +
      'SISAC fora, só o código, com `sisac: "fora"`. Entra no registro de acesso do cliente. Lê ' +
      'quem lê a oportunidade, na carteira dele. Só com a funcionalidade "clinica" ligada, senão 404.',
    resposta: PacienteVinculadoDaConsultaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  cadastrarPacienteNaAgenda: {
    metodo: 'POST',
    caminho: '/v1/opportunities/{id}/consulta/paciente',
    resumo: 'Cadastra o paciente da oportunidade na agenda da clínica (SISAC)',
    descricao:
      'Roda a operação de escrita que cadastra o paciente na agenda da clínica (`operacao_id`, ' +
      'com `parametros` por nome) e devolve só o código do paciente, nunca as linhas do SISAC. ' +
      'O `operacao_id` tem de ser o da finalidade `cadastrar_paciente` da agenda da organização ' +
      '(ADR-0028): outro é 404, e sem essa parte da agenda ligada, 409 `CONFLITO_DE_ESTADO`. ' +
      'Paciente já cadastrado: 409 `CONFLITO_DE_ESTADO`. Exige `Idempotency-Key`. O envio entra ' +
      'no registro de acesso do cliente da oportunidade, com os nomes dos campos. Grava quem ' +
      'edita a oportunidade, na carteira dele. Só com a funcionalidade "clinica" ligada; sem ' +
      'ela, 404.',
    corpo: CadastrarPacienteNaAgendaSchema,
    resposta: PacienteCadastradoNaAgendaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  agendaDaClinica: {
    metodo: 'GET',
    caminho: '/v1/admin/clinica/agenda',
    resumo: 'Que operação do serviço atende cada parte da agenda da clínica',
    descricao:
      'O mapa da organização (ADR-0028): cada parte da agenda (buscar paciente, vagas, agendar…) ' +
      '→ a operação publicada no serviço de automações. Parte ausente é parte não ligada. É o ' +
      'mesmo mapa que a tela recebe em `/v1/me`. Só a Cliqfy (`integracao_de_dados` × ' +
      '`editar`); só com a funcionalidade "clinica" ligada, senão 404.',
    resposta: AgendaDaClinicaSchema,
  },
  gravarAgendaDaClinica: {
    metodo: 'PUT',
    caminho: '/v1/admin/clinica/agenda',
    resumo: 'Liga as partes da agenda da clínica às operações do serviço',
    descricao:
      'Troca o mapa inteiro da organização (ADR-0028). Cada operação é conferida no serviço ' +
      'antes de gravar: existe, está publicada (a escrita, aprovada), é de leitura ou de ' +
      'escrita conforme a parte, declara o número de parâmetros da parte, e todas usam a mesma ' +
      'conexão. O que não passa volta em `errors`, por parte, e nada é gravado. Só a Cliqfy ' +
      '(`integracao_de_dados` × `editar`); só com a funcionalidade "clinica" ligada, senão 404.',
    corpo: AgendaDaClinicaSchema,
    resposta: AgendaDaClinicaSchema,
  },
  listaDaAgendaDaClinica: {
    metodo: 'POST',
    caminho: '/v1/admin/clinica/agenda/lista',
    resumo: 'Uma lista da clínica, para escolher os padrões do agendamento',
    descricao:
      'Roda a operação que o mapa da organização liga à lista (locais, origens, convênios ou ' +
      'os planos de um convênio) e devolve só código e nome: são cadastros da clínica, sem dado ' +
      'de paciente (#1187). É o que alimenta os padrões da tela "Agenda da clínica", sem ' +
      'oportunidade aberta. Lista sem operação no mapa: 409 `CONFLITO_DE_ESTADO`, e a tela pede ' +
      'o código. Sistema da clínica fora: 503. Só a Cliqfy (`integracao_de_dados` × `editar`); ' +
      'só com a funcionalidade "clinica" ligada, senão 404.',
    corpo: PedidoDeListaDaAgendaSchema,
    resposta: ListaDaAgendaSchema,
  },
  consultarSisac: {
    metodo: 'POST',
    caminho: '/v1/consultas/leitura',
    resumo: 'Roda uma leitura da agenda da clínica (vagas, médicos, listas do formulário)',
    descricao:
      'Executa, pelo serviço de automações, uma operação de **leitura** da agenda da clínica ' +
      'a partir de uma oportunidade (`oportunidade_id`, `operacao_id` e `parametros`): vagas, ' +
      'médicos, especialidades e as listas do formulário do agendamento (convênios, planos, ' +
      'origens, locais). As buscas e a leitura do paciente NÃO entram aqui (#1024): rodam pelas ' +
      'rotas do paciente, com o código tirado do vínculo. As linhas saem projetadas: as vagas ' +
      'com `id_agenda` e `data_hora`, as listas com `codigo` e `nome`, nunca a linha inteira. ' +
      'O `operacao_id` tem de ' +
      'ser o de uma dessas finalidades na agenda da organização (ADR-0028): outro é 404, e ' +
      'sem essa parte da agenda ligada, 409 `CONFLITO_DE_ESTADO`. Lê quem lê a oportunidade, na carteira dele; ' +
      'oportunidade de outra organização ou fora da carteira é 404. Só com a funcionalidade ' +
      '"clinica" ligada, senão 404.',
    corpo: ConsultaLeituraSchema,
    resposta: ResultadoDaExecucaoSchema,
  },
  notasDaOportunidade: {
    metodo: 'GET',
    caminho: '/v1/opportunities/{id}/notas',
    resumo: 'As notas escritas na oportunidade, da mais recente para a mais antiga',
    descricao:
      'O que foi combinado na negociação, e que não cabe em campo nenhum: "topa 48 mil à ' +
      'vista, mas só depois do dia 10". Vem com o NOME de quem escreveu, porque quem lê ' +
      'quase nunca é quem escreveu — é o gestor que assume a carteira ou o colega que cobre ' +
      'as férias, e um id ali obrigaria a tela a buscar a pessoa por nota. ' +
      'Mora em tabela nossa (migração 044), com RLS por organização.',
    resposta: NotasDaOportunidadeSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  criarNotaDaOportunidade: {
    metodo: 'POST',
    caminho: '/v1/opportunities/{id}/notas',
    resumo: 'Escrever uma nota na oportunidade',
    descricao:
      'ACRESCENTA, e só: não existe rota que edite nem que apague uma nota. Nota que pode ser ' +
      'reescrita deixa de ser registro do que foi dito no dia — corrigir é escrever outra. ' +
      'As únicas escritas que mudam uma nota são a anonimização e a eliminação do titular. ' +
      'Mesma permissão de editar a oportunidade: quem pode mudar o valor do negócio pode ' +
      'dizer o que foi combinado nele. ' +
      'Exige `Idempotency-Key` (§6.1): sem ela o segundo clique grava a mesma nota duas ' +
      'vezes, e como não há como apagar, a duplicata fica.',
    corpo: CriarNotaDaOportunidadeSchema,
    resposta: NotaDaOportunidadeSchema,
    status: 201,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' }],
  },
  editarNotaDaOportunidade: {
    metodo: 'PATCH',
    caminho: '/v1/opportunities/{id}/notas/{notaId}',
    resumo: 'Corrigir o texto de uma nota',
    descricao:
      'Existe para o erro de digitação: sem ela, corrigir uma palavra obrigaria a escrever ' +
      'outra nota, e a tabela do card viraria uma conversa com ela mesma. ' +
      'A nota corrigida passa a mostrar `editada_em`, e a tela marca a linha — sem isso o ' +
      'texto reescrito continuaria parecendo registro do que foi dito no dia. O que havia ' +
      'antes fica na trilha de auditoria, que guarda antes e depois. ' +
      'Mesma permissão de editar a oportunidade, e não só o autor: a correção serve ao gestor ' +
      'que arruma a nota de quem saiu de férias.',
    corpo: EditarNotaDaOportunidadeSchema,
    resposta: NotaDaOportunidadeSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id canônico da oportunidade.' },
      { nome: 'notaId', em: 'path', descricao: 'Id da nota a corrigir.' },
    ],
  },
  mencoesParaMim: {
    metodo: 'GET',
    caminho: '/v1/notificacoes/mencoes',
    resumo: 'Menções para mim',
    descricao:
      'As menções em nota interna dos últimos 30 dias (#569), vistas ou não, com as não lidas ' +
      'primeiro — para voltar a um chamado depois que o card sumiu. Só as da própria pessoa, ' +
      'com o escopo de caixa reavaliado.',
    resposta: MencoesParaMimSchema,
  },
  mencionaveis: {
    metodo: 'GET',
    caminho: '/v1/conversations/{id}/mencionaveis',
    resumo: 'Quem pode ser mencionado numa nota interna desta conversa',
    descricao:
      'As pessoas ativas da organização com acesso ao atendimento, para a lista do "@" no editor ' +
      'da nota interna (#569). Quem não vê a conversa vem marcado (`pode_ver: false`) e com o ' +
      'motivo, para a tela avisar antes do envio. Quem pergunta não aparece. Conversa de outra ' +
      'organização é 404.',
    resposta: MencionaveisSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id canônico da conversa.' }],
  },
  responsaveisPossiveis: {
    metodo: 'GET',
    caminho: '/v1/conversations/{id}/responsaveis',
    resumo: 'Para quem esta conversa pode ir',
    descricao:
      'Membros do CliqCRM, e não agentes do motor: "vendedor" é conceito daqui, com papel e ' +
      'carteira, e é isso que decide o que ele enxerga depois de receber. A lista inclui quem ' +
      'NÃO pode receber, com o motivo — esconder responderia "não existe" a quem pergunta ' +
      '"cadê o Fulano?", e a resposta certa é "existe, e falta isto". Recebe quem tem reflexo ' +
      'nas duas engines: sem agente no motor de conversa a atribuição é aceita e ignorada, e ' +
      'sem membro no CRM o cliente e a oportunidade não têm para onde ir.',
    resposta: ResponsaveisPossiveisSchema,
    parametros: [
      {
        nome: 'id',
        em: 'path',
        descricao: 'O canônico da conversa (§5.1) — nunca o id da engine.',
      },
    ],
  },
  transferirConversa: {
    metodo: 'POST',
    caminho: '/v1/conversations/{id}/responsavel',
    resumo: 'Passa a conversa para outro vendedor',
    descricao:
      'Move TRÊS coisas: a conversa, o cliente e a oportunidade. É tudo ou nada — se qualquer ' +
      'uma não puder ir, nenhuma vai, e a conferência acontece antes da primeira escrita. ' +
      'Meia transferência é pior que nenhuma: o WhatsApp apontando para um vendedor e o funil ' +
      'para outro é o estado que ninguém percebe até o cliente cobrar resposta de quem já não ' +
      'é dono. `usuario_id` nulo desatribui, e nesse caso o cliente e a oportunidade FICAM: ' +
      '"ninguém está atendendo agora" é diferente de "este cliente não é de ninguém". A ' +
      'atribuição é conferida relendo do motor, porque um `assignee_id` que ele não aceita ' +
      'devolve 200 e não faz nada (A02 §4.4). Exige Idempotency-Key.',
    corpo: TransferirConversaSchema,
    resposta: ConversaTransferidaSchema,
    parametros: [
      {
        nome: 'id',
        em: 'path',
        descricao: 'O canônico da conversa (§5.1) — nunca o id da engine.',
      },
      {
        nome: 'Idempotency-Key',
        em: 'header',
        obrigatorio: true,
        descricao: 'Transferir duas vezes não pode registrar duas transferências.',
      },
    ],
  },
  comporConversa: {
    metodo: 'GET',
    caminho: '/v1/conversations/{id}',
    resumo: 'Composição da tela de atendimento',
    descricao:
      'Uma requisição pinta as três colunas da §11.2: a conversa, as mensagens ' +
      '(paginadas por cursor, das mais novas para as mais antigas) e o contexto ' +
      'comercial — vínculo, cliente e oportunidades — com as capabilities do canal. ' +
      'A composição no BFF é o que impede regra de integração de se espalhar pelo ' +
      'navegador (§11). Blocos de projeção levam projetado_em e a conversa leva stale ' +
      '(§14.3): leitura DEGRADA marcando defasagem — quem bloqueia com engine fora é ' +
      'comando, nunca leitura. Nenhum nome de campo de engine na resposta (§3.2).',
    resposta: ConversaCompostaSchema,
    parametros: [
      {
        nome: 'id',
        em: 'path',
        obrigatorio: true,
        descricao: 'Id canônico da conversa (§5.1) — nunca o id do Chatwoot.',
      },
      {
        nome: 'cursor',
        em: 'query',
        descricao:
          'Cursor devolvido em mensagens.pagina.cursor_anterior — busca a página de ' +
          'mensagens mais antigas. Ausente = a página mais recente.',
      },
    ],
  },
  anexoDaConversa: {
    metodo: 'GET',
    caminho: '/v1/anexos/chatwoot/{token}',
    resumo: 'Serve o arquivo anexado a uma mensagem',
    descricao:
      'A única rota do produto sem sessão, e é deliberado: quem a chama é o `<audio>` ou o ' +
      '`<img>` do navegador, e nenhum dos dois manda `Authorization`. Quem autoriza é a ' +
      'ASSINATURA da URL — HMAC sobre `tenant.anexo.prazo`, válida por cinco minutos —, e é o ' +
      'tenant dela que abre o contexto de RLS. O link é emitido por `GET /v1/conversations/{id}`, ' +
      'que passa pela matriz: quem não pode ler a conversa nunca o recebe. A API PROXIA os ' +
      'bytes em vez de redirecionar: a URL do motor é pública e não expira (A02 §4.2), e ' +
      'redirecionar entregaria ao navegador um endereço permanente para o documento do ' +
      'cliente. Expirado ou adulterado responde 404 — não 401 —, porque distinguir os dois ' +
      'confirmaria a quem sonda que o anexo existe.',
    // O corpo são bytes. A schema existe porque o tipo `Rota` a exige; quem
    // descreve a resposta de verdade é `conteudoDaResposta`.
    resposta: z.string(),
    conteudoDaResposta: 'application/octet-stream',
    autenticada: false,
    parametros: [
      {
        nome: 'token',
        em: 'path',
        descricao: 'A assinatura opaca emitida na composição da conversa. Vale cinco minutos.',
      },
    ],
  },
  enviarMensagem: {
    metodo: 'POST',
    caminho: '/v1/conversations/{id}/messages',
    resumo: 'Envia mensagem numa conversa',
    descricao:
      'O fluxo da §10.2, com as cinco autorizações: tenant (RLS), usuário (papel e ' +
      'escopo de inbox), canal, janela de atendimento e conteúdo. Fora da janela sem ' +
      'template aprovado recusa com FORA_DA_JANELA — verificar antes é o que impede a ' +
      'mensagem de sumir no provedor sem explicação. Exige Idempotency-Key: vendedor ' +
      'clicando duas vezes é rotina, e sem a chave o cliente recebe duas mensagens. ' +
      'Chatwoot fora do ar responde 503, nunca 202 (§14.3: bloquear envio, não simular ' +
      'sucesso). O id do caminho e o da resposta são CANÔNICOS (§3.2).',
    corpo: EnviarMensagemSchema,
    resposta: MensagemEnviadaSchema,
    status: 201,
    parametros: [
      {
        nome: 'id',
        em: 'path',
        obrigatorio: true,
        descricao: 'Id canônico da conversa (§5.1) — nunca o id do Chatwoot.',
      },
      {
        nome: 'Idempotency-Key',
        em: 'header',
        obrigatorio: true,
        descricao:
          'Valor único por comando (um UUID serve). Repetir o mesmo valor devolve a ' +
          'resposta original em vez de enviar de novo; a mesma chave com corpo diferente ' +
          'é recusada com 409.',
      },
    ],
  },
  /* ── Administração de pessoas, times e unidades (D12) ─────────────────── */

  listarUnidades: {
    metodo: 'GET',
    caminho: '/v1/admin/unidades',
    resumo: 'Unidades da organização',
    descricao:
      'Traz inativas também, com o `status` dizendo qual é qual. Esconder a inativa faria ' +
      'sumir a unidade que ainda aparece no histórico de todo mundo que trabalhou nela.',
    resposta: UnidadesSchema,
  },
  criarUnidade: {
    metodo: 'POST',
    caminho: '/v1/admin/unidades',
    resumo: 'Cria uma unidade',
    descricao:
      'Nome repetido na mesma organização é recusado com 409: duas lojas com o mesmo nome é ' +
      'erro de digitação, não caso de uso. Entre organizações diferentes é normal.',
    corpo: CriarUnidadeSchema,
    resposta: UnidadeSchema,
    status: 201,
  },
  alterarUnidade: {
    metodo: 'PATCH',
    caminho: '/v1/admin/unidades/{id}',
    resumo: 'Renomeia ou desativa uma unidade',
    descricao:
      'Não existe apagar. Desativar é reversível e preserva a origem do histórico — a ' +
      'mesma separação da decisão B2, e o banco a impõe: `cliqcrm_app` não tem DELETE aqui.',
    corpo: AlterarUnidadeSchema,
    resposta: UnidadeSchema,
    parametros: [{ nome: 'id', em: 'path', obrigatorio: true, descricao: 'Id da unidade.' }],
  },
  listarTimes: {
    metodo: 'GET',
    caminho: '/v1/admin/times',
    resumo: 'Times da organização',
    descricao:
      'Cada time vem com a unidade a que pertence, quando pertence a alguma: organização de uma ' +
      'loja só não precisa inventar uma unidade para ter um time.',
    resposta: TimesSchema,
  },
  criarTime: {
    metodo: 'POST',
    caminho: '/v1/admin/times',
    resumo: 'Cria um time',
    corpo: CriarTimeSchema,
    resposta: TimeSchema,
    status: 201,
  },
  alterarTime: {
    metodo: 'PATCH',
    caminho: '/v1/admin/times/{id}',
    resumo: 'Renomeia, remaneja ou desativa um time',
    descricao: '`unidadeId: null` explícito tira o time da unidade; o campo ausente não mexe nela.',
    corpo: AlterarTimeSchema,
    resposta: TimeSchema,
    parametros: [{ nome: 'id', em: 'path', obrigatorio: true, descricao: 'Id do time.' }],
  },
  listarPessoas: {
    metodo: 'GET',
    caminho: '/v1/admin/pessoas',
    resumo: 'Pessoas da organização, com papel e escopo',
    descricao:
      'Inclui quem está `provisionando` e quem falhou ao provisionar. Mostrar só quem ficou ' +
      'pronto esconderia exatamente o caso que precisa de ação.',
    resposta: PessoasSchema,
  },
  convidarPessoa: {
    metodo: 'POST',
    caminho: '/v1/admin/pessoas',
    resumo: 'Convida uma pessoa para a organização',
    descricao:
      'Devolve 202 com a pessoa em `provisionando`: criar toca Keycloak e Chatwoot, ' +
      'e a §3.2 proíbe transação distribuída — a saga da B28 conclui depois. Falha parcial ' +
      'compensa ou escala; nunca deixa meio-criado. E-mail que já tem vínculo ATIVO nesta ' +
      'organização é recusado com 409; a mesma pessoa em outra organização é normal e não conflita. ' +
      'A resposta diz em `primeiroAcesso` como a pessoa entra: por e-mail com link que expira, ' +
      'ou — num ambiente sem remetente — com uma senha provisória que se mostra UMA vez. ' +
      'O convite que falhou se refaz por aqui, como o de quem foi desativado. Com a ' +
      'organização no limite de usuários contratados (#768), o convite é recusado com ' +
      '`CONFIRMACAO_EXIGIDA` e a extensão `limite`: o administrador confirma com ' +
      '`contratarUsuarioAdicional`, e o contrato sobe 1 junto com o convite. Sem poder ' +
      'contratar (o valor do adicional não definido, quem não é o administrador, ou a ' +
      'organização acima do limite), a recusa é 409 `CONFLITO_DE_ESTADO`, com a mesma extensão.',
    corpo: ConvidarPessoaSchema,
    resposta: PessoaConvidadaSchema,
    status: 202,
  },
  alterarPessoa: {
    metodo: 'PATCH',
    caminho: '/v1/admin/pessoas/{id}',
    resumo: 'Muda papel, escopo ou lotação',
    descricao:
      'Mudar papel repercute no Keycloak e no time do Chatwoot. Time que pertence a outra ' +
      'unidade é recusado: o vínculo incoerente responderia coisas diferentes conforme o ' +
      'nível da carteira consultado.',
    corpo: AlterarPessoaSchema,
    resposta: PessoaSchema,
    parametros: [{ nome: 'id', em: 'path', obrigatorio: true, descricao: 'Id do vínculo.' }],
  },
  desativarPessoa: {
    metodo: 'POST',
    caminho: '/v1/admin/pessoas/{id}/desativar',
    resumo: 'Tira o acesso de alguém, com justificativa',
    descricao:
      'A resposta diz o que aconteceu em CADA engine. Falha em qualquer uma é incidente, ' +
      'não pendência: `alerta: true` significa que a pessoa pode ainda ter acesso a algo. ' +
      'As conversas não mudam de dono — elas ficam no nome de quem atendeu, e o dono só ' +
      'troca na próxima interação (decisão de 24/08/2026).',
    corpo: DesativarPessoaSchema,
    resposta: DesativacaoSchema,
    parametros: [{ nome: 'id', em: 'path', obrigatorio: true, descricao: 'Id do vínculo.' }],
  },
  reativarPessoa: {
    metodo: 'POST',
    caminho: '/v1/admin/pessoas/{id}/reativar',
    resumo: 'Devolve o acesso a quem foi desativado',
    descricao:
      'Só para quem foi desativado e tem a conta no login: a pessoa cujo convite não terminou ' +
      'se refaz pelo convite (409 aqui). No limite de usuários contratados (#768), como no ' +
      'convite: `CONFIRMACAO_EXIGIDA` com a extensão `limite`, e a confirmação vem em ' +
      '`contratarUsuarioAdicional`. Sem corpo, reativa como sempre, se houver vaga.',
    corpo: ReativarPessoaSchema,
    corpoOpcional: true,
    resposta: DesativacaoSchema,
    parametros: [{ nome: 'id', em: 'path', obrigatorio: true, descricao: 'Id do vínculo.' }],
  },
  reprovisionarPessoa: {
    metodo: 'POST',
    caminho: '/v1/admin/pessoas/{id}/reprovisionar',
    resumo: 'Completa nas engines quem já entrou',
    descricao:
      'A segunda chance do convite. Ele toca as engines uma vez, no instante em que a pessoa ' +
      'é admitida, e o que ficou para trás ali ficava para sempre — sendo que os dois casos ' +
      'que fica é o normal: o Chatwoot só é tocado se a organização já tiver ligado a ' +
      'integração, o que costuma vir depois de a equipe estar montada. Refaz os passos das ' +
      'DUAS engines e devolve o que aconteceu em cada uma; `dispensado` não é falha. Não é ' +
      'saga, ao contrário do convite: sucesso parcial aqui é progresso, e compensar apagaria ' +
      'o agente recém-criado. A identidade fica de fora de propósito — quem está `ativo` já ' +
      'a tem, e mexer nela traria junto a compensação que desativa quem está trabalhando. ' +
      'Idempotente: quem já está completo volta `dispensado` nas duas.',
    resposta: ReprovisionamentoSchema,
    parametros: [{ nome: 'id', em: 'path', obrigatorio: true, descricao: 'Id do vínculo.' }],
  },
  limitesDaOrganizacao: {
    metodo: 'GET',
    caminho: '/v1/admin/limites',
    resumo: 'Os limites do contrato da organização',
    descricao:
      'Para a tela de Pessoas (#768): os usuários contratados, os em uso (vínculos ativos e ' +
      'convites pendentes, fora as contas técnicas) e o valor do usuário adicional deste ' +
      'cliente. `usuarios` é nulo quando a organização não tem contrato, e aí não há limite. ' +
      '`pode_contratar` diz se quem chama contrata pelo convite ou pela reativação. Só o ' +
      'administrador da organização e o Super Admin, quem contrata ou faz a gestão (decisão de ' +
      '28/10/2026): os outros papéis recebem 403.',
    resposta: LimitesDaOrganizacaoSchema,
  },
  contratarArmazenamento: {
    metodo: 'POST',
    caminho: '/v1/admin/armazenamento/contratar',
    resumo: 'Contrata mais um bloco de armazenamento',
    descricao:
      'Para a tela de Pessoas (#718): o contrato sobe `bloco_gb` na hora, e a resposta são os ' +
      'limites já com ele. `valor_aceito` é o `valor_do_bloco` que a pessoa viu; sem ele, ou ' +
      'se o valor mudou, `CONFIRMACAO_EXIGIDA` com a extensão `armazenamento_adicional` e o ' +
      'valor de hoje, e o mesmo pedido com ele é a segunda tentativa. Sem o valor do GB ' +
      'adicional no contrato, ou para quem não contrata por aqui (o Super Admin muda o ' +
      'contrato pela gestão), `CONFLITO_DE_ESTADO` com a mesma extensão. Sem contrato, ' +
      '`CONFLITO_DE_ESTADO`. A trilha da organização guarda quem aceitou, quando e o valor; a ' +
      'cobrança é fora do sistema. Só o administrador da organização e o Super Admin chegam ' +
      'aqui: os outros papéis recebem 403.',
    corpo: ContratarArmazenamentoSchema,
    resposta: LimitesDaOrganizacaoSchema,
  },
  redefinirSenhaDaPessoa: {
    metodo: 'POST',
    caminho: '/v1/admin/pessoas/{id}/redefinir-senha',
    resumo: 'Redefine a senha de alguém da equipe, com justificativa',
    descricao:
      'Vetor de sequestro de conta (ADR-0033): exige justificativa, step-up ' +
      '(`usuario.redefinir_senha`, com o id do vínculo como alvo; sem concessão, 403 com a ' +
      'extensão `step_up`) e gera `audit_logs` antes e depois da troca, nunca com a senha. A ' +
      'senha atual deixa de valer na hora, as sessões abertas da pessoa são encerradas ' +
      '(`sessoesEncerradas: false` quando o login não confirmou) e o bloqueio por tentativas é ' +
      'liberado. A resposta diz como ela entra de novo: link por e-mail, ou, sem remetente, uma ' +
      'senha provisória que se mostra UMA vez e que o login obriga a trocar. Só para agente, ' +
      'vendedor, gestor e administrador com vínculo `ativo`, nunca a própria senha. A conta do ' +
      'login é da pessoa e serve a todas as organizações dela: quem tem vínculo em outra ' +
      'organização (em qualquer situação, encerrada inclusive), o Super Admin e a operação da ' +
      'Cliqfy recebem a mesma recusa 403, sem extensão, que fica na trilha; o Super Admin passa ' +
      'pela recusa de outra organização. Vínculo de outra organização, inexistente ou id ' +
      'malformado: 404. Conta que o login não tem mais, ou senha recusada pela política: 409. ' +
      'Login fora do ar: 503, e cada nova tentativa gera uma senha nova. A resposta não vai para ' +
      'cache.',
    corpo: RedefinirSenhaSchema,
    resposta: RedefinicaoDeSenhaSchema,
    parametros: [{ nome: 'id', em: 'path', obrigatorio: true, descricao: 'Id do vínculo.' }],
  },
  /*
   * Vem ANTES de `redistribuir`, e antes de qualquer `/pessoas/:algo` futuro,
   * pela mesma razão de sempre: o Nest casa na ordem de declaração.
   */
  carteiraDaPessoa: {
    metodo: 'GET',
    caminho: '/v1/admin/pessoas/{id}/carteira',
    resumo: 'Quantos negócios em aberto esta pessoa tem',
    descricao:
      'O número que a tela mostra ANTES de redistribuir, e que ela devolve como `esperado` ' +
      'na hora de mover. Sai da MESMA consulta que a redistribuição usa — duas consultas ' +
      'parecidas divergiriam no primeiro ajuste, e o sintoma seria a tela oferecendo mover ' +
      'trinta enquanto o servidor conta vinte e oito e recusa, para sempre, sem que ninguém ' +
      'entenda de onde vêm os dois números. Conta só as etapas que a organização declarou como ' +
      'ABERTAS (C17). O `impedimento` vem junto em vez de virar erro: quem pergunta está ' +
      'montando a operação, e com ele à vista ninguém digita dez linhas de justificativa ' +
      'para descobrir no fim que a origem nem tem vínculo com o CRM.',
    resposta: CarteiraDaPessoaSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id do usuário (não o do vínculo).' }],
  },
  redistribuirCarteira: {
    metodo: 'POST',
    caminho: '/v1/admin/pessoas/redistribuir',
    resumo: 'Passa a carteira de uma pessoa para outra, em lote',
    descricao:
      'Move os NEGÓCIOS em aberto, não as conversas. `esperado` é o número que apareceu na ' +
      'tela: se o banco discordar, nada é movido — entre a conferência e o clique o total ' +
      'muda, e "tudo que houver" é como se movem quarenta achando que eram trinta.',
    corpo: RedistribuirSchema,
    resposta: RedistribuicaoSchema,
  },
  /* ── Ciclo de vida da revenda (G01) ───────────────────────────────────── */

  provisionarRevenda: {
    metodo: 'POST',
    caminho: '/v1/operacao/revendas',
    resumo: 'Provisiona uma organização inteira',
    descricao:
      'O gate da etapa G: uma organização de pé por um comando. Cria o tenant e a configuração ' +
      'padrão, e conduz a saga (B28) dos passos que tocam engine: os clients no Keycloak, as ' +
      'etapas do funil, a conta no Chatwoot, o administrador inicial com o primeiro acesso, o ' +
      'usuário de integração do atendimento e o aviso (webhook) do atendimento. ' +
      'Idempotente por `slug`. ' +
      'Organização já `ativo` é recusada com 409. Só o Super Admin. ' +
      'Com `contrato` (#769), grava também o cadastro da empresa, o fuso, o contrato do ' +
      'catálogo e o contato financeiro, que então são obrigatórios; CNPJ de outra organização ' +
      'é 409. Retomar pelo identificador uma organização que já tem outro CNPJ, ou que tem CNPJ ' +
      'e o pedido não traz o cadastro, é 409; retomar uma encerrada pede ' +
      '`confirmarRetomadaDeEncerrada` (`CONFIRMACAO_EXIGIDA`).',
    corpo: ProvisionarRevendaSchema,
    resposta: RevendaProvisionadaSchema,
    status: 201,
  },
  completarRevenda: {
    metodo: 'POST',
    caminho: '/v1/operacao/revendas/completar',
    resumo: 'Completa a configuração de uma organização que já existe',
    descricao:
      'Faz numa organização criada antes o que a criação de hoje já faz sozinha, e só o que ' +
      'faltar: o usuário de integração do atendimento (quando a credencial gravada não abre a ' +
      'conta do Chatwoot), o vínculo de atendente dos administradores sem ele, e os avisos ' +
      '(webhooks) do Chatwoot ainda não ligados; o aviso do cadastro volta sempre ' +
      'dispensado. Idempotente: numa organização ' +
      'completa, responde só dispensados. Só o Super Admin.',
    corpo: CompletarRevendaSchema,
    resposta: RevendaCompletadaSchema,
  },
  encerrarRevenda: {
    metodo: 'POST',
    caminho: '/v1/operacao/revendas/{id}/encerrar',
    resumo: 'Encerra uma organização, de forma reversível',
    descricao:
      'O primeiro dos dois passos da decisão B2 — o que acontece quando um cliente cancela. ' +
      'Desliga o realm, desativa as conexões e marca a organização como `encerrado`. Nada é ' +
      'apagado, e `reabrir` desfaz tudo. A resposta diz o que aconteceu em CADA sistema; ' +
      '`alerta: true` significa que alguém desta organização talvez ainda autentique. ' +
      'Idempotente: encerrar quem já está encerrado responde 200 sem mexer em nada.',
    corpo: EncerrarRevendaSchema,
    resposta: RevendaDesprovisionadaSchema,
    parametros: [{ nome: 'id', em: 'path', obrigatorio: true, descricao: 'Id da organização.' }],
  },
  reabrirRevenda: {
    metodo: 'POST',
    caminho: '/v1/operacao/revendas/{id}/reabrir',
    resumo: 'Devolve uma organização encerrada à operação',
    descricao:
      'O contrário exato de encerrar. Existe porque a B2 chama a desativação de reversível, ' +
      'e reversível sem caminho de volta implementado é só uma palavra. Organização que não ' +
      'está encerrada é recusada com 409.',
    resposta: RevendaDesprovisionadaSchema,
    parametros: [{ nome: 'id', em: 'path', obrigatorio: true, descricao: 'Id da organização.' }],
  },
  excluirRevenda: {
    metodo: 'POST',
    caminho: '/v1/operacao/revendas/{id}/excluir',
    resumo: 'Exclui uma organização definitivamente — não volta',
    descricao:
      'O segundo passo da B2, e o irreversível: apaga o realm no Keycloak e a conta no ' +
      'Chatwoot, com as conversas dentro, e destrói as credenciais no cofre. Exige step-up ' +
      '(B17) com a ação `tenant.excluir` e o id da organização como alvo, justificativa, e que ' +
      'a organização JÁ ESTEJA encerrada — encerrar primeiro é o que dá a chance de perceber o ' +
      'identificador errado enquanto ainda dá para voltar atrás. O que não sai inteiro a ' +
      'resposta nomeia: o espaço legado da engine de cadastro, onde houver, e o cadastro no ' +
      'Postgres, que fica porque a trilha de auditoria o referencia e tem DELETE revogado.',
    corpo: EncerrarRevendaSchema,
    resposta: RevendaDesprovisionadaSchema,
    parametros: [{ nome: 'id', em: 'path', obrigatorio: true, descricao: 'Id da organização.' }],
  },
  forcarReconciliacao: {
    metodo: 'POST',
    caminho: '/v1/admin/operacao/reconciliar',
    resumo: 'Força uma rodada de reconciliação desta organização',
    descricao:
      'A ação segura do painel de operação (G04, §14.1). O worker já reconcilia a cada ' +
      'minuto; isto antecipa a rodada para AGORA e devolve os números — o ganho não é ' +
      'velocidade, é confirmação: transforma "acho que voltou" em quantos eventos saíram ' +
      'do chão. Idempotente: a segunda chamada não acha o que a primeira já promoveu. ' +
      'Mexe no ESTADO do evento, nunca no payload — ação de painel não é porta dos fundos ' +
      'para conteúdo de cliente (§13.1). Registrada em `audit_logs` com o nome de quem ' +
      'clicou. Só a organização da sessão, e não todas.',
    resposta: ReconciliacaoForcadaSchema,
  },

  /*
   * ── Direitos do titular (D16, art. 18 da LGPD) ──────────────────────────
   *
   * O PEDIDO é o recurso, e não a operação. Não há
   * `POST /v1/customers/{id}/anonimizar`: o que se cria é um pedido, e a
   * execução é uma transição dele.
   *
   * A diferença aparece no dia em que o titular pergunta "vocês eliminaram
   * meus dados?". Com a operação como recurso, a resposta é um efeito no banco
   * — sem quem pediu, quando, por qual canal, nem quem executou. Com o pedido,
   * a resposta é uma linha.
   */
  registrarPedidoDoTitular: {
    metodo: 'POST',
    caminho: '/v1/titular/pedidos',
    resumo: 'Registra um pedido do titular',
    descricao:
      'Registra e NÃO executa. O prazo de 15 dias do art. 18, §3º começa a correr aqui, e ' +
      'pode levar dias até alguém identificar o titular ou obter o step-up — criar e ' +
      'executar numa chamada só faria o registro depender de a execução dar certo, e um ' +
      'pedido que falhou continua sendo um pedido recebido. `cliente_id` é opcional pela ' +
      'mesma razão: recusar por não achar o titular perderia o prazo.',
    corpo: CriarPedidoDoTitularSchema,
    resposta: PedidoDoTitularSchema,
    status: 201,
    parametros: [
      {
        nome: 'Idempotency-Key',
        em: 'header',
        descricao: 'Chave do comando (B30). Repetir a mesma devolve a primeira resposta.',
        obrigatorio: true,
      },
    ],
  },
  listarPedidosDoTitular: {
    metodo: 'GET',
    caminho: '/v1/titular/pedidos',
    resumo: 'A fila de pedidos em aberto, do mais urgente ao menos',
    descricao:
      'Ordenada por prazo, e não por chegada: a fila é de quem opera, e o que vence amanhã ' +
      'não pode ficar embaixo do que vence em duas semanas. `vencendo_em` filtra os que ' +
      'estouram dentro de N dias — é o que o alerta consulta. Prazo vencido NÃO some: ' +
      'aparece com `dias_restantes` negativo, porque esconder o atraso é como ele deixa de ' +
      'ser resolvido.',
    resposta: PaginaDePedidosDoTitularSchema,
    parametros: [
      {
        nome: 'vencendo_em',
        em: 'query',
        descricao: 'Só os que vencem em até N dias. Ausente = todos os abertos.',
      },
    ],
  },
  buscarPedidoDoTitular: {
    metodo: 'GET',
    caminho: '/v1/titular/pedidos/{id}',
    resumo: 'Um pedido, com os recibos por sistema',
    descricao:
      'Os `recibos` são o que a organização apresenta ao titular: um por sistema, com o que ' +
      'ele respondeu e quando. É o que distingue "eliminado" de "afirmamos ter eliminado".',
    resposta: PedidoDoTitularSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id do pedido.' }],
  },
  executarPedidoDoTitular: {
    metodo: 'POST',
    caminho: '/v1/titular/pedidos/{id}/executar',
    resumo: 'Executa o pedido registrado',
    descricao:
      'Exige step-up: `titular.exportar` para acesso e portabilidade, `titular.eliminar` ' +
      'para eliminação e anonimização. Reexecutar pedido concluído é recusado — dois ' +
      'recibos sobre um pedido fariam o titular receber duas respostas para uma pergunta. ' +
      'Falha numa engine não impede as outras: cada sistema eliminado é dano reduzido, e o ' +
      'pedido fica `em_execucao` com o recibo da falha registrado.',
    corpo: ExecutarPedidoDoTitularSchema,
    resposta: ResultadoDoPedidoSchema,
    parametros: [
      { nome: 'id', em: 'path', descricao: 'Id do pedido.' },
      {
        nome: 'Idempotency-Key',
        em: 'header',
        descricao: 'Chave do comando (B30).',
        obrigatorio: true,
      },
    ],
  },
  recusarPedidoDoTitular: {
    metodo: 'POST',
    caminho: '/v1/titular/pedidos/{id}/recusar',
    resumo: 'Recusa o pedido, com motivo',
    descricao:
      'O motivo é obrigatório: o art. 18, §4º dá ao titular o direito de saber por quê, e ' +
      'recusa sem motivo é indefensável. Só pedido aberto pode ser recusado.',
    corpo: RecusarPedidoDoTitularSchema,
    resposta: PedidoDoTitularSchema,
    parametros: [{ nome: 'id', em: 'path', descricao: 'Id do pedido.' }],
  },
  exportarTitular: {
    metodo: 'GET',
    caminho: '/v1/titular/{clienteId}/exportacao',
    resumo: 'A exportação do titular, sem passar por pedido',
    descricao:
      'O mesmo conteúdo que a execução de um pedido de acesso devolve, para quem precisa ' +
      'CONFERIR antes de responder ao titular. Exige o step-up `titular.exportar` igual. ' +
      'Inclui a origem dos dados (art. 18, II), a lista de operadores (art. 18, VII) e o ' +
      'que NÃO está incluído — exportação silenciosamente incompleta faz o titular assinar ' +
      'que recebeu tudo sem ter recebido.',
    resposta: ExportacaoDoTitularSchema,
    parametros: [{ nome: 'clienteId', em: 'path', descricao: 'Id canônico do cliente.' }],
  },
} as const satisfies Record<string, Rota>;

export type NomeDeRota = keyof typeof ROTAS;
